// src/pages/projects/InvoicesPage.jsx
// ============================================================================
// INVOICE MANAGEMENT PAGE
// ============================================================================
// Follows TimelinePage.jsx layout, spacing, and styling exactly.
// BRIO Migration: code "5a" (table dense + striped)
// HEADER ALIGNMENT FIX: Full-width header band with BrioFrame inside
//
// VISIBILITY RULES:
//   Admin       → full CRUD + confirm payment + delete
//   Supervisor  → read + create + edit drafts + confirm payment (no delete)
//   User        → NO ACCESS (canAccessPage blocks entry)
//
// DATA FLOW:
//   Owns: invoice lifecycle, payment confirmation
//   Reads: Timeline intent date (stageTimeline.*.invoiceDate) for alert banner
//   Never: creates/edits/deletes Timeline data
//
// FEATURE 1 — AUTO INVOICE NUMBERING:
//   Format: YYYYMMSS (e.g. 20260201 = first invoice of Feb 2026)
//   On "New Invoice": auto-allocates via RPC allocate_invoice_number()
//   "Override invoice number" toggle controls editability
//   If date changes to a different month and override is OFF → re-allocate
//   Once issued, invoice number is LOCKED — no edits.
//
// FEATURE 2 — ACCOUNT NAME LEARNING:
//   account_name stores the payer bank account name from credit narration.
//   On payment confirmation, user can enter/confirm account name.
//   Learned names are upserted into project_account_names for future matching.
//   IMPORTANT: account_name is a CONFIDENCE SIGNAL only — primary matching
//   uses invoice_number + net_amount. Never rely solely on account_name.
// ============================================================================
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { supabase } from '../../supabaseClient';
import { Pencil, Trash2, Lock, Check, X, AlertTriangle, Copy, FileText, Undo2, ChevronDown, ChevronUp, ChevronRight, Download, Mail, Bell, RefreshCw } from 'lucide-react';
import { formatDateForEmail, calculateDueDate, getInvoiceDisplayStatus, getAdminBccEmail, getReminderTemplateTypes, getStageAbbreviation, getPartStageAbbreviation } from '../../utils/emailTemplates';
import { redactBankBalance } from '../../utils/redactBankBalance';
import { stripBankEmailFooter } from '../../utils/stripBankEmailFooter';
import useCommunicationLog from '../../hooks/useCommunicationLog';
import useRecommendedActions from '../../hooks/useRecommendedActions';
import { resolveTimelineTrigger } from '../../utils/timelineTriggerEngine';
import CommunicationLogList from '../../components/CommunicationLogList';
import ReminderActionMenu from '../../components/ReminderActionMenu';
import EmailDraftModal from '../../components/EmailDraftModal';
import UsedInvoiceNumbersList from '../../components/UsedInvoiceNumbersList';
import {
  TAX_REGIME_OPTIONS, normalizeTaxRegime, getTaxPercentForRegime, officeForTaxRegime, letterheadForInvoice,
} from '../../utils/invoiceTaxRegime';
import { resolveInvoiceCurrency } from '../../invoices/renderInvoiceHtml';
import {
  BrioAnalyticsSurface,
  BrioByCode,
  BrioTable,
  BrioFrame,
  BrioDatePicker,
  getTablePropsFromCode
} from '../../components/BrioComponents';
import RecalculationPreviewModalShared, { computePlannedAtNewFee, computeRecalcBreakdown, generateExplanationText } from '../../components/RecalculationPreviewModal';
import { resolveBrandingImageSrc } from '../../utils/brandingImages';


const PRESENTATION_CODE = '5a';

// Debug flag — when true, logs key invoice operations
const DEBUG_INVOICES = false;

// ============================================================================
// STATUS CONSTANTS & BADGE MAPPING
// ============================================================================
const STATUS_BADGE = {
  draft:    { label: 'Draft',        className: '' },
  issued:   { label: 'Issued',       className: 'brio-badge--warning' },
  sent:     { label: 'Sent',         className: 'brio-badge--warning' },
  due:      { label: 'Due',          className: 'brio-badge--warning' },
  overdue:  { label: 'Overdue',      className: 'brio-badge--error' },
  paid:     { label: 'Paid',         className: 'brio-badge--approved' },
};

// ============================================================================
// HELPERS
// ============================================================================
const fmtDate = (d) => {
  if (!d) return '—';
  const parts = d.split('-');
  if (parts.length !== 3) return d;
  return `${parts[0]}-${parts[1]}-${parts[2]}`;
};

const computeAmounts = (base, gstPct, tdsPct, applyTds = true) => {
  const b   = Number(base)   || 0;
  const g   = Number(gstPct) || 0;
  const t   = Number(tdsPct) || 0;
  const gross = b + (b * g / 100);
  const tds   = applyTds ? (b * t / 100) : 0;          // TDS on base, not gross
  const net   = gross - tds;
  return { gross, tds, net };
};

// ============================================================================
// HELPER: Get current total fee for a project (base fee + CC retainer if active)
// ============================================================================
const getCurrentTotalFee = (project) => {
  const baseFee = Number(project?.budget) || 0;
  let ccRetainer = 0;
  if (project?.retainerConfig?.status === 'active') {
    const monthly = Number(project.retainerConfig.monthlyAmount) || 0;
    const months  = Number(project.retainerConfig.totalMonths) || 0;
    if (months > 0) ccRetainer = monthly * months;
  }
  const miscFee = (Array.isArray(project?.miscFeeScopes) ? project.miscFeeScopes : [])
    .reduce((sum, s) => sum + (Number(s.amount) || 0), 0);
  return baseFee + ccRetainer + miscFee;
};

// Helper: sum of misc fee scopes
const getMiscFeeTotal = (project) => {
  return (Array.isArray(project?.miscFeeScopes) ? project.miscFeeScopes : [])
    .reduce((sum, s) => sum + (Number(s.amount) || 0), 0);
};

// Extract YYYYMM from a date string (e.g. '2026-02-15' → '202602')
const getYearMonth = (dateStr) => {
  if (!dateStr) return '';
  return dateStr.replace(/-/g, '').slice(0, 6);
};

const DEFAULT_GST_PERCENT = 18;
const DEFAULT_TDS_PERCENT = 10;

// Tax regime options, GST % per regime and the office each regime bills from
// live in src/utils/invoiceTaxRegime.js.

// Office locations
const OFFICE_LOCATIONS = ['NL', 'IN'];

// Suggest the next invoice number client-side from existing numbers in the
// same YYYYMM window (format YYYYMMSS). Doesn't consume the RPC sequence.
const computeSuggestedInvoiceNumber = (invoiceDate, allInvoices) => {
  if (!invoiceDate || !/^\d{4}-\d{2}/.test(invoiceDate)) return '';
  const monthPrefix = invoiceDate.slice(0, 7).replace('-', '');
  const list = Array.isArray(allInvoices) ? allInvoices : [];
  let maxSeq = 0;
  for (const inv of list) {
    const num = String(inv?.invoiceNumber || '').trim();
    if (num.length === 8 && num.startsWith(monthPrefix)) {
      const seq = Number(num.slice(6));
      if (Number.isFinite(seq) && seq > maxSeq) maxSeq = seq;
    }
  }
  return `${monthPrefix}${String(maxSeq + 1).padStart(2, '0')}`;
};

// ============================================================================
// SUB-COMPONENT: NumericField
// Uncontrolled-style text input for numeric values. Maintains its own local
// draft state so fast typing never loses keystrokes (each keystroke only
// re-renders this tiny component). Commits the value to the parent via
// onCommit only on blur, preventing the expensive parent re-render on every
// digit. Syncs from the parent whenever the field is not focused.
// ============================================================================
function NumericField({ value, onCommit, disabled, className, style, placeholder }) {
  const [draft, setDraft] = React.useState(() => String(value ?? ''));
  const isFocused = React.useRef(false);

  React.useEffect(() => {
    if (!isFocused.current) {
      setDraft(String(value ?? ''));
    }
  }, [value]);

  return (
    <input
      type="text"
      inputMode="decimal"
      className={className}
      style={style}
      placeholder={placeholder}
      value={draft}
      disabled={disabled}
      onChange={(e) => setDraft(e.target.value)}
      onFocus={() => { isFocused.current = true; }}
      onBlur={(e) => {
        isFocused.current = false;
        onCommit(e.target.value);
      }}
    />
  );
}

// Editor form state for an invoice, or the defaults for a new one. A new
// invoice takes the client's default tax regime, the GST % that regime implies
// and the office it bills from (GST from Mumbai, VAT from Rotterdam); 'Other'
// leaves the office to the project. A saved invoice keeps what it was saved with.
const buildEditorForm = (invoice, project, client) => {
  const taxRegime = invoice?.taxRegime ?? normalizeTaxRegime(client?.defaultTaxRegime) ?? 'IN_inter_state';
  const customTaxPercent = invoice?.customTaxPercent ?? 18;
  return {
    invoiceNumber: invoice?.invoiceNumber ?? '',
    invoiceDate:   invoice?.invoiceDate   ?? new Date().toISOString().slice(0, 10),
    stageKey:      invoice?.stageKey      ?? '',
    baseAmount:    invoice?.baseAmount    ?? '',
    gstPercent:    invoice?.gstPercent    ?? getTaxPercentForRegime(taxRegime, customTaxPercent),
    tdsPercent:    invoice?.tdsPercent    ?? (taxRegime === 'NL' ? 0 : DEFAULT_TDS_PERCENT),
    note:          invoice?.note          ?? '',
    accountName:   invoice?.accountName   ?? '',
    officeLocation: invoice?.officeLocation ?? officeForTaxRegime(taxRegime) ?? project?.officeLocation ?? 'NL',
    taxRegime,
    customTaxPercent,
    applyTds:      invoice?.applyTds !== false,  // default true
    invoiceDocType: invoice?.invoiceDocType ?? 'tax',
    customStageName: invoice?.customStageName ?? '',
    sacCode: invoice?.sacCode ?? '',
  };
};

// ============================================================================
// SUB-COMPONENT: InvoiceEditorModal
// ============================================================================
function InvoiceEditorModal({
  invoice, projectId, project, client, stages,
  onSave, onIssue, onClose, fmtCurr, formatMoney,
  allocateInvoiceNumber,
  allInvoices,
  projectsById,
  sacCodes,
}) {
  const isNew  = !invoice?.id;
  const isDraft = isNew || invoice?.status === 'draft';
  // An existing invoice is "override" if it was explicitly manual OR has
  // fee-revision adjustment baked in (so the editor locks to the stored
  // base_amount instead of recomputing from stage %).
  const hasRecalcAdjustment = !isNew && (
    (Number(invoice?.adjustmentAmount) || 0) !== 0 ||
    invoice?.basisMode === 'percent_plus_adjustment' ||
    invoice?.basisMode === 'lumpsum' ||
    invoice?.basisMode === 'adjustment'
  );
  const initialOverride = !isNew && (invoice?.isManualOverride === true || hasRecalcAdjustment);

  const [form, setForm] = useState(() => buildEditorForm(invoice, project, client));
  const [manualOverride, setManualOverride] = useState(initialOverride);
  const [error, setError]   = useState('');
  const [saving, setSaving] = useState(false);
  // Format in the project's contract currency (no FX conversion)
  const fmtCC = (a) => formatMoney ? formatMoney(a, project?.contractCurrency || 'INR') : fmtCurr(a);

  // Safety net: if invoice prop changes (e.g. recalc-created draft arrives),
  // reset form state from the new invoice. This pairs with the key= prop on
  // the modal render but guards against React not remounting.
  const invoiceIdRef = useRef(invoice?.id);
  useEffect(() => {
    if (invoice?.id && invoice.id !== invoiceIdRef.current) {
      invoiceIdRef.current = invoice.id;
      setForm(buildEditorForm(invoice, project, client));
      const shouldOverride =
        invoice.isManualOverride === true ||
        (Number(invoice.adjustmentAmount) || 0) !== 0 ||
        invoice.basisMode === 'percent_plus_adjustment' ||
        invoice.basisMode === 'lumpsum' ||
        invoice.basisMode === 'adjustment';
      setManualOverride(shouldOverride);
    }
  }, [invoice]);

  // Feature 2: Account Name override toggle (admin/supervisor only)
  const [accountNameOverride, setAccountNameOverride] = useState(false);

  // Invoice number is entered manually. Drafts get a client-side suggestion
  // pre-filled from existing numbers in the same YYYYMM window. Editing the
  // field marks it as user-touched so we stop refreshing the suggestion.
  const [invoiceNumberEdited, setInvoiceNumberEdited] = useState(
    !isNew && !!invoice?.invoiceNumber
  );

  const suggestedInvoiceNumber = useMemo(
    () => computeSuggestedInvoiceNumber(form.invoiceDate, allInvoices),
    [form.invoiceDate, allInvoices]
  );

  // Pre-fill suggestion for new drafts (or empty existing drafts) until the
  // user edits the field.
  useEffect(() => {
    if (!isDraft) return;
    if (invoiceNumberEdited) return;
    if (suggestedInvoiceNumber && form.invoiceNumber !== suggestedInvoiceNumber) {
      setForm(prev => ({ ...prev, invoiceNumber: suggestedInvoiceNumber }));
    }
  }, [suggestedInvoiceNumber, isDraft, invoiceNumberEdited, form.invoiceNumber]);

  const duplicateExists = useMemo(() => {
    const trimmed = String(form.invoiceNumber || '').trim();
    if (!trimmed) return false;
    const myId = invoice?.id != null ? String(invoice.id) : null;
    return (allInvoices || []).some(inv => {
      if (myId && String(inv?.id) === myId) return false; // ignore self
      return String(inv?.invoiceNumber || '').trim() === trimmed;
    });
  }, [form.invoiceNumber, allInvoices, invoice?.id]);

  const handleDateChange = useCallback(async (newDate) => {
    setForm(prev => ({ ...prev, invoiceDate: newDate }));
  }, []);

  // Helper: resolve parent stage key from a potentially composite key (part stages / CC monthly)
  // Part stage keys: "cd_ad__part__abc123" → parent is "cd_ad"
  // CC monthly keys: "cc__2026-02" → parent is "cc"
  const resolveParentKey = (stageKey) => {
    if (!stageKey) return stageKey;
    const partIdx = stageKey.indexOf('__part__');
    if (partIdx > -1) return stageKey.slice(0, partIdx);
    // CC monthly: cc__YYYY-MM → cc
    if (stageKey.startsWith('cc__')) return 'cc';
    // Misc scope: misc__<id> stays as-is (it IS the parent)
    return stageKey;
  };

  // Get billing config for a stage key (supports normal + part + misc stages)
  const getBillingForStageKey = (stageKey, proj) => {
    if (!stageKey || !proj) return null;
    const partIdx = stageKey.indexOf('__part__');
    if (partIdx > -1) {
      const parentKey = stageKey.slice(0, partIdx);
      const partId = stageKey.slice(partIdx + '__part__'.length);
      const partStages = proj.stageTimeline?.[parentKey]?.partStages;
      if (Array.isArray(partStages)) {
        const part = partStages.find(ps => String(ps.id) === String(partId));
        return part?.billing || null;
      }
      return null;
    }
    // Normal stages, CC, and misc scopes all stored at stageTimeline[key].billing
    return proj.stageTimeline?.[stageKey]?.billing || null;
  };

  const suggestedBaseAmount = useMemo(() => {
    if (!form.stageKey || !project) return 0;
    const baseDesignFee = Number(project?.budget) || 0;
    const stageKey = form.stageKey;

    // ── Misc scope: default to scope amount, override with lumpsum ──
    if (stageKey.startsWith('misc__') && !stageKey.includes('__part__')) {
      const scopeId = stageKey.replace('misc__', '');
      const scope = (Array.isArray(project?.miscFeeScopes) ? project.miscFeeScopes : [])
        .find(s => s.id === scopeId);
      const billing = project.stageTimeline?.[stageKey]?.billing || {};
      const lumpsum = Number(billing.lumpsum ?? billing.lumpSum ?? 0);
      if (lumpsum > 0) return lumpsum;
      return Number(scope?.amount) || 0;
    }
    // ── Misc scope part stage: misc__<scopeId>__part__<partId> ──
    if (stageKey.startsWith('misc__') && stageKey.includes('__part__')) {
      const partIdx = stageKey.indexOf('__part__');
      const parentKey = stageKey.slice(0, partIdx);
      const partId = stageKey.slice(partIdx + '__part__'.length);
      const partStages = project.stageTimeline?.[parentKey]?.partStages;
      const part = Array.isArray(partStages)
        ? partStages.find(ps => String(ps.id) === String(partId))
        : null;
      const billing = part?.billing || {};
      const lumpsum = Number(billing.lumpsum ?? billing.lumpSum ?? 0);
      if (lumpsum > 0) return lumpsum;
      // Compute parent planned (scope amount)
      const scopeId = parentKey.replace('misc__', '');
      const scope = (Array.isArray(project?.miscFeeScopes) ? project.miscFeeScopes : [])
        .find(s => s.id === scopeId);
      const parentBilling = project.stageTimeline?.[parentKey]?.billing || {};
      const parentLumpsum = Number(parentBilling.lumpsum ?? parentBilling.lumpSum ?? 0);
      const parentPlanned = parentLumpsum > 0 ? parentLumpsum : (Number(scope?.amount) || 0);
      const pctOfStage = Number(billing.percentOfStage) || 0;
      return parentPlanned * pctOfStage / 100;
    }

    // ── CC monthly: default to retainer monthly amount, override with lumpsum ──
    if (stageKey.startsWith('cc__') && stageKey !== 'cc') {
      const billing = project.stageTimeline?.[stageKey]?.billing || {};
      const lumpsum = Number(billing.lumpsum ?? billing.lumpSum ?? 0);
      if (lumpsum > 0) return lumpsum;
      return Number(project?.retainerConfig?.monthlyAmount) || 0;
    }

    // ── Part stage: parentKey__part__partId ──
    const partIdx = stageKey.indexOf('__part__');
    if (partIdx > -1) {
      const parentKey = stageKey.slice(0, partIdx);
      const partId = stageKey.slice(partIdx + '__part__'.length);
      const partStages = project.stageTimeline?.[parentKey]?.partStages;
      const part = Array.isArray(partStages)
        ? partStages.find(ps => String(ps.id) === String(partId))
        : null;
      const billing = part?.billing || {};
      const lumpsum = Number(billing.lumpsum ?? billing.lumpSum ?? 0);
      if (lumpsum > 0) return lumpsum;

      // Compute parent planned amount (same logic as StageBillingTable)
      const parentBilling = project.stageTimeline?.[parentKey]?.billing || {};
      const parentLumpsum = Number(parentBilling.lumpsum ?? parentBilling.lumpSum ?? 0);
      const parentPlanned = parentLumpsum > 0
        ? parentLumpsum
        : baseDesignFee * (Number(parentBilling.percent) || 0) / 100;
      const pctOfStage = Number(billing.percentOfStage) || 0;
      return parentPlanned * pctOfStage / 100;
    }

    // ── Full stage ──
    const billing = project.stageTimeline?.[stageKey]?.billing || {};
    const lumpsum = Number(billing.lumpsum ?? billing.lumpSum ?? 0);
    if (lumpsum > 0) return lumpsum;
    const percent = Number(billing.percent) || 0;
    return baseDesignFee * percent / 100;
  }, [form.stageKey, project]);

  // Determine the effective percent for display
  const effectiveStagePercent = useMemo(() => {
    if (!form.stageKey || !project) return 0;
    const stageKey = form.stageKey;
    // CC monthly: no percent
    if (stageKey.startsWith('cc__') && stageKey !== 'cc') return 0;
    // Part stage: show percentOfStage
    const partIdx = stageKey.indexOf('__part__');
    if (partIdx > -1) {
      const parentKey = stageKey.slice(0, partIdx);
      const partId = stageKey.slice(partIdx + '__part__'.length);
      const partStages = project.stageTimeline?.[parentKey]?.partStages;
      const part = Array.isArray(partStages)
        ? partStages.find(ps => String(ps.id) === String(partId))
        : null;
      return Number(part?.billing?.percentOfStage) || 0;
    }
    // Full stage: billing.percent
    return Number(project.stageTimeline?.[stageKey]?.billing?.percent) || 0;
  }, [form.stageKey, project]);

  // Track source of suggested amount for hint text
  const suggestedSource = useMemo(() => {
    if (!form.stageKey || !project) return 'none';
    const stageKey = form.stageKey;
    if (stageKey.startsWith('cc__') && stageKey !== 'cc') {
      const billing = project.stageTimeline?.[stageKey]?.billing || {};
      return Number(billing.lumpsum ?? billing.lumpSum ?? 0) > 0 ? 'lumpsum' : 'cc_monthly';
    }
    const billing = getBillingForStageKey(stageKey, project) || {};
    return Number(billing.lumpsum ?? billing.lumpSum ?? 0) > 0 ? 'lumpsum' : 'percent';
  }, [form.stageKey, project]);

  const effectiveBase = manualOverride ? (Number(form.baseAmount) || 0) : suggestedBaseAmount;

  const handleStageChange = (newKey) => {
    if (!manualOverride) {
      setForm(prev => ({ ...prev, stageKey: newKey, baseAmount: '' }));
    } else {
      setForm(prev => ({ ...prev, stageKey: newKey }));
    }
  };

  const { gross, tds, net } = computeAmounts(effectiveBase, form.gstPercent, form.tdsPercent, form.applyTds);
  const set = (field, val) => setForm(prev => ({ ...prev, [field]: val }));

  // B) Tax regime change handler — syncs gstPercent from regime
  const handleTaxRegimeChange = (newRegime) => {
    // ...and the office with it, so the letterhead follows the regime. The
    // Office select stays editable for the odd case the two part ways.
    const taxPct = getTaxPercentForRegime(newRegime, form.customTaxPercent);
    setForm(prev => ({
      ...prev, taxRegime: newRegime, gstPercent: taxPct,
      officeLocation: officeForTaxRegime(newRegime) ?? prev.officeLocation,
    }));
  };
  const handleCustomTaxChange = (val) => {
    // Store raw string so partial decimal input (e.g. "18.") isn't clobbered by
    // Number() conversion. computeAmounts and handleSave both coerce via Number().
    setForm(prev => ({ ...prev, customTaxPercent: val, gstPercent: val }));
  };

  const toggleOverride = () => {
    if (manualOverride) {
      setManualOverride(false);
      setForm(prev => ({ ...prev, baseAmount: '' }));
    } else {
      setManualOverride(true);
      setForm(prev => ({ ...prev, baseAmount: suggestedBaseAmount || '' }));
    }
  };

  const validate = () => {
    if (!form.invoiceDate) return 'Invoice date is required.';
    if (effectiveBase <= 0) {
      if (!manualOverride && !form.stageKey) return 'Select a stage or enable manual override to set base amount.';
      return 'Base amount must be > 0.';
    }
    return '';
  };

  const confirmDuplicateIfNeeded = () => {
    if (!duplicateExists) return true;
    return window.confirm(
      `Invoice number "${String(form.invoiceNumber).trim()}" is already in use.\n\n` +
      `You can still proceed, but you will need to resolve the conflict ` +
      `after assignment (e.g. update one of the invoices to a unique number).`
    );
  };

  // One payload for both Save and Save-then-Issue. These were two copies of the
  // same object literal; the fields below were missing from both.
  //
  // `currency` was never sent at all, so the insert omitted the column and
  // Postgres filled in its default of 'INR' — on every invoice, including Dutch
  // ones, regardless of the project's contract currency. Resolved here through
  // the same rule the PDF renderer uses, so the stored row agrees with what
  // gets printed instead of being overridden at render time.
  //
  // `tdsPercent` came straight from the form, which prefills DEFAULT_TDS_PERCENT.
  // TDS is an Indian withholding instrument and has no place on an NL invoice,
  // so the regime zeroes it here the way the recalculation paths already do.
  const buildPayload = () => {
    const officeKey = (form.officeLocation || 'NL') === 'IN' ? 'mumbai' : 'rotterdam';
    return {
      projectId, stageKey: form.stageKey || null, invoiceNumber: form.invoiceNumber,
      invoiceDate: form.invoiceDate, baseAmount: effectiveBase,
      gstPercent: Number(form.gstPercent) || 0,
      tdsPercent: form.taxRegime === 'NL' ? 0 : Number(form.tdsPercent) || 0,
      currency: resolveInvoiceCurrency({ officeKey }),
      note: form.note, isManualOverride: manualOverride,
      officeLocation: form.officeLocation || 'NL',
      taxRegime: form.taxRegime || null,
      customTaxPercent: form.taxRegime === 'Other' ? (Number(form.customTaxPercent) || 0) : null,
      applyTds: form.applyTds,
      invoiceDocType: form.invoiceDocType || 'tax',
      customStageName: form.customStageName || null,
      sacCode: form.sacCode || null,
      ...(accountNameOverride ? { accountName: form.accountName || null } : {}),
    };
  };

  const handleSave = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    if (!confirmDuplicateIfNeeded()) return;
    setSaving(true);
    setError('');
    const result = await onSave(buildPayload(), invoice?.id || null);
    setSaving(false);
    if (result.success) {
      // C2) Non-blocking warning if office_location column missing
      if (result.warning) console.warn('[InvoiceEditor]', result.warning);
      onClose();
    }
    else setError(result.error || 'Save failed.');
  };

  const handleIssue = async () => {
    if (!invoice?.id) return;
    if (!window.confirm('Issue this invoice? It will be locked and cannot be edited.')) return;
    if (!confirmDuplicateIfNeeded()) return;
    setSaving(true);
    setError('');
    const saveFirst = await onSave(buildPayload(), invoice.id);
    if (!saveFirst.success) { setSaving(false); setError(saveFirst.error || 'Failed to save before issuing.'); return; }
    const result = await onIssue(invoice.id);
    setSaving(false);
    if (result.success) onClose();
    else setError(result.error || 'Failed to issue.');
  };

  const inputStyle = {
    padding: '6px 8px', fontSize: '11px', width: '100%',
    border: '1px solid var(--brio-border)', fontFamily: 'var(--brio-font-family)',
  };
  const sectionHdr = {
    gridColumn: '1 / -1', fontSize: '9px', fontWeight: 700, letterSpacing: '0.09em',
    textTransform: 'uppercase', color: 'var(--brio-gold)',
    borderBottom: '1px solid var(--brio-border)', paddingBottom: '5px', marginTop: '6px',
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--brio-white, #fff)', border: '1px solid var(--brio-border)', width: '100%', maxWidth: '660px', maxHeight: '90vh', overflowY: 'auto' }}>
        {/* Header */}
        <div className="brio-flex brio-justify-between brio-items-center" style={{ padding: '12px 16px', borderBottom: '1px solid var(--brio-border)' }}>
          <span className="brio-font-bold" style={{ fontSize: '13px' }}>
            {isNew ? 'Create Invoice' : isDraft ? 'Edit Draft Invoice' : 'Invoice Details'}
          </span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px' }}><X className="w-4 h-4" /></button>
        </div>

        {/* Body */}
        <div style={{ padding: '18px' }}>
          {/* Fee revision info banner */}
          {!isNew && invoice?.feeRevisionId && (
            <div style={{ padding: '8px 10px', background: 'var(--brio-gold-light)', border: '1px solid var(--brio-gold)', fontSize: '11px', marginBottom: '14px' }}>
              <strong>Fee revision adjustment baked in.</strong>
              {invoice.baseAmountPlanned != null && invoice.adjustmentAmount != null && (
                <span className="brio-text-grey"> Planned: {fmtCC(invoice.baseAmountPlanned)}, adjustment: {invoice.adjustmentAmount >= 0 ? '+' : ''}{fmtCC(invoice.adjustmentAmount)}</span>
              )}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 18px', alignItems: 'start' }}>

            {/* Invoice Details */}
            <div style={sectionHdr}>Invoice Details</div>
            <div>
              <label className="brio-text-xs brio-font-medium" style={{ display: 'block', marginBottom: '4px' }}>Invoice Number</label>
              <input type="text" value={form.invoiceNumber}
                onChange={(e) => { setInvoiceNumberEdited(true); set('invoiceNumber', e.target.value); }}
                disabled={!isDraft}
                placeholder={isDraft ? (suggestedInvoiceNumber || 'YYYYMMSS') : '—'}
                className="brio-input"
                style={{ ...inputStyle, backgroundColor: !isDraft ? 'var(--brio-grey-light)' : undefined }} />
              <div className="brio-text-xs" style={{ marginTop: '2px', color: duplicateExists ? 'var(--brio-amber, #b45309)' : 'var(--brio-grey)' }}>
                {duplicateExists
                  ? `⚠ Already in use — please resolve this conflict after assignment.`
                  : isDraft
                    ? (suggestedInvoiceNumber
                        ? `Suggested next: ${suggestedInvoiceNumber} (edit freely; format YYYYMMSS)`
                        : 'Manual entry — ensure uniqueness (format YYYYMMSS)')
                    : 'Locked — invoice has been issued.'}
              </div>
            </div>
            <div>
              <label className="brio-text-xs brio-font-medium" style={{ display: 'block', marginBottom: '4px' }}>Invoice Date *</label>
              <BrioDatePicker value={form.invoiceDate} onChange={v => handleDateChange(v)}
                disabled={!isDraft} clearable={false}
                style={{ display: 'block', width: '100%' }} triggerStyle={{ width: '100%' }} />
            </div>
            {isDraft && (
              <div style={{ gridColumn: '1 / -1' }}>
                <UsedInvoiceNumbersList
                  allInvoices={allInvoices}
                  projectsById={projectsById}
                  filterDate={form.invoiceDate}
                  currentNumber={form.invoiceNumber}
                />
              </div>
            )}

            {/* Stage & Classification */}
            <div style={sectionHdr}>Stage &amp; Classification</div>
            <div>
              <label className="brio-text-xs brio-font-medium" style={{ display: 'block', marginBottom: '4px' }}>Stage (optional)</label>
              <select value={form.stageKey} onChange={(e) => handleStageChange(e.target.value)} disabled={!isDraft} className="brio-input" style={inputStyle}>
                <option value="">— none —</option>
                {stages.map(s => {
                  const lookupKey = s.isPartStage ? s.parentKey : s.key;
                  const allocPct = Number(project?.budgetAllocation?.[lookupKey]) || 0;
                  const invPct = project?.stageTimeline?.[lookupKey]?.invoicePercent;
                  const displayPct = invPct != null ? invPct : allocPct;
                  const prefix = s.isPartStage ? '  ↳ ' : '';
                  return <option key={s.key} value={s.key}>{prefix}{s.name}{displayPct > 0 ? ` (${displayPct}%)` : ''}</option>;
                })}
              </select>
            </div>
            <div>
              <label className="brio-text-xs brio-font-medium" style={{ display: 'block', marginBottom: '4px' }}>Office</label>
              <select value={form.officeLocation || 'NL'} onChange={(e) => set('officeLocation', e.target.value)} disabled={!isDraft} className="brio-input" style={inputStyle}>
                {OFFICE_LOCATIONS.map(loc => <option key={loc} value={loc}>{loc}</option>)}
              </select>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label className="brio-text-xs brio-font-medium" style={{ display: 'block', marginBottom: '4px' }}>Document Type</label>
              <div className="brio-flex brio-gap-s" style={{ paddingTop: '3px' }}>
                {[{ key: 'tax', label: 'Tax Invoice' }, { key: 'proforma', label: 'Proforma Invoice' }].map(({ key, label }) => (
                  <label key={key} className="brio-flex brio-items-center brio-gap-xs" style={{ cursor: isDraft ? 'pointer' : 'default', fontSize: '11px' }}>
                    <input type="radio" name="invoiceDocType" value={key} checked={form.invoiceDocType === key}
                      onChange={() => set('invoiceDocType', key)} disabled={!isDraft}
                      style={{ accentColor: 'var(--brio-gold, #9F902A)' }} />
                    {label}
                  </label>
                ))}
              </div>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label className="brio-text-xs brio-font-medium" style={{ display: 'block', marginBottom: '4px' }}>Stage Description (PDF override)</label>
              <input
                type="text"
                className="brio-input"
                style={inputStyle}
                placeholder={form.stageKey ? 'Leave blank to use default stage name' : 'e.g. Schematic Design — Interior'}
                value={form.customStageName || ''}
                onChange={e => set('customStageName', e.target.value)}
                disabled={!isDraft}
              />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label className="brio-text-xs brio-font-medium" style={{ display: 'block', marginBottom: '4px' }}>SAC Code</label>
              <select
                className="brio-input"
                style={inputStyle}
                value={form.sacCode || ''}
                onChange={e => set('sacCode', e.target.value)}
                disabled={!isDraft}
              >
                <option value="">
                  {client?.sacCode ? `Use client default (${client.sacCode})` : 'Use client default'}
                </option>
                {(sacCodes || []).filter(sc => sc && sc.code).map((sc, i) => (
                  <option key={i} value={sc.code}>
                    {sc.code}{sc.label ? ` — ${sc.label}` : ''}
                  </option>
                ))}
                {form.sacCode && !(sacCodes || []).some(sc => sc && sc.code === form.sacCode) && (
                  <option value={form.sacCode}>{form.sacCode} (custom)</option>
                )}
              </select>
              <div className="brio-text-xs brio-text-grey" style={{ marginTop: '3px' }}>
                Overrides the client SAC for this invoice. Manage the list in Settings → Invoice PDF.
              </div>
            </div>

            {/* Amounts & Tax */}
            <div style={sectionHdr}>Amounts &amp; Tax</div>
            <div style={{ gridColumn: '1 / -1' }}>
              <div className="brio-flex brio-justify-between brio-items-center" style={{ marginBottom: '4px' }}>
                <label className="brio-text-xs brio-font-medium">Base Amount *</label>
                {isDraft && (
                  <label className="brio-flex brio-items-center brio-gap-xs" style={{ cursor: 'pointer' }}>
                    <span className="brio-text-xs brio-text-grey">Manual override</span>
                    <input type="checkbox" checked={manualOverride} onChange={toggleOverride} style={{ cursor: 'pointer', width: '14px', height: '14px' }} />
                  </label>
                )}
              </div>
              <NumericField
                value={manualOverride ? form.baseAmount : (suggestedBaseAmount || '')}
                onCommit={(v) => set('baseAmount', v)}
                disabled={!isDraft || !manualOverride}
                className="brio-input"
                style={{ ...inputStyle, backgroundColor: (!manualOverride && isDraft) ? 'var(--brio-grey-light)' : undefined }} />
              <div className="brio-text-xs brio-text-grey" style={{ marginTop: '3px' }}>
                {manualOverride
                  ? (hasRecalcAdjustment
                      ? `Locked: includes fee revision adjustment (planned ${fmtCC(invoice?.baseAmountPlanned || 0)} ${(invoice?.adjustmentAmount || 0) >= 0 ? '+' : ''}${fmtCC(invoice?.adjustmentAmount || 0)})`
                      : 'Manual override enabled')
                  : form.stageKey ? (suggestedSource === 'lumpsum'
                      ? `Auto: Lumpsum from planning (${fmtCC(suggestedBaseAmount)})`
                      : suggestedSource === 'cc_monthly'
                      ? `Auto: Monthly retainer (${fmtCC(suggestedBaseAmount)})`
                      : form.stageKey.indexOf('__part__') > -1
                      ? `Auto: Parent planned × ${effectiveStagePercent}% (of stage)`
                      : `Auto: ${fmtCC(Number(project?.budget) || 0)} × ${effectiveStagePercent}%`)
                    : 'Select a stage for auto-calculated amount'}
              </div>
            </div>
            <div>
              <label className="brio-text-xs brio-font-medium" style={{ display: 'block', marginBottom: '4px' }}>Tax Regime</label>
              <select value={form.taxRegime || 'IN_inter_state'} onChange={(e) => handleTaxRegimeChange(e.target.value)} disabled={!isDraft} className="brio-input" style={inputStyle}>
                {TAX_REGIME_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              {form.taxRegime === 'IN_intra_state' && (
                <div className="brio-text-xs brio-text-grey" style={{ marginTop: '3px' }}>CGST 9% + SGST 9% = 18% total</div>
              )}
            </div>
            <div>
              {form.taxRegime === 'Other' ? (
                <>
                  <label className="brio-text-xs brio-font-medium" style={{ display: 'block', marginBottom: '4px' }}>Custom Tax %</label>
                  <NumericField value={form.customTaxPercent} onCommit={(v) => handleCustomTaxChange(v)} disabled={!isDraft} className="brio-input" style={inputStyle} />
                </>
              ) : (
                <>
                  <label className="brio-text-xs brio-font-medium" style={{ display: 'block', marginBottom: '4px' }}>Tax %</label>
                  <input type="number" min="0" max="100" step="0.01" value={form.gstPercent} disabled className="brio-input" style={{ ...inputStyle, backgroundColor: 'var(--brio-grey-light)' }} />
                </>
              )}
            </div>
            <div>
              <label className="brio-text-xs brio-font-medium" style={{ display: 'block', marginBottom: '4px' }}>Apply TDS</label>
              <label className="brio-flex brio-items-center brio-gap-xs" style={{ cursor: isDraft ? 'pointer' : 'default', paddingTop: '3px' }}>
                <input type="checkbox" checked={form.applyTds} onChange={(e) => set('applyTds', e.target.checked)} disabled={!isDraft}
                  style={{ cursor: isDraft ? 'pointer' : 'default', width: '14px', height: '14px' }} />
                <span style={{ fontSize: '11px' }}>Deduct TDS at source</span>
              </label>
              <div className="brio-text-xs brio-text-grey" style={{ marginTop: '3px' }}>
                {form.applyTds ? `TDS ${Number(form.tdsPercent) || 0}% deducted on base` : 'TDS not applied'}
              </div>
            </div>
            <div>
              <label className="brio-text-xs brio-font-medium" style={{ display: 'block', marginBottom: '4px' }}>TDS %</label>
              <NumericField value={form.tdsPercent} onCommit={(v) => set('tdsPercent', v)} disabled={!isDraft || !form.applyTds} className="brio-input"
                style={{ ...inputStyle, backgroundColor: !form.applyTds ? 'var(--brio-grey-light)' : undefined, opacity: !form.applyTds ? 0.5 : 1 }} />
            </div>
            <div style={{ gridColumn: '1 / -1', padding: '10px 12px', background: 'var(--brio-grey-light)', border: '1px solid var(--brio-border)' }}>
              <div className="brio-text-xs brio-font-medium" style={{ marginBottom: '6px' }}>Computed Totals</div>
              <div className="brio-flex brio-justify-between brio-text-s"><span className="brio-text-grey">Base Amount</span><span className="brio-font-medium">{fmtCC(effectiveBase)}</span></div>
              {form.taxRegime === 'IN_intra_state' ? (
                <>
                  <div className="brio-flex brio-justify-between brio-text-s" style={{ marginTop: '2px' }}><span className="brio-text-grey">+ CGST (9%)</span><span className="brio-font-medium">{fmtCC(effectiveBase * 9 / 100)}</span></div>
                  <div className="brio-flex brio-justify-between brio-text-s" style={{ marginTop: '2px' }}><span className="brio-text-grey">+ SGST (9%)</span><span className="brio-font-medium">{fmtCC(effectiveBase * 9 / 100)}</span></div>
                </>
              ) : form.taxRegime === 'IN_inter_state' ? (
                <div className="brio-flex brio-justify-between brio-text-s" style={{ marginTop: '2px' }}><span className="brio-text-grey">+ IGST ({Number(form.gstPercent) || 0}%)</span><span className="brio-font-medium">{fmtCC(gross - effectiveBase)}</span></div>
              ) : form.taxRegime === 'NL' ? (
                <div className="brio-flex brio-justify-between brio-text-s" style={{ marginTop: '2px' }}><span className="brio-text-grey">+ VAT ({Number(form.gstPercent) || 0}%)</span><span className="brio-font-medium">{fmtCC(gross - effectiveBase)}</span></div>
              ) : (
                <div className="brio-flex brio-justify-between brio-text-s" style={{ marginTop: '2px' }}><span className="brio-text-grey">+ Tax ({Number(form.gstPercent) || 0}%)</span><span className="brio-font-medium">{fmtCC(gross - effectiveBase)}</span></div>
              )}
              <div className="brio-flex brio-justify-between brio-text-s" style={{ marginTop: '2px' }}><span className="brio-text-grey">Gross Amount</span><span className="brio-font-medium">{fmtCC(gross)}</span></div>
              <div className="brio-flex brio-justify-between brio-text-s" style={{ marginTop: '2px' }}>
                <span className="brio-text-grey">{form.applyTds ? `− TDS (${Number(form.tdsPercent) || 0}% on base)` : '− TDS (not applied)'}</span>
                <span style={{ color: form.applyTds && tds > 0 ? 'var(--brio-red)' : 'var(--brio-grey)' }}>{form.applyTds ? `−${fmtCC(tds)}` : '—'}</span>
              </div>
              <div className="brio-flex brio-justify-between brio-text-s brio-font-bold" style={{ marginTop: '4px', paddingTop: '4px', borderTop: '1px solid var(--brio-border)' }}><span>Net Receivable</span><span>{fmtCC(net)}</span></div>
            </div>

            {/* Payment & Notes */}
            <div style={sectionHdr}>Payment &amp; Notes</div>
            <div>
              <div className="brio-flex brio-justify-between brio-items-center" style={{ marginBottom: '4px' }}>
                <label className="brio-text-xs brio-font-medium">Account Name</label>
                {isDraft && (
                  <label className="brio-flex brio-items-center brio-gap-xs" style={{ cursor: 'pointer' }}>
                    <span className="brio-text-xs brio-text-grey">Edit</span>
                    <input type="checkbox" checked={accountNameOverride} onChange={() => setAccountNameOverride(prev => !prev)}
                      style={{ cursor: 'pointer', width: '12px', height: '12px' }} />
                  </label>
                )}
              </div>
              <input type="text" value={form.accountName}
                onChange={(e) => set('accountName', e.target.value)}
                disabled={!accountNameOverride}
                placeholder={form.accountName ? '' : 'Auto-filled when first payment is confirmed'}
                className="brio-input"
                style={{ ...inputStyle, backgroundColor: !accountNameOverride ? 'var(--brio-grey-light)' : undefined }} />
              <div className="brio-text-xs brio-text-grey" style={{ marginTop: '2px' }}>
                {accountNameOverride
                  ? 'Manual edit — payer name will be saved on next save'
                  : form.accountName
                    ? 'Payer name from confirmed payment (confidence signal only)'
                    : 'Auto-filled when first payment is confirmed'}
              </div>
            </div>
            <div>
              <label className="brio-text-xs brio-font-medium" style={{ display: 'block', marginBottom: '4px' }}>Note</label>
              <input type="text" value={form.note} onChange={(e) => set('note', e.target.value)} disabled={!isDraft} placeholder="Optional note" className="brio-input" style={inputStyle} />
            </div>

            {error && <div className="brio-text-xs" style={{ gridColumn: '1 / -1', color: 'var(--brio-red)' }}>{error}</div>}
          </div>
        </div>

        {/* Footer */}
        <div className="brio-flex brio-justify-end brio-gap-xs" style={{ padding: '12px 16px', borderTop: '1px solid var(--brio-border)' }}>
          <button onClick={onClose} className="brio-btn brio-btn-secondary" style={{ padding: '6px 14px', fontSize: '11px' }}>Cancel</button>
          {isDraft && (
            <>
              <button onClick={handleSave} disabled={saving} className="brio-btn brio-btn-secondary" style={{ padding: '6px 14px', fontSize: '11px', opacity: saving ? 0.5 : 1 }}>{saving ? 'Saving…' : 'Save Draft'}</button>
              {!isNew && (
                <button onClick={handleIssue} disabled={saving} className="brio-btn brio-btn-primary" style={{ padding: '6px 14px', fontSize: '11px', opacity: saving ? 0.5 : 1 }}>{saving ? '…' : 'Issue Invoice'}</button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// SUB-COMPONENT: PaymentConfirmModal (Feature 2 enhanced)
// ============================================================================
function PaymentConfirmModal({ invoice, onConfirm, onClose, fmtCurr, formatMoney, knownAccountNames }) {
  const [paidDate, setPaidDate] = useState(new Date().toISOString().slice(0, 10));
  const [accountName, setAccountName] = useState(invoice?.accountName || '');
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');

  const handle = async () => {
    if (!paidDate) { setError('Paid date is required.'); return; }
    setSaving(true);
    const paymentEvent = {};
    if (accountName.trim()) paymentEvent.accountName = accountName.trim();
    const result = await onConfirm(invoice.id, paidDate, paymentEvent);
    setSaving(false);
    if (result.success) onClose();
    else setError(result.error || 'Confirmation failed.');
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--brio-white, #fff)', border: '1px solid var(--brio-border)', width: '100%', maxWidth: '360px' }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--brio-border)' }}>
          <span className="brio-font-bold" style={{ fontSize: '13px' }}>Confirm Payment</span>
        </div>
        <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div className="brio-text-s">Confirming payment for <strong>{invoice.invoiceNumber || `Invoice #${invoice.id}`}</strong></div>
          <div className="brio-text-s">
            Net amount:{' '}
            <strong>
              {formatMoney
                ? formatMoney(Number(invoice?.netAmount) || 0, invoice?.currency || 'INR')
                : fmtCurr(invoice?.netAmount)}
            </strong>
          </div>
          <div>
            <label className="brio-text-xs brio-font-medium" style={{ display: 'block', marginBottom: '4px' }}>Payment Date</label>
            <BrioDatePicker value={paidDate} onChange={v => setPaidDate(v)} placeholder="Payment date" />
          </div>
          {/* Feature 2: Account Name input with datalist autocomplete */}
          <div>
            <label className="brio-text-xs brio-font-medium" style={{ display: 'block', marginBottom: '4px' }}>
              Account Name <span className="brio-text-grey" style={{ fontWeight: 'normal' }}>(optional)</span>
            </label>
            <input type="text" value={accountName} onChange={(e) => setAccountName(e.target.value)}
              placeholder="Payer name from credit narration" className="brio-input" list="known-account-names"
              style={{ padding: '6px 8px', fontSize: '11px', width: '100%', border: '1px solid var(--brio-border)', fontFamily: 'var(--brio-font-family)' }} />
            {knownAccountNames && knownAccountNames.length > 0 && (
              <datalist id="known-account-names">
                {knownAccountNames.map((name, idx) => <option key={idx} value={name} />)}
              </datalist>
            )}
            <div className="brio-text-xs brio-text-grey" style={{ marginTop: '2px' }}>
              Enter the payer name as shown in credit alert. Used as confidence signal for future matching.
            </div>
          </div>
          {error && <div className="brio-text-xs" style={{ color: 'var(--brio-red)' }}>{error}</div>}
        </div>
        <div className="brio-flex brio-justify-end brio-gap-xs" style={{ padding: '12px 16px', borderTop: '1px solid var(--brio-border)' }}>
          <button onClick={onClose} className="brio-btn brio-btn-secondary" style={{ padding: '6px 14px', fontSize: '11px' }}>Cancel</button>
          <button onClick={handle} disabled={saving} className="brio-btn brio-btn-primary" style={{ padding: '6px 14px', fontSize: '11px', opacity: saving ? 0.5 : 1 }}>
            {saving ? 'Confirming…' : 'Confirm Paid'}
          </button>
        </div>
      </div>
    </div>
  );
}

// computePlannedAtNewFee, computeRecalcBreakdown, generateExplanationText
// imported from ../../components/RecalculationPreviewModal

// RecalculationPreviewModal imported from ../../components/RecalculationPreviewModal
// Local wrapper passes getCurrentTotalFee so the shared component stays pure
function RecalculationPreviewModal(props) {
  return <RecalculationPreviewModalShared {...props} getCurrentTotalFee={getCurrentTotalFee} />;
}


// ============================================================================
// SUB-COMPONENT: PaymentMatchConfirmModal (Step 2 - detailed confirm dialog)
// ============================================================================
function PaymentMatchConfirmModal({ paymentEvent, invoice, onConfirm, onClose, fmtCurr, formatMoney }) {
  const [learnPayer, setLearnPayer] = useState(!!(paymentEvent?.payerName));
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState('');

  const handleConfirm = async () => {
    setConfirming(true);
    setError('');
    const result = await onConfirm({ invoiceId: invoice.id, paymentEvent, invoice, learnPayer });
    setConfirming(false);
    if (result?.success) onClose();
    else setError(result?.error || 'Confirmation failed.');
  };

  const narrationTrunc = (paymentEvent?.narration || '').length > 120
    ? paymentEvent.narration.slice(0, 120) + '...' : (paymentEvent?.narration || '');
  const reasons = paymentEvent?.suggestedReasons;
  const reasonsText = reasons
    ? (Array.isArray(reasons) ? reasons.join('; ') : typeof reasons === 'string' ? reasons : JSON.stringify(reasons))
    : '';
  const cellL = { fontSize: '11px', color: 'var(--brio-grey)', padding: '3px 8px 3px 0', verticalAlign: 'top', whiteSpace: 'nowrap' };
  const cellV = { fontSize: '11px', padding: '3px 0', fontWeight: 500 };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--brio-white, #fff)', border: '1px solid var(--brio-border)', width: '100%', maxWidth: '440px' }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--brio-border)' }}>
          <span className="brio-font-bold" style={{ fontSize: '13px' }}>Confirm Payment Match</span>
        </div>
        <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div>
            <div className="brio-text-xs brio-font-bold" style={{ marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--brio-grey)' }}>Bank Payment</div>
            <table style={{ fontSize: '11px' }}><tbody>
              <tr><td style={cellL}>Amount</td><td style={cellV}>{fmtCurr(paymentEvent?.amount || 0)}</td></tr>
              <tr><td style={cellL}>Date</td><td style={cellV}>{fmtDate(paymentEvent?.txnDate)}</td></tr>
              <tr><td style={cellL}>Payer</td><td style={cellV}>{paymentEvent?.payerName || '(unknown)'}</td></tr>
              {narrationTrunc && <tr><td style={cellL}>Narration</td><td style={{ ...cellV, fontWeight: 'normal', color: 'var(--brio-grey)' }}>{narrationTrunc}</td></tr>}
              <tr><td style={cellL}>Confidence</td><td style={cellV}>{paymentEvent?.suggestedConfidence || 0}%</td></tr>
              {reasonsText && <tr><td style={cellL}>Reason</td><td style={{ ...cellV, fontWeight: 'normal', fontStyle: 'italic', color: 'var(--brio-grey)' }}>{reasonsText}</td></tr>}
            </tbody></table>
          </div>
          <div style={{ borderTop: '1px solid var(--brio-border)', paddingTop: '10px' }}>
            <div className="brio-text-xs brio-font-bold" style={{ marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--brio-grey)' }}>Invoice</div>
            <table style={{ fontSize: '11px' }}><tbody>
              <tr><td style={cellL}>Number</td><td style={cellV}>{invoice?.invoiceNumber || '(draft)'}</td></tr>
              <tr><td style={cellL}>Net Amount</td><td style={cellV}>{formatMoney ? formatMoney(Number(invoice?.netAmount) || 0, invoice?.currency || 'INR') : fmtCurr(invoice?.netAmount || 0)}</td></tr>
              <tr><td style={cellL}>Status</td><td style={cellV}>{invoice?.status || 'unknown'}{invoice?.paidConfirmed ? ' (already paid)' : ''}</td></tr>
            </tbody></table>
          </div>
          {paymentEvent?.payerName && (
            <label className="brio-flex brio-items-center brio-gap-xs brio-text-xs" style={{ cursor: 'pointer', borderTop: '1px solid var(--brio-border)', paddingTop: '10px' }}>
              <input type="checkbox" checked={learnPayer} onChange={(e) => setLearnPayer(e.target.checked)} />
              Learn payer name for this project
            </label>
          )}
          {error && <div className="brio-text-xs" style={{ color: 'var(--brio-red)' }}>{error}</div>}
        </div>
        <div className="brio-flex brio-justify-end brio-gap-xs" style={{ padding: '12px 16px', borderTop: '1px solid var(--brio-border)' }}>
          <button onClick={onClose} className="brio-btn brio-btn-secondary" style={{ padding: '6px 14px', fontSize: '11px' }}>Cancel</button>
          <button onClick={handleConfirm} disabled={confirming} className="brio-btn brio-btn-primary" style={{ padding: '6px 14px', fontSize: '11px', opacity: confirming ? 0.5 : 1 }}>
            {confirming ? 'Confirming...' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// SUB-COMPONENT: PaymentInboxPanel (Step 1 - Suggested/Unmatched events)
// ============================================================================
// Now rendered above the project selector so new payments are immediately visible.
// Features: alert banner, email metadata tooltip, expense payment option.
// ============================================================================
function PaymentInboxPanel({
  suggestedEvents, unmatchedEvents, loading: evtLoading,
  invoices, projects, activeClients, fmtProject,
  onConfirmMatch, onIgnore, onLinkToInvoice, onExpensePayment,
  fmtCurr, canConfirm,
  // Archived tab props
  archivedEvents, archivedLoading, archivedPage, archivedHasNext,
  onFetchArchived,
}) {
  const [expanded, setExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState('suggested');
  const [linkingEventId, setLinkingEventId] = useState(null);
  const [archivedInitialized, setArchivedInitialized] = useState(false);
  const [tooltipEvtId, setTooltipEvtId] = useState(null);
  const [expenseEvtId, setExpenseEvtId] = useState(null);
  const [expenseNote, setExpenseNote] = useState('');
  const [ignoreConfirmId, setIgnoreConfirmId] = useState(null);

  const totalCount = (suggestedEvents?.length || 0) + (unmatchedEvents?.length || 0);
  const archivedCount = archivedEvents?.length || 0;

  // Auto-expand when there are new payments
  const hasNewPayments = totalCount > 0;

  if (totalCount === 0 && archivedCount === 0 && !evtLoading && !archivedLoading) return null;

  const events = activeTab === 'suggested' ? (suggestedEvents || []) : activeTab === 'unmatched' ? (unmatchedEvents || []) : [];
  const issuedInvoices = invoices.filter(i => i.status === 'issued');

  // Format received/email date
  const fmtEmailDate = (d) => {
    if (!d) return null;
    const str = typeof d === 'string' ? d : '';
    const dateOnly = str.slice(0, 10);
    return fmtDate(dateOnly);
  };

  // Fetch archived on first tab switch
  const handleTabSwitch = (tab) => {
    setActiveTab(tab);
    if (tab === 'archived' && !archivedInitialized && onFetchArchived) {
      onFetchArchived(0);
      setArchivedInitialized(true);
    }
  };

  const handleArchivedPage = (newPage) => {
    if (onFetchArchived) onFetchArchived(newPage);
  };

  const tabStyle = (active) => ({
    padding: '6px 14px', fontSize: '11px', border: 'none', cursor: 'pointer',
    background: active ? 'var(--brio-white, #fff)' : 'transparent',
    borderBottom: active ? '2px solid var(--brio-primary, #2563eb)' : '2px solid transparent',
    fontWeight: active ? 600 : 400, color: active ? 'var(--brio-primary, #2563eb)' : 'var(--brio-grey)',
  });

  // Render a single event row with enhanced info
  const renderEventRow = (evt) => {
    const showTooltip = tooltipEvtId === evt.id;
    // Clean up the bank-alert body for preview: remove the disclosed account
    // balance, drop the trailing footer boilerplate (call-centre line, sign-off,
    // marketing, copyright), then strip tracking/marketing URLs — leaving only
    // the transaction details. Each transform lives in its own tested file.
    const emailBodyClean = evt.emailBody
      ? stripBankEmailFooter(redactBankBalance(evt.emailBody))
          .replace(/https?:\/\/\S+/g, '')           // strip tracking URLs
          .replace(/\n{3,}/g, '\n\n').trim()
      : null;
    const emailBodySnippet = emailBodyClean ? (emailBodyClean.length > 1200 ? emailBodyClean.slice(0, 1200) + '…' : emailBodyClean) : null;
    const projectForEvt = evt.suggestedProjectId ? (projects || []).find(p => String(p.id) === String(evt.suggestedProjectId)) : null;
    const clientForEvt = projectForEvt ? (activeClients || []).find(c => c.id === projectForEvt.clientId) : null;

    return (
      <div key={evt.id} style={{ padding: '8px 16px', borderBottom: '1px solid var(--brio-border)', position: 'relative' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Row 1: Amount, transaction date, confidence, email received date */}
            <div className="brio-flex brio-items-center brio-gap-xs" style={{ flexWrap: 'wrap' }}>
              <span className="brio-font-medium brio-text-s">{fmtCurr(evt.amount)}</span>
              <span className="brio-text-xs brio-text-grey">txn {fmtDate(evt.txnDate)}</span>
              {evt.createdAt && (
                <span className="brio-text-xs" style={{ color: 'var(--brio-grey)', fontStyle: 'italic' }}>
                  received {fmtEmailDate(evt.createdAt)}
                </span>
              )}
              {evt.suggestedConfidence > 0 && (
                <span className="brio-text-xs" style={{ color: evt.suggestedConfidence >= 80 ? 'var(--brio-green, #16a34a)' : 'var(--brio-gold)' }}>{evt.suggestedConfidence}% match</span>
              )}
            </div>
            {/* Row 2: Payer, suggested invoice, project */}
            <div className="brio-text-xs brio-text-grey" style={{ marginTop: '2px' }}>
              {evt.payerName || '(unknown payer)'}
              {evt.suggestedInvoiceNumber && <span> &rarr; Inv. {evt.suggestedInvoiceNumber}</span>}
              {projectForEvt && (
                <span style={{ marginLeft: '8px', color: 'var(--brio-gold)' }}>
                  [{fmtProject ? fmtProject(projectForEvt) : projectForEvt.name}
                  {clientForEvt ? ` – ${clientForEvt?.organisation || clientForEvt?.name}` : ''}]
                </span>
              )}
            </div>
            {/* Row 3: Narration */}
            {evt.narration && (
              <div className="brio-text-xs" style={{ color: 'var(--brio-grey)', marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '420px' }}>{evt.narration}</div>
            )}
            {/* Row 4: Email subject (if available) */}
            {evt.emailSubject && (
              <div className="brio-text-xs" style={{ color: '#888', marginTop: '2px', fontStyle: 'italic', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '420px' }}>
                Subject: {evt.emailSubject}
              </div>
            )}
          </div>
          {canConfirm && (
            <div className="brio-flex brio-gap-xs" style={{ flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {activeTab === 'suggested' && (
                <button onClick={() => onConfirmMatch(evt)} className="brio-btn brio-btn-primary" style={{ padding: '3px 10px', fontSize: '10px' }}>Confirm</button>
              )}
              {activeTab === 'unmatched' && (
                linkingEventId === evt.id ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <select style={{ fontSize: '10px', padding: '3px 6px', border: '1px solid var(--brio-border)' }} defaultValue=""
                      onChange={async (e) => {
                        if (!e.target.value) return;
                        const result = await onLinkToInvoice({ eventId: evt.id, invoiceId: e.target.value });
                        if (!result?.success) alert('Link failed: ' + (result?.error || ''));
                        setLinkingEventId(null);
                      }}>
                      <option value="">Select invoice...</option>
                      {issuedInvoices.map(inv => {
                        const proj = projects.find(p => String(p.id) === String(inv.projectId));
                        const projLabel = proj ? ` — ${proj.name}` : '';
                        return (
                          <option key={inv.id} value={inv.id}>{inv.invoiceNumber || `#${inv.id}`} ({fmtCurr(inv.netAmount)}){projLabel}</option>
                        );
                      })}
                    </select>
                    <button onClick={() => setLinkingEventId(null)} className="brio-text-xs" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--brio-grey)' }}>Cancel</button>
                  </div>
                ) : (
                  <button onClick={() => setLinkingEventId(evt.id)} className="brio-btn brio-btn-secondary" style={{ padding: '3px 10px', fontSize: '10px' }}>Link to Invoice</button>
                )
              )}
              {/* Expense payment button (for ad-hoc non-invoice payments) */}
              {activeTab === 'unmatched' && (
                expenseEvtId === evt.id ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <input
                      type="text"
                      placeholder="Note (e.g. client expense reimbursement)"
                      value={expenseNote}
                      onChange={(e) => setExpenseNote(e.target.value)}
                      style={{ fontSize: '10px', padding: '3px 6px', border: '1px solid var(--brio-border)', width: '200px' }}
                    />
                    <div className="brio-flex brio-gap-xs">
                      <button onClick={async () => {
                        const result = await onExpensePayment(evt.id, expenseNote);
                        if (!result?.success) alert('Failed: ' + (result?.error || ''));
                        setExpenseEvtId(null);
                        setExpenseNote('');
                      }} className="brio-btn brio-btn-primary" style={{ padding: '3px 8px', fontSize: '10px' }}>Save</button>
                      <button onClick={() => { setExpenseEvtId(null); setExpenseNote(''); }} className="brio-text-xs" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--brio-grey)' }}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <button onClick={() => setExpenseEvtId(evt.id)} className="brio-btn brio-btn-secondary" style={{ padding: '3px 10px', fontSize: '10px' }}>Expense</button>
                )
              )}
              {/* Email detail tooltip toggle */}
              {(evt.emailBody || evt.emailSubject) && (
                <button
                  onClick={() => setTooltipEvtId(showTooltip ? null : evt.id)}
                  className="brio-btn brio-btn-secondary"
                  style={{ padding: '3px 8px', fontSize: '10px', color: 'var(--brio-grey)' }}
                  title="View email details"
                >
                  <FileText className="w-3 h-3" />
                </button>
              )}
              {/* Ignore button with inline confirmation */}
              {ignoreConfirmId === evt.id ? (
                <div className="brio-flex brio-gap-xs brio-items-center">
                  <span className="brio-text-xs" style={{ color: 'var(--brio-grey)' }}>Hide?</span>
                  <button onClick={async () => {
                    const result = await onIgnore(evt.id);
                    if (!result?.success) alert('Ignore failed: ' + (result?.error || ''));
                    setIgnoreConfirmId(null);
                  }} className="brio-btn brio-btn-primary" style={{ padding: '2px 8px', fontSize: '10px' }}>Yes</button>
                  <button onClick={() => setIgnoreConfirmId(null)} className="brio-btn brio-btn-secondary" style={{ padding: '2px 8px', fontSize: '10px' }}>No</button>
                </div>
              ) : (
                <button onClick={() => setIgnoreConfirmId(evt.id)}
                  className="brio-btn brio-btn-secondary" style={{ padding: '3px 10px', fontSize: '10px', color: 'var(--brio-grey)' }}>Ignore</button>
              )}
            </div>
          )}
        </div>
        {/* Email body tooltip (expanded below the row) */}
        {showTooltip && (evt.emailBody || evt.emailSubject) && (
          <div style={{
            marginTop: '8px', padding: '10px 12px', background: '#f8f9fa', border: '1px solid var(--brio-border)',
            fontSize: '11px', color: '#444', whiteSpace: 'pre-wrap', maxHeight: '300px', overflowY: 'auto', lineHeight: '1.5',
          }}>
            {/* Parsed payment fields — key info for manual matching */}
            <div style={{ marginBottom: '8px', padding: '6px 8px', background: '#eef2ff', border: '1px solid #c7d2fe', fontSize: '11px' }}>
              {evt.amount > 0 && <div><strong>Amount:</strong> {fmtCurr(evt.amount)}</div>}
              {evt.txnDate && <div><strong>Txn Date:</strong> {fmtDate(evt.txnDate)}</div>}
              {evt.payerName && <div><strong>Payer:</strong> {evt.payerName}</div>}
              {evt.narration && <div><strong>Reference:</strong> {evt.narration}</div>}
            </div>
            {/* Email metadata */}
            {evt.emailFrom && <div style={{ marginBottom: '4px' }}><strong>From:</strong> {evt.emailFrom}</div>}
            {evt.emailSubject && <div style={{ marginBottom: '4px' }}><strong>Subject:</strong> {evt.emailSubject}</div>}
            {evt.emailDate && <div style={{ marginBottom: '8px' }}><strong>Date:</strong> {fmtEmailDate(evt.emailDate)}</div>}
            {emailBodySnippet && <div style={{ borderTop: '1px solid var(--brio-border)', paddingTop: '6px' }}>{emailBodySnippet}</div>}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ marginTop: '12px', marginBottom: '4px', border: '1px solid var(--brio-border)', background: 'var(--brio-white, #fff)' }}>
      {/* Alert banner for new unprocessed payments */}
      {hasNewPayments && !expanded && (
        <div
          className="brio-flex brio-items-center brio-gap-xs"
          style={{
            padding: '8px 16px', background: 'var(--brio-gold-light, #f5f0d0)', borderBottom: '1px solid var(--brio-gold, #9F902A)',
            cursor: 'pointer', fontSize: '12px', color: 'var(--brio-black)',
          }}
          onClick={() => setExpanded(true)}
        >
          <AlertTriangle className="w-4 h-4" style={{ color: 'var(--brio-gold, #9F902A)', flexShrink: 0 }} />
          <span>
            <strong>{totalCount}</strong> new payment{totalCount !== 1 ? 's' : ''} received
            {unmatchedEvents?.length > 0 && <span> — <strong>{unmatchedEvents.length} unmatched</strong> (assign to a project or mark as expense)</span>}
          </span>
        </div>
      )}

      {/* Collapsible header */}
      <div className="brio-flex brio-items-center brio-justify-between" style={{ padding: '10px 16px', cursor: 'pointer', background: '#f8fafc' }} onClick={() => setExpanded(!expanded)}>
        <span className="brio-font-bold brio-text-s">
          Payment Inbox {totalCount > 0 && <span style={{ fontWeight: 400, color: 'var(--brio-grey)' }}>({totalCount})</span>}
        </span>
        {expanded ? <ChevronUp className="w-4 h-4" style={{ color: 'var(--brio-grey)' }} /> : <ChevronDown className="w-4 h-4" style={{ color: 'var(--brio-grey)' }} />}
      </div>

      {expanded && (
        <div>
          <div style={{ borderBottom: '1px solid var(--brio-border)', display: 'flex' }}>
            <button style={tabStyle(activeTab === 'suggested')} onClick={() => handleTabSwitch('suggested')}>Suggested ({suggestedEvents?.length || 0})</button>
            <button style={tabStyle(activeTab === 'unmatched')} onClick={() => handleTabSwitch('unmatched')}>Unmatched ({unmatchedEvents?.length || 0})</button>
            <button style={tabStyle(activeTab === 'archived')} onClick={() => handleTabSwitch('archived')}>Archived</button>
          </div>

          {/* Suggested / Unmatched tabs */}
          {activeTab !== 'archived' ? (
          <div style={{ padding: '8px 0', maxHeight: '400px', overflowY: 'auto' }}>
            {evtLoading ? (
              <div className="brio-text-xs brio-text-grey" style={{ padding: '12px 16px' }}>Loading...</div>
            ) : events.length === 0 ? (
              <div className="brio-text-xs brio-text-grey" style={{ padding: '12px 16px' }}>No {activeTab} payments.</div>
            ) : events.map(evt => renderEventRow(evt))}
          </div>
          ) : (
          /* ── Archived tab content ── */
          <div style={{ padding: '8px 0' }}>
            {archivedLoading ? (
              <div className="brio-text-xs brio-text-grey" style={{ padding: '12px 16px' }}>Loading archived payments…</div>
            ) : (archivedEvents || []).length === 0 ? (
              <div className="brio-text-xs brio-text-grey" style={{ padding: '12px 16px' }}>No archived payments.</div>
            ) : (archivedEvents || []).map(evt => (
              <div key={evt.id} style={{ padding: '8px 16px', borderBottom: '1px solid var(--brio-border)', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="brio-flex brio-items-center brio-gap-xs">
                    <span className="brio-font-medium brio-text-s">{fmtCurr(evt.amount)}</span>
                    <span className="brio-text-xs brio-text-grey">{fmtDate(evt.txnDate)}</span>
                    <span className="brio-badge brio-badge--approved" style={{ fontSize: '9px', padding: '1px 6px' }}>Confirmed</span>
                  </div>
                  <div className="brio-text-xs brio-text-grey" style={{ marginTop: '2px' }}>
                    {evt.payerName || '(unknown payer)'}
                    {evt.invoiceNumber && <span> → Inv. {evt.invoiceNumber}</span>}
                  </div>
                  {evt.narration && (
                    <div className="brio-text-xs" style={{ color: 'var(--brio-grey)', marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '280px' }}>{evt.narration}</div>
                  )}
                  {evt.confirmedAt && (
                    <div className="brio-text-xs" style={{ color: 'var(--brio-grey)', marginTop: '2px' }}>Confirmed {fmtDate(evt.confirmedAt?.slice(0, 10))}</div>
                  )}
                </div>
              </div>
            ))}
            {/* Pagination controls */}
            {(archivedEvents || []).length > 0 && (
              <div className="brio-flex brio-items-center brio-justify-between" style={{ padding: '8px 16px', borderTop: '1px solid var(--brio-border)' }}>
                <button
                  onClick={() => handleArchivedPage((archivedPage || 0) - 1)}
                  disabled={!archivedPage || archivedPage <= 0}
                  className="brio-btn brio-btn-secondary"
                  style={{ padding: '3px 10px', fontSize: '10px', opacity: (!archivedPage || archivedPage <= 0) ? 0.4 : 1 }}
                >Previous</button>
                <span className="brio-text-xs brio-text-grey">Page {(archivedPage || 0) + 1}</span>
                <button
                  onClick={() => handleArchivedPage((archivedPage || 0) + 1)}
                  disabled={!archivedHasNext}
                  className="brio-btn brio-btn-secondary"
                  style={{ padding: '3px 10px', fontSize: '10px', opacity: !archivedHasNext ? 0.4 : 1 }}
                >Next</button>
              </div>
            )}
          </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// SUB-COMPONENT: PdfPreviewModal — office selector + real PDF preview + download
// ============================================================================
// Calls /api/invoice-pdf (server-side Chromium) → previews returned PDF in iframe.
// HTML template: src/invoices/renderInvoiceHtml.js (shared with server).
// ============================================================================


/** Download a blob as a file. */
function downloadBlob(blob, filename) {
  const a = document.createElement('a');
  const url = URL.createObjectURL(blob);
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function PdfPreviewModal({ inv, getInvoicePdfData, onClose, fmtCurr, invoicesHook }) {
  // Letterhead follows the tax regime (GST → Mumbai, VAT → Rotterdam), else the
  // invoice's office. The radio below stays as an override for edge cases such
  // as a Mumbai invoice to a foreign client with no GST.
  const defaultOffice = letterheadForInvoice(inv);
  const [officeKey, setOfficeKey] = useState(defaultOffice);
  const [invoiceDocType, setInvoiceDocType] = useState(inv?.invoiceDocType || 'tax'); // 'tax' | 'proforma'
  const [pdfUrl, setPdfUrl] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [pdfError, setPdfError] = useState('');
  const prevUrlRef = useRef(null);
  const blobRef = useRef(null);
  const filenameRef = useRef('invoice.pdf');

  // Generate PDF via server API whenever officeKey changes
  useEffect(() => {
    if (!inv) return;
    let cancelled = false;
    setGenerating(true);
    setPdfError('');

    // Revoke previous blob URL
    if (prevUrlRef.current) { URL.revokeObjectURL(prevUrlRef.current); prevUrlRef.current = null; }
    setPdfUrl(null);
    blobRef.current = null;

    // Build filename from local data (no server round-trip needed for this)
    const pdfData = getInvoicePdfData?.(inv, officeKey);
    const projSlug = (pdfData?.projectName || 'project')
      .replace(/\s+/g, '-')
      .replace(/[^a-zA-Z0-9-]/g, '')
      .substring(0, 30);
    const typeTag = invoiceDocType === 'proforma' ? 'PROFORMA' : 'INVOICE';
    filenameRef.current = `BRIO_${officeKey.toUpperCase()}_${typeTag}_${inv.invoiceNumber || 'DRAFT'}_${projSlug}.pdf`;

    // Include debug flag if URL has ?debugPdf
    const debug = typeof window !== 'undefined' && window.location?.search?.includes('debugPdf');

    (async () => {
      try {
        // Get auth token for the API call
        const { supabase } = await import('../../supabaseClient');
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token || '';

        const response = await fetch('/api/invoice-pdf', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            invoiceId: inv.id,
            officeKey,
            invoiceDocType,
            debug,
          }),
        });

        if (cancelled) return;

        if (!response.ok) {
          let errMsg = `Server error (${response.status})`;
          try {
            const errJson = await response.json();
            errMsg = errJson.error || errMsg;
          } catch { /* ignore parse error */ }
          throw new Error(errMsg);
        }

        // Extract filename from Content-Disposition header if present
        const disposition = response.headers.get('Content-Disposition');
        if (disposition) {
          const match = disposition.match(/filename="?([^"]+)"?/);
          if (match?.[1]) filenameRef.current = match[1];
        }

        const blob = await response.blob();
        if (cancelled) return;

        blobRef.current = blob;
        const url = URL.createObjectURL(blob);
        prevUrlRef.current = url;
        setPdfUrl(url);
      } catch (err) {
        if (!cancelled) setPdfError(err?.message || 'Failed to generate PDF');
      } finally {
        if (!cancelled) setGenerating(false);
      }
    })();

    return () => { cancelled = true; };
  }, [inv, officeKey, invoiceDocType, getInvoicePdfData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup on unmount
  useEffect(() => {
    return () => { if (prevUrlRef.current) URL.revokeObjectURL(prevUrlRef.current); };
  }, []);

  const handleDownload = () => {
    if (!blobRef.current) return;
    downloadBlob(blobRef.current, filenameRef.current);

    // Keep the invoice's office in step with the letterhead it went out on,
    // only when it actually changed.
    const selectedOffice = officeKey === 'mumbai' ? 'IN' : 'NL';
    if (invoicesHook?.updateInvoice && inv.id && inv.officeLocation !== selectedOffice) {
      invoicesHook.updateInvoice(inv.id, { officeLocation: selectedOffice });
    }
  };

  if (!inv) return null;

  const modalBg = { position: 'fixed', inset: 0, zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 };
  const modalOverlay = { position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' };

  return (
    <div style={modalBg}>
      <div style={modalOverlay} onClick={onClose} />
      <div style={{
        position: 'relative', background: '#fff', borderRadius: 8, width: '100%', maxWidth: 700,
        maxHeight: '90vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 8px 32px rgba(0,0,0,0.18)', fontFamily: 'var(--brio-font-family, Raleway, sans-serif)'
      }}>
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(0,0,0,0.1)', flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 13, fontWeight: 400 }}>Invoice PDF Preview</span>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}>
              <X style={{ width: 14, height: 14, color: '#666' }} />
            </button>
          </div>

          {/* Office selector */}
          <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, fontWeight: 400, color: '#666' }}>Generate invoice as:</span>
            {['rotterdam', 'mumbai'].map(key => (
              <label key={key} style={{
                display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer',
                fontSize: 11, fontWeight: officeKey === key ? 500 : 300,
                color: officeKey === key ? '#9F902A' : '#666'
              }}>
                <input type="radio" name="pdfOffice" value={key} checked={officeKey === key}
                  onChange={() => setOfficeKey(key)} style={{ accentColor: '#9F902A' }} />
                {key.charAt(0).toUpperCase() + key.slice(1)}
              </label>
            ))}
            <span style={{ fontSize: 9, color: '#999', fontStyle: 'italic' }}>
              {officeKey === defaultOffice
                ? (officeForTaxRegime(inv.taxRegime) ? 'Set by the tax regime. Change only for an exception.' : 'Set by the invoice office.')
                : 'Override: header and bank details differ from the default.'}
            </span>
          </div>

          {/* Document type selector */}
          <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, fontWeight: 400, color: '#666' }}>Document type:</span>
            {[{ key: 'tax', label: 'Tax Invoice' }, { key: 'proforma', label: 'Proforma Invoice' }].map(({ key, label }) => (
              <label key={key} style={{
                display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer',
                fontSize: 11, fontWeight: invoiceDocType === key ? 500 : 300,
                color: invoiceDocType === key ? '#9F902A' : '#666'
              }}>
                <input type="radio" name="pdfDocType" value={key} checked={invoiceDocType === key}
                  onChange={() => setInvoiceDocType(key)} style={{ accentColor: '#9F902A' }} />
                {label}
              </label>
            ))}
          </div>
        </div>

        {/* Preview */}
        <div style={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
          {generating ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#999', fontSize: 11 }}>
              <div style={{ marginBottom: 8 }}>Generating PDF on server…</div>
              <div style={{ fontSize: 10, color: '#bbb' }}>This may take a few seconds on first load.</div>
            </div>
          ) : pdfError ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#934948', fontSize: 11 }}>
              <div style={{ marginBottom: 8 }}>Error generating PDF</div>
              <div style={{ fontSize: 10, color: '#999' }}>{pdfError}</div>
            </div>
          ) : pdfUrl ? (
            <iframe src={pdfUrl} title="Invoice PDF preview" style={{ width: '100%', height: '60vh', border: 'none' }} />
          ) : (
            <div style={{ padding: 40, textAlign: 'center', color: '#999', fontSize: 11 }}>No preview available</div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 20px', borderTop: '1px solid rgba(0,0,0,0.1)', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', flexShrink: 0, flexWrap: 'wrap', gap: 8 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose}
              style={{ padding: '6px 14px', fontSize: 11, border: '1px solid rgba(0,0,0,0.15)', borderRadius: 4, background: '#fff', cursor: 'pointer', fontFamily: 'inherit' }}>
              Close
            </button>
            <button onClick={handleDownload} disabled={generating || !blobRef.current}
              style={{
                padding: '6px 14px', fontSize: 11, border: '1px solid #9F902A', borderRadius: 4,
                background: '#9F902A', color: '#fff', cursor: 'pointer', fontFamily: 'inherit',
                opacity: (generating || !blobRef.current) ? 0.5 : 1, display: 'flex', alignItems: 'center', gap: 4
              }}>
              <Download style={{ width: 11, height: 11 }} /> Download PDF
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// SUB-COMPONENT: CompactPrimaryAction — single context button for the compact row
// ============================================================================
function CompactPrimaryAction({ row, canCreate, canConfirm, openCreate, openEdit, onIssue, onConfirmPaid, timelineInvDate }) {
  const { stageInvs, primaryInv, hasInvoices, paid, stage } = row;
  const btnStyle = { background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px', fontSize: '9px', fontFamily: 'var(--brio-font-family)' };

  // Paid → no primary action
  if (paid.length > 0 && paid.length === stageInvs.length) return null;

  // Issued and unpaid → "Confirm"
  const issuedUnpaid = stageInvs.filter(i => i.status === 'issued');
  if (issuedUnpaid.length > 0 && canConfirm) {
    return (
      <button onClick={(e) => { e.stopPropagation(); onConfirmPaid(issuedUnpaid[0]); }}
        title="Confirm payment" style={{ ...btnStyle, color: 'var(--brio-green, #16a34a)' }}>
        <Check className="w-3 h-3" />
      </button>
    );
  }

  // Draft exists → "Edit" or "Issue"
  const drafts = stageInvs.filter(i => i.status === 'draft');
  if (drafts.length > 0 && canCreate) {
    return (
      <button onClick={(e) => { e.stopPropagation(); openEdit(drafts[0]); }}
        title="Edit draft" style={{ ...btnStyle, color: 'var(--brio-grey)' }}>
        <Pencil className="w-3 h-3" />
      </button>
    );
  }

  // No invoices → "Create"
  if (!hasInvoices && canCreate) {
    return (
      <button onClick={(e) => { e.stopPropagation(); openCreate(stage.key, timelineInvDate); }}
        title="Create draft" style={{ ...btnStyle, color: 'var(--brio-gold)', textDecoration: 'underline' }}>
        + New
      </button>
    );
  }

  return null;
}

// ============================================================================
// SUB-COMPONENT: StageBillingTable
// ============================================================================
// Renders a Timeline-aligned Stage Billing table with:
//   - Same stage list + ordering as Timeline (including part stages)
//   - Billing planning (% + lumpsum override)
//   - Invoice-driven projections when invoices exist
//   - Expandable invoice detail rows per stage
// ============================================================================
function StageBillingTable({
  combinedStages, project, invoices, fmtCurr, formatMoney, canCreate, canConfirm, canDelete,
  openCreate, openEdit, onIssue, onConfirmPaid, onConfirmMatch, onUndo, onDelete,
  onExportPdf, onSendEmail, onSendReminder,
  useSupabaseDB, supabaseProjects, setProjects,
  isAdmin, invoicesHook, client,
}) {
  const [expandedKey, setExpandedKey] = React.useState(null);
  const [filter, setFilter] = React.useState('all');
  const [search, setSearch] = React.useState('');
  const [officeFilter, setOfficeFilter] = React.useState('all');
  const [showAllocatedOnly, setShowAllocatedOnly] = React.useState(true);
  // PI/TI action column drawers (collapsed = only Status visible)
  const [piDrawerOpen, setPiDrawerOpen] = React.useState(false);
  const [tiDrawerOpen, setTiDrawerOpen] = React.useState(false);
  // Stage assignment: bulk selection + busy tracking
  const [selectedInvIds, setSelectedInvIds] = React.useState(new Set());
  const [bulkStageKey, setBulkStageKey] = React.useState('');
  const [bulkBusy, setBulkBusy] = React.useState(false);
  const [busyInvIds, setBusyInvIds] = React.useState(new Set());
  const totalFee = getCurrentTotalFee(project);
  // fmtCC: format amount in the project's contract currency (no FX conversion)
  const fmtCC = (amount) => formatMoney ? formatMoney(amount, project?.contractCurrency || 'INR') : String(amount);
  const baseDesignFee = Number(project?.budget) || 0;
  // CC Retainer Total: computed from Edit Project modal config values (not derived from invoices)
  const ccMonthlyAmount = Number(project?.retainerConfig?.monthlyAmount) || 0;
  const ccPlannedMonths = Number(project?.retainerConfig?.totalMonths) || 0;
  const ccRetainerTotal = ccMonthlyAmount * ccPlannedMonths;
  const miscDesignFee = getMiscFeeTotal(project);
  const totalDesignFee = baseDesignFee + ccRetainerTotal + miscDesignFee;
  const timeline = project?.stageTimeline || {};

  // ── Per-stage invoice aggregates ──
  const stageInvoiceMap = React.useMemo(() => {
    const map = {};
    (invoices || []).forEach(inv => {
      const k = inv.stageKey;
      if (!k) return;
      if (!map[k]) map[k] = [];
      map[k].push(inv);
    });
    return map;
  }, [invoices]);

  // ── Update billing field (persist) ──
  // Option A: When a parent stage's "% of fee" changes, also write
  // stageTimeline[stageKey].invoicePercent so cashflow/analytics can read it.
  const updateBilling = async (stageKey, field, value, isPartStage, parentKey, partId, isCCMonthly) => {
    if (!project) return;
    const tl = { ...timeline };
    if (isCCMonthly) {
      // CC monthly: store directly at stageTimeline[cc__YYYY-MM].billing
      tl[stageKey] = { ...(tl[stageKey] || {}), billing: { ...((tl[stageKey] || {}).billing || {}), [field]: value } };
    } else if (isPartStage && parentKey) {
      const parentTl = { ...(tl[parentKey] || {}) };
      const parts = (parentTl.partStages || []).map(p => {
        if (p.id !== partId) return p;
        return { ...p, billing: { ...(p.billing || {}), [field]: value } };
      });
      parentTl.partStages = parts;
      tl[parentKey] = parentTl;
      // Part stages: do NOT write parent invoicePercent from part % — keep them separate
    } else {
      tl[stageKey] = { ...(tl[stageKey] || {}), billing: { ...((tl[stageKey] || {}).billing || {}), [field]: value } };
      // Option A: Sync invoicePercent for parent (non-part) stages when % of fee changes
      if (field === 'percent') {
        tl[stageKey] = { ...tl[stageKey], invoicePercent: value != null ? Number(value) : null };
      }
    }
    if (setProjects) {
      setProjects(prev => prev.map(p =>
        p.id === project.id ? { ...p, stageTimeline: tl } : p
      ));
    }
    if (useSupabaseDB && supabaseProjects) {
      // Only send the key that actually changed, not the entire timeline blob.
      const changedKey = isPartStage ? parentKey : stageKey;
      const partial = { [changedKey]: tl[changedKey] };
      const result = await supabaseProjects.mergeProjectTimeline(project.id, partial, timeline || {});
      if (!result.success) console.error('[StageBilling] save error:', result.error);
    }
  };

  // ── Resolve billing for a stage ──
  const getBilling = (stage) => {
    if (stage.isCCMonthly) {
      // CC monthly: billing stored at stageTimeline[cc__YYYY-MM].billing
      return timeline[stage.key]?.billing || {};
    }
    if (stage.isPartStage) {
      const parentParts = timeline[stage.parentKey]?.partStages || [];
      const part = parentParts.find(p => p.id === stage.partId);
      return part?.billing || {};
    }
    // Misc scopes: billing stored at stageTimeline[misc__<scopeId>].billing
    if (stage.isMiscScope) {
      return timeline[stage.key]?.billing || {};
    }
    return timeline[stage.key]?.billing || {};
  };

  // ── Compute planned amount for a full stage (uses baseDesignFee, not totalFee) ──
  const getParentPlanned = (parentKey) => {
    // Misc scope parents: use scope amount as planned
    if (parentKey.startsWith('misc__')) {
      const scopeId = parentKey.replace('misc__', '');
      const scope = (Array.isArray(project?.miscFeeScopes) ? project.miscFeeScopes : [])
        .find(s => s.id === scopeId);
      if (scope) {
        const billing = timeline[parentKey]?.billing || {};
        const ls = billing.lumpsum != null ? Number(billing.lumpsum) : null;
        if (ls != null && ls > 0) return ls;
        return Number(scope.amount) || 0;
      }
    }
    const billing = timeline[parentKey]?.billing || {};
    const pct = billing.percent != null ? Number(billing.percent) : null;
    const ls = billing.lumpsum != null ? Number(billing.lumpsum) : null;
    if (ls != null && ls > 0) return ls;
    if (pct != null) return pct * baseDesignFee / 100;
    return 0;
  };

  // ── Compute row data ──
  const rows = React.useMemo(() => {
    let parentIdx = 0;
    const partCounters = {};

    return combinedStages.map(stage => {
      let rowNum = '';
      if (!stage.isPartStage) {
        parentIdx++;
        partCounters[stage.key] = 0;
        rowNum = String(parentIdx);
      } else {
        partCounters[stage.parentKey] = (partCounters[stage.parentKey] || 0) + 1;
        const parentOrder = combinedStages.filter(s => !s.isPartStage).findIndex(s => s.key === stage.parentKey) + 1;
        rowNum = `${parentOrder}.${partCounters[stage.parentKey]}`;
      }

      const billing = getBilling(stage);
      const isPart = stage.isPartStage;
      const isCCMonthly = !!stage.isCCMonthly;

      // % input value and amount calculation
      const isMiscScope = !!stage.isMiscScope;
      let pctValue, amtFromPct, plannedAmt;
      if (isCCMonthly) {
        // CC monthly: lumpsum-only, defaults to retainerMonthlyAmount
        pctValue = null; // % not applicable
        amtFromPct = 0;
        const ls = billing.lumpsum != null ? Number(billing.lumpsum) : null;
        const defaultLs = stage.retainerMonthlyAmount || 0;
        plannedAmt = (ls != null && ls > 0) ? ls : defaultLs;
      } else if (isMiscScope) {
        // Misc scope: lumpsum-based, defaults to scope amount
        pctValue = null; // % not applicable for misc scopes
        amtFromPct = 0;
        const ls = billing.lumpsum != null ? Number(billing.lumpsum) : null;
        plannedAmt = (ls != null && ls > 0) ? ls : (stage.miscScopeAmount || 0);
      } else if (isPart) {
        // Part stage: % is of parent stage's planned amount
        const pctOfStage = billing.percentOfStage != null ? Number(billing.percentOfStage) : null;
        const ls = billing.lumpsum != null ? Number(billing.lumpsum) : null;
        const parentPlanned = getParentPlanned(stage.parentKey);
        pctValue = pctOfStage;
        amtFromPct = pctOfStage != null ? (parentPlanned * pctOfStage / 100) : 0;
        plannedAmt = (ls != null && ls > 0) ? ls : amtFromPct;
      } else {
        // Full stage: % is of base design fee (excluding CC)
        const pct = billing.percent != null ? Number(billing.percent) : null;
        const ls = billing.lumpsum != null ? Number(billing.lumpsum) : null;
        pctValue = pct;
        amtFromPct = pct != null ? (pct * baseDesignFee / 100) : 0;
        plannedAmt = (ls != null && ls > 0) ? ls : amtFromPct;
      }
      // For CC monthly, lumpsum display defaults to retainer amount if not overridden
      const lumpsum = billing.lumpsum != null ? Number(billing.lumpsum) : (isCCMonthly ? (stage.retainerMonthlyAmount || null) : null);

      const stageInvs = stageInvoiceMap[stage.key] || [];
      const hasInvoices = stageInvs.length > 0;
      const issued = stageInvs.filter(i => i.status === 'issued' || i.status === 'paid');
      const drafts = stageInvs.filter(i => i.status === 'draft');
      const paid = stageInvs.filter(i => i.status === 'paid');

      // Primary invoice for actions: prefer latest draft, then latest issued
      const primaryInv = drafts[0] || issued[0] || null;
      const latestInv = issued[0] || drafts[0] || null;
      const sumBase = hasInvoices ? stageInvs.reduce((s, i) => s + (Number(i.baseAmount) || 0), 0) : plannedAmt;
      const sumNet = hasInvoices ? stageInvs.reduce((s, i) => s + (Number(i.netAmount) || 0), 0) : plannedAmt;

      let status = 'Planned';
      let statusClass = '';
      if (paid.length > 0) { status = 'Paid'; statusClass = 'brio-badge--approved'; }
      else if (issued.length > 0) { status = 'Issued'; statusClass = 'brio-badge--warning'; }
      else if (drafts.length > 0) { status = 'Draft'; statusClass = ''; }

      // ── PI / TI independent status computation ──
      const piInvs = stageInvs.filter(i => i.invoiceDocType === 'proforma');
      const tiInvs = stageInvs.filter(i => i.invoiceDocType === 'tax' || (!i.invoiceDocType && i.status !== 'draft'));

      function computeTypeStatus(invs) {
        if (invs.length === 0) return { label: null, cls: '' };
        const hasPaid = invs.some(i => i.status === 'paid');
        const hasIssued = invs.some(i => i.status === 'issued');
        const hasDraft = invs.some(i => i.status === 'draft');
        if (hasPaid) return { label: 'Paid', cls: 'brio-badge--approved' };
        if (hasIssued) return { label: 'Issued', cls: 'brio-badge--warning' };
        if (hasDraft) return { label: 'Draft', cls: '' };
        return { label: null, cls: '' };
      }
      const piStatus = computeTypeStatus(piInvs);
      const tiStatus = computeTypeStatus(tiInvs);

      let gstDisplay = '—';
      let tdsDisplay = '—';
      if (hasInvoices) {
        const gstVals = [...new Set(stageInvs.map(i => i.gstPercent))];
        const tdsVals = [...new Set(stageInvs.map(i => i.tdsPercent))];
        gstDisplay = gstVals.length === 1 ? String(gstVals[0] || 0) : 'mixed';
        tdsDisplay = tdsVals.length === 1 ? String(tdsVals[0] || 0) : 'mixed';
      }

      return {
        stage, rowNum, billing, pctValue, lumpsum, amtFromPct, plannedAmt,
        stageInvs, hasInvoices, primaryInv, latestInv, sumBase, sumNet,
        status, statusClass, gstDisplay, tdsDisplay, paid, isPart, isCCMonthly, isMiscScope,
        piStatus, tiStatus, piInvs, tiInvs,
        // A1: parent stage has substages → disable its controls
        hasSubstages: !isPart && !isCCMonthly && combinedStages.some(s => s.isPartStage && s.parentKey === stage.key),
      };
    });
  }, [combinedStages, timeline, stageInvoiceMap, baseDesignFee]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Part stage % of parent validation warnings ──
  const partSumWarnings = React.useMemo(() => {
    const warnings = {};
    const parentKeys = [...new Set(combinedStages.filter(s => s.isPartStage).map(s => s.parentKey))];
    parentKeys.forEach(pk => {
      const parts = (timeline[pk]?.partStages || []);
      if (parts.length === 0) return;
      const sum = parts.reduce((acc, p) => acc + (Number(p.billing?.percentOfStage) || 0), 0);
      if (parts.some(p => p.billing?.percentOfStage != null) && Math.abs(sum - 100) > 0.01) {
        warnings[pk] = sum;
      }
    });
    return warnings;
  }, [combinedStages, timeline]);

  // ── Filter ──
  const filteredRows = React.useMemo(() => {
    let r = rows;

    // "Allocated only" filter: show stages with allocation > 0, CC always visible
    // If a child substage is allocated but parent is not, auto-include parent as group header
    if (showAllocatedOnly) {
      const allocatedKeys = new Set();
      r.forEach(row => {
        const { stage, isCCMonthly, isPart, pctValue, lumpsum, plannedAmt } = row;
        // CC stages/months always visible
        if (stage.key === 'cc' || isCCMonthly) {
          allocatedKeys.add(stage.key);
          return;
        }
        // Allocated = has % > 0 OR lumpsum > 0 OR a non-zero planned amount
        const hasPct = (Number(pctValue) || 0) > 0;
        const hasLs = (Number(lumpsum) || 0) > 0;
        const hasPlanned = (Number(plannedAmt) || 0) > 0;
        if (hasPct || hasLs || hasPlanned) {
          allocatedKeys.add(stage.key);
          // Auto-include parent so substage doesn't float
          if (isPart && stage.parentKey) allocatedKeys.add(stage.parentKey);
        }
      });
      r = r.filter(row => allocatedKeys.has(row.stage.key));
    }

    if (filter === 'with_invoices') r = r.filter(x => x.hasInvoices);
    else if (filter === 'planned') r = r.filter(x => !x.hasInvoices);
    else if (filter === 'paid') r = r.filter(x => x.paid.length > 0);
    else if (filter === 'unassigned') {
      // Show only stages that have at least one unassigned imported invoice
      r = r.filter(x => x.stageInvs.some(inv =>
        inv.source === 'tally_import' && (!inv.stageKey || inv.stageKey === 'legacy_import')
      ));
    } else if (filter === 'tally') {
      // Show only stages that have at least one tally-imported invoice
      r = r.filter(x => x.stageInvs.some(inv => inv.source === 'tally_import'));
    }
    if (officeFilter !== 'all') {
      r = r.filter(x => {
        if (!x.hasInvoices) return true; // show planned rows regardless
        return x.stageInvs.some(inv => inv.officeLocation === officeFilter);
      });
    }
    if (search) {
      const q = search.toLowerCase();
      r = r.filter(x =>
        x.stage.name.toLowerCase().includes(q) ||
        (x.latestInv?.invoiceNumber || '').toLowerCase().includes(q)
      );
    }
    return r;
  }, [rows, filter, search, officeFilter, showAllocatedOnly, project]);

  // ── Totals ──
  // PART 1 FIX: If a main stage has substages, its planned amount must NOT
  // contribute to the total (only the substages' planned amounts count).
  // Test scenario: Stage A 100k with substages A1 60k, A2 40k → totals = 100k, not 200k.
  const totals = React.useMemo(() => {
    return rows.reduce((acc, r) => ({
      planned: acc.planned + (r.hasSubstages ? 0 : r.plannedAmt),
      baseInv: acc.baseInv + (r.hasInvoices ? r.sumBase : 0),
      netInv: acc.netInv + (r.hasInvoices ? r.sumNet : 0),
    }), { planned: 0, baseInv: 0, netInv: 0 });
  }, [rows]);

  // ── Paid totals ──
  const paidTotals = React.useMemo(() => {
    return rows.reduce((acc, r) => {
      const paidAmt = r.paid.reduce((s, i) => s + (Number(i.netAmount) || 0), 0);
      return { paid: acc.paid + paidAmt };
    }, { paid: 0 });
  }, [rows]);

  // ── Total allocated % of base design fee (main stages only, excludes CC + misc) ──
  // What the fixed and custom stages are planned to bill, as a share of the
  // fee, summed the way the Planned total is: each stage's lump sum if entered,
  // else its %, and a stage with substages counted through them. Adding up the
  // % column alone flagged RF, whose % are notes against separate AD and ID
  // fees (150% in total) while its lump sums add up to exactly the fee. With no
  // fee to divide by, fall back to the % column.
  const totalAllocatedPercent = React.useMemo(() => {
    const isMainRow = r => !r.isCCMonthly && !r.isMiscScope && r.stage.key !== 'cc'
      && !(r.isPart && (r.stage.parentKey === 'cc' || String(r.stage.parentKey || '').startsWith('misc__')));
    if (!(baseDesignFee > 0)) {
      return rows.reduce((sum, r) =>
        (isMainRow(r) && !r.isPart ? sum + (Number(r.pctValue) || 0) : sum), 0);
    }
    const plannedTotal = rows.reduce((sum, r) =>
      (isMainRow(r) && !r.hasSubstages ? sum + (Number(r.plannedAmt) || 0) : sum), 0);
    return (plannedTotal / baseDesignFee) * 100;
  }, [rows, baseDesignFee]);

  // ── Action prop bundle (avoids repetition) ──
  const actionBtnStyle = { background: 'none', border: 'none', cursor: 'pointer', padding: '2px' };

  // ── Stage options for dropdown (derived from combinedStages) ──
  const stageOptions = React.useMemo(() => {
    const opts = [];
    (combinedStages || []).forEach(stage => {
      if (stage.isPartStage && !stage.isCCMonthly) {
        opts.push({ value: stage.key, label: `↳ ${stage.name}` });
      } else if (stage.isCCMonthly) {
        opts.push({ value: stage.key, label: `  ${stage.name}` });
      } else {
        opts.push({ value: stage.key, label: stage.name });
      }
    });
    opts.push({ value: 'legacy_import', label: '— Unassigned / Legacy —' });
    return opts;
  }, [combinedStages]);

  // ── Import-aware counts for filter badges ──
  const importCounts = React.useMemo(() => {
    const allInvs = invoices || [];
    const unassigned = allInvs.filter(i =>
      i.source === 'tally_import' && (!i.stageKey || i.stageKey === 'legacy_import')
    ).length;
    const tally = allInvs.filter(i => i.source === 'tally_import').length;
    return { unassigned, tally };
  }, [invoices]);

  // ── Per-row stage change handler ──
  const handleInvStageChange = React.useCallback(async (invoiceId, newStageKey) => {
    if (!isAdmin || !invoicesHook?.updateInvoiceStageKey) return;
    setBusyInvIds(prev => new Set(prev).add(invoiceId));
    const result = await invoicesHook.updateInvoiceStageKey(invoiceId, newStageKey);
    setBusyInvIds(prev => { const n = new Set(prev); n.delete(invoiceId); return n; });
    if (!result.success) alert('Failed to assign stage: ' + (result.error || ''));
  }, [isAdmin, invoicesHook]);

  // ── Bulk stage assignment handler ──
  const handleBulkStageApply = React.useCallback(async () => {
    if (!bulkStageKey || selectedInvIds.size === 0 || !invoicesHook?.bulkUpdateInvoiceStageKey) return;
    setBulkBusy(true);
    const result = await invoicesHook.bulkUpdateInvoiceStageKey([...selectedInvIds], bulkStageKey);
    setBulkBusy(false);
    if (result.success) {
      setSelectedInvIds(new Set());
      setBulkStageKey('');
    } else {
      alert('Bulk assign failed: ' + (result.error || ''));
    }
  }, [bulkStageKey, selectedInvIds, invoicesHook]);

  // ── Selection helpers ──
  const toggleInvSelect = (id) => {
    setSelectedInvIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const DEBUG_ALLOC = true; // flip to false after confirming chip renders
  if (DEBUG_ALLOC) console.log('[StageBillingTable] totalAllocatedPercent =', totalAllocatedPercent, '| rows.length =', rows.length);

  return (
    <div style={{ marginBottom: '16px' }}>
      {/* ── Toolbar ── */}
      <div className="brio-flex brio-justify-between brio-items-center brio-mb-xs" style={{ flexWrap: 'wrap', gap: '8px' }}>
        <h4 className="brio-font-bold" style={{ fontSize: '13px', margin: 0 }}>Stage Billing & Invoices</h4>
        <div className="brio-flex brio-gap-xs brio-items-center" style={{ flexWrap: 'wrap' }}>
          {['all', 'with_invoices', 'planned', 'paid', ...(isAdmin && importCounts.unassigned > 0 ? ['unassigned'] : []), ...(isAdmin && importCounts.tally > 0 ? ['tally'] : [])].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              style={{
                padding: '2px 8px', fontSize: '9px', border: '1px solid var(--brio-border)',
                background: filter === f ? (f === 'unassigned' || f === 'tally' ? 'var(--brio-gold-dark, #7a6f1f)' : 'var(--brio-gold)') : 'transparent',
                color: filter === f ? '#fff' : 'inherit',
                cursor: 'pointer', fontFamily: 'var(--brio-font-family)',
              }}>
              {f === 'all' ? 'All' : f === 'with_invoices' ? 'Invoiced' : f === 'planned' ? 'Planned' : f === 'paid' ? 'Paid' : f === 'unassigned' ? `Unassigned (${importCounts.unassigned})` : `Tally (${importCounts.tally})`}
            </button>
          ))}
          <input type="text" placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)}
            className="brio-input" style={{ padding: '3px 8px', fontSize: '10px', width: '110px' }} />
          <select value={officeFilter} onChange={e => setOfficeFilter(e.target.value)}
            className="brio-input" style={{ padding: '2px 6px', fontSize: '9px', border: '1px solid var(--brio-border)', fontFamily: 'var(--brio-font-family)' }}>
            <option value="all">All offices</option>
            {OFFICE_LOCATIONS.map(loc => <option key={loc} value={loc}>{loc}</option>)}
          </select>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '9px', color: 'var(--brio-grey)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
            <input type="checkbox" checked={showAllocatedOnly} onChange={e => setShowAllocatedOnly(e.target.checked)} style={{ accentColor: 'var(--brio-gold, #9F902A)' }} />
            Allocated only
          </label>
        </div>
      </div>

      {/* ── Summary chips (stacked layout) ── */}
      <div className="brio-mb-xs" style={{ padding: '8px 12px', background: 'var(--brio-grey-light)', border: '1px solid var(--brio-border)', fontSize: '10px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3px 16px' }}>
        <div><span className="brio-text-grey">Base Design Fee:</span> <strong>{fmtCC(baseDesignFee)}</strong></div>
        <div><span className="brio-text-grey">Planned:</span> <strong>{fmtCC(totals.planned)}</strong></div>
        {miscDesignFee > 0 && <div><span className="brio-text-grey">Misc Design Fee:</span> <strong>{fmtCC(miscDesignFee)}</strong></div>}
        <div><span className="brio-text-grey">Invoiced:</span> <strong>{fmtCC(totals.baseInv)}</strong></div>
        {ccRetainerTotal > 0 && <div><span className="brio-text-grey">CC Fee:</span> <strong>{fmtCC(ccRetainerTotal)}</strong></div>}
        <div><span className="brio-text-grey">Paid:</span> <strong style={{ color: 'var(--brio-green, #16a34a)' }}>{fmtCC(paidTotals.paid)}</strong></div>
        <div style={{ borderTop: '1px solid var(--brio-border)', paddingTop: '3px', marginTop: '2px' }}><span className="brio-text-grey">Total Design Fee:</span> <strong>{fmtCC(totalDesignFee)}</strong></div>
        <div style={{ borderTop: '1px solid var(--brio-border)', paddingTop: '3px', marginTop: '2px' }}><span className="brio-text-grey">Allocated:</span>{' '}<strong>{totalAllocatedPercent.toFixed(1)}%</strong>{Math.abs(totalAllocatedPercent - 100) > 0.01 && <span style={{ color: 'red', marginLeft: 4, fontSize: 9 }}>⚠ not 100%</span>}</div>
      </div>

      {/* ── Layer 1: Compact stage billing table ── */}
      <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse', fontFamily: 'var(--brio-font-family)', tableLayout: 'fixed' }}>
        <colgroup>
          <col style={{ width: '28px' }} />
          <col style={{ width: '68px' }} />
          <col style={{ width: '48px' }} />
          <col style={{ width: '72px' }} />
          <col style={{ width: '72px' }} />
          <col style={{ width: '72px' }} />
          <col style={{ width: '68px' }} />
          {/* PI columns: Status always, action cols when drawer open */}
          <col style={{ width: '44px' }} />
          {piDrawerOpen && <col style={{ width: '36px' }} />}
          {piDrawerOpen && <col style={{ width: '36px' }} />}
          {piDrawerOpen && <col style={{ width: '36px' }} />}
          {piDrawerOpen && canDelete && <col style={{ width: '32px' }} />}
          {/* TI columns: Status always, action cols when drawer open */}
          <col style={{ width: '44px' }} />
          {tiDrawerOpen && <col style={{ width: '36px' }} />}
          {tiDrawerOpen && <col style={{ width: '36px' }} />}
          {tiDrawerOpen && <col style={{ width: '36px' }} />}
          {tiDrawerOpen && canDelete && <col style={{ width: '32px' }} />}
        </colgroup>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--brio-border)', position: 'sticky', top: 0, background: 'var(--brio-white, #fff)', zIndex: 3 }}>
            <th colSpan={7} />
            <th colSpan={piDrawerOpen ? (canDelete ? 5 : 4) : 1}
              onClick={() => setPiDrawerOpen(p => !p)}
              style={{ padding: '3px 2px 0', textAlign: 'center', fontSize: '8px', fontWeight: 600, color: 'var(--brio-gold)', borderLeft: '1px solid var(--brio-border)', letterSpacing: '0.5px', cursor: 'pointer', userSelect: 'none' }}>
              {piDrawerOpen ? <ChevronDown className="w-2 h-2" style={{ display: 'inline', verticalAlign: 'middle', marginRight: '2px' }} /> : <ChevronRight className="w-2 h-2" style={{ display: 'inline', verticalAlign: 'middle', marginRight: '2px' }} />}
              {piDrawerOpen ? 'PROFORMA (PI)' : 'PI'}
            </th>
            <th colSpan={tiDrawerOpen ? (canDelete ? 5 : 4) : 1}
              onClick={() => setTiDrawerOpen(p => !p)}
              style={{ padding: '3px 2px 0', textAlign: 'center', fontSize: '8px', fontWeight: 600, color: 'var(--brio-grey)', borderLeft: '1px solid var(--brio-border)', letterSpacing: '0.5px', cursor: 'pointer', userSelect: 'none' }}>
              {tiDrawerOpen ? <ChevronDown className="w-2 h-2" style={{ display: 'inline', verticalAlign: 'middle', marginRight: '2px' }} /> : <ChevronRight className="w-2 h-2" style={{ display: 'inline', verticalAlign: 'middle', marginRight: '2px' }} />}
              {tiDrawerOpen ? 'TAX (TI)' : 'TI'}
            </th>
          </tr>
          <tr style={{ borderBottom: '2px solid var(--brio-border)', position: 'sticky', top: '18px', background: 'var(--brio-white, #fff)', zIndex: 2 }}>
            <th style={{ padding: '5px 3px', textAlign: 'center', fontWeight: 600 }}>#</th>
            <th style={{ padding: '5px 4px', textAlign: 'left', fontWeight: 600 }}>Stage</th>
            <th style={{ padding: '5px 3px', textAlign: 'center', fontWeight: 600 }}>Stage %</th>
            <th style={{ padding: '5px 4px', textAlign: 'right', fontWeight: 600 }}>Lumpsum {project?.contractCurrency === 'EUR' ? '(€)' : '(₹)'}</th>
            <th style={{ padding: '5px 4px', textAlign: 'right', fontWeight: 600 }}>Planned</th>
            <th style={{ padding: '5px 4px', textAlign: 'right', fontWeight: 600 }}>Invoiced</th>
            <th style={{ padding: '5px 4px', textAlign: 'right', fontWeight: 600 }}>Paid</th>
            {/* PI sub-headers */}
            <th style={{ padding: '4px 2px', textAlign: 'center', fontWeight: 500, fontSize: '9px', borderLeft: '1px solid var(--brio-border)' }}>Status</th>
            {piDrawerOpen && <th style={{ padding: '4px 2px', textAlign: 'center', fontWeight: 500, fontSize: '9px' }}>Edit</th>}
            {piDrawerOpen && <th style={{ padding: '4px 2px', textAlign: 'center', fontWeight: 500, fontSize: '9px' }}>PDF</th>}
            {piDrawerOpen && <th style={{ padding: '4px 2px', textAlign: 'center', fontWeight: 500, fontSize: '9px' }}>Mail</th>}
            {piDrawerOpen && canDelete && <th style={{ padding: '4px 2px', textAlign: 'center', fontWeight: 500, fontSize: '9px' }}>Del</th>}
            {/* TI sub-headers */}
            <th style={{ padding: '4px 2px', textAlign: 'center', fontWeight: 500, fontSize: '9px', borderLeft: '1px solid var(--brio-border)' }}>Status</th>
            {tiDrawerOpen && <th style={{ padding: '4px 2px', textAlign: 'center', fontWeight: 500, fontSize: '9px' }}>Edit</th>}
            {tiDrawerOpen && <th style={{ padding: '4px 2px', textAlign: 'center', fontWeight: 500, fontSize: '9px' }}>PDF</th>}
            {tiDrawerOpen && <th style={{ padding: '4px 2px', textAlign: 'center', fontWeight: 500, fontSize: '9px' }}>Mail</th>}
            {tiDrawerOpen && canDelete && <th style={{ padding: '4px 2px', textAlign: 'center', fontWeight: 500, fontSize: '9px' }}>Del</th>}
          </tr>
        </thead>
        <tbody>
          {filteredRows.map(row => {
            const { stage, rowNum, billing, pctValue, lumpsum, amtFromPct, hasInvoices, primaryInv, latestInv, sumBase, sumNet, status, statusClass, gstDisplay, tdsDisplay, stageInvs, isPart, isCCMonthly, isMiscScope, plannedAmt, paid: paidInvs, hasSubstages, piStatus, tiStatus } = row;
            const isExpanded = expandedKey === stage.key;
            const rowBg = isPart ? 'var(--brio-grey-light)' : undefined;
            // A1: If main stage has substages, disable its planned/status/actions
            const parentDisabled = hasSubstages;

            // Timeline invoiceDate for prefill
            let timelineInvDate = null;
            if (isPart) {
              const parentParts = timeline[stage.parentKey]?.partStages || [];
              const part = parentParts.find(p => p.id === stage.partId);
              timelineInvDate = part?.invoiceDate;
            } else {
              timelineInvDate = timeline[stage.key]?.invoiceDate;
            }
            const pctField = isPart ? 'percentOfStage' : 'percent';
            const pctLabel = isPart ? '% stg' : '% fee';
            const partWarning = !isPart && partSumWarnings[stage.key] != null;
            const paidAmt = paidInvs.reduce((s, i) => s + (Number(i.netAmount) || 0), 0);

            return (
              <React.Fragment key={stage.key}>
                {/* ── Compact row ── */}
                <tr
                  style={{ borderBottom: '1px solid var(--brio-border)', background: isExpanded ? 'var(--brio-gold-light, #fef9e7)' : rowBg, cursor: 'pointer', transition: 'background 0.1s' }}
                  onClick={() => setExpandedKey(isExpanded ? null : stage.key)}
                >
                  {/* # */}
                  <td style={{ padding: '4px 3px', textAlign: 'center', color: 'var(--brio-grey)', fontSize: '10px' }}>{rowNum}</td>
                  {/* Stage */}
                  <td style={{ padding: '4px 6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <div className="brio-flex brio-items-center" style={{ gap: '3px' }}>
                      {isExpanded
                        ? <ChevronDown className="w-3 h-3" style={{ color: 'var(--brio-grey)', flexShrink: 0 }} />
                        : <ChevronRight className="w-3 h-3" style={{ color: 'var(--brio-grey)', flexShrink: 0 }} />
                      }
                      {isPart && <span className="brio-text-grey" style={{ fontSize: '9px' }}>↳</span>}
                      <span className={isPart ? 'brio-text-grey' : 'brio-font-medium'} style={{ fontSize: isPart ? '10px' : '11px' }} title={stage.name}>
                        {isPart ? getPartStageAbbreviation(stage.name) : getStageAbbreviation(stage.name)}
                      </span>
                      {partWarning && <span title={`Parts sum to ${partSumWarnings[stage.key].toFixed(1)}%`} style={{ fontSize: '8px', color: 'var(--brio-red)', marginLeft: '2px' }}>⚠</span>}
                      {stageInvs.length > 1 && <span className="brio-text-grey" style={{ fontSize: '8px', marginLeft: '2px' }}>×{stageInvs.length}</span>}
                    </div>
                  </td>
                  {/* Stage % (inline % input) */}
                  <td style={{ padding: '4px 3px', textAlign: 'center', fontSize: '9px' }} onClick={e => e.stopPropagation()}>
                    {(isCCMonthly || isMiscScope) ? (
                      <span className="brio-text-grey" style={{ fontSize: '8px' }}>—</span>
                    ) : (
                      <input
                        type="number" min="0" max="100" step="0.1"
                        value={pctValue ?? ''}
                        onChange={e => updateBilling(stage.key, pctField, e.target.value ? parseFloat(e.target.value) : null, isPart, stage.parentKey, stage.partId)}
                        placeholder="—"
                        title={isPart ? '% of stage' : '% of base design fee'}
                        style={{ width: '100%', maxWidth: '50px', padding: '1px 3px', fontSize: '9px', textAlign: 'center', border: '1px solid var(--brio-border)', fontFamily: 'var(--brio-font-family)', background: 'transparent' }}
                      />
                    )}
                  </td>
                  {/* Lumpsum (editable override) */}
                  <td style={{ padding: '4px 4px', textAlign: 'right', fontSize: '9px' }} onClick={e => e.stopPropagation()}>
                    {parentDisabled ? (
                      <span className="brio-text-grey" style={{ fontSize: '8px' }}>—</span>
                    ) : (
                      <input
                        type="number" min="0" step="1000"
                        value={lumpsum != null && lumpsum > 0 ? lumpsum : ''}
                        onChange={e => {
                          const val = e.target.value ? parseFloat(e.target.value) : null;
                          updateBilling(stage.key, 'lumpsum', val, isPart, stage.parentKey, stage.partId, isCCMonthly);
                        }}
                        placeholder="—"
                        title="Lumpsum override"
                        style={{ width: '100%', maxWidth: '70px', padding: '1px 3px', fontSize: '9px', textAlign: 'right', border: '1px solid var(--brio-border)', fontFamily: 'var(--brio-font-family)', background: 'transparent' }}
                      />
                    )}
                  </td>
                  {/* Planned — PART 1 FIX: blank for parent with substages (only substages show planned) */}
                  <td style={{ padding: '4px 4px', textAlign: 'right', opacity: parentDisabled ? 0.45 : 1 }}>
                    <span style={{ fontWeight: 500, fontSize: '10px' }}>{hasSubstages ? '—' : (plannedAmt > 0 ? fmtCC(plannedAmt) : '—')}</span>
                  </td>
                  {/* Invoiced */}
                  <td style={{ padding: '4px 4px', textAlign: 'right', fontWeight: hasInvoices ? 500 : 400, color: hasInvoices ? 'inherit' : 'var(--brio-grey)' }}>
                    {hasInvoices ? fmtCC(sumBase) : '—'}
                  </td>
                  {/* Paid */}
                  <td style={{ padding: '4px 4px', textAlign: 'right', color: paidAmt > 0 ? 'var(--brio-green, #16a34a)' : 'var(--brio-grey)', fontWeight: paidAmt > 0 ? 500 : 400 }}>
                    {paidAmt > 0 ? fmtCC(paidAmt) : '—'}
                  </td>
                  {/* ── PI columns ── */}
                  {(() => {
                    const piInv = row.piInvs[0] || null;
                    const dash = <span className="brio-text-grey" style={{ fontSize: '8px' }}>—</span>;
                    const cellStyle = { padding: '4px 2px', textAlign: 'center' };
                    if (parentDisabled) {
                      return (
                        <>
                          <td style={{ ...cellStyle, borderLeft: '1px solid var(--brio-border)' }}>{dash}</td>
                          {piDrawerOpen && <td style={cellStyle}>{dash}</td>}
                          {piDrawerOpen && <td style={cellStyle}>{dash}</td>}
                          {piDrawerOpen && <td style={cellStyle}>{dash}</td>}
                          {piDrawerOpen && canDelete && <td style={cellStyle}>{dash}</td>}
                        </>
                      );
                    }
                    return (
                      <>
                        {/* PI Status */}
                        <td style={{ ...cellStyle, borderLeft: '1px solid var(--brio-border)' }}>
                          {piStatus.label
                            ? <span className={`brio-badge ${piStatus.cls}`} style={{ fontSize: '7px', padding: '1px 4px' }}>{piStatus.label}</span>
                            : canCreate && !row.hasInvoices
                              ? <button onClick={(e) => { e.stopPropagation(); openCreate(stage.key, timelineInvDate, 'proforma'); }} title="Create PI" style={{ ...actionBtnStyle, fontSize: '8px', color: 'var(--brio-gold)', textDecoration: 'underline' }}>+ New</button>
                              : dash
                          }
                        </td>
                        {/* PI Edit */}
                        {piDrawerOpen && (
                          <td style={cellStyle} onClick={e => e.stopPropagation()}>
                            {piInv && piInv.status === 'draft' && canCreate
                              ? <button onClick={() => openEdit(piInv)} title="Edit PI draft" style={actionBtnStyle}><Pencil className="w-3 h-3" style={{ color: 'var(--brio-grey)' }} /></button>
                              : dash
                            }
                          </td>
                        )}
                        {/* PI PDF */}
                        {piDrawerOpen && (
                          <td style={cellStyle} onClick={e => e.stopPropagation()}>
                            {piInv && (piInv.status === 'issued' || piInv.status === 'paid') && onExportPdf
                              ? <button onClick={() => onExportPdf(piInv)} title="Export PI PDF" style={actionBtnStyle}><Download className="w-3 h-3" style={{ color: 'var(--brio-gold)' }} /></button>
                              : dash
                            }
                          </td>
                        )}
                        {/* PI Mail */}
                        {piDrawerOpen && (
                          <td style={cellStyle} onClick={e => e.stopPropagation()}>
                            {piInv && (piInv.status === 'issued' || piInv.status === 'paid')
                              ? <ReminderActionMenu invoice={piInv} displayStatus={getInvoiceDisplayStatus(piInv, calculateDueDate(piInv.invoiceDate, client?.paymentTermsDays).dueDate)} daysOverdue={(() => { const dd = calculateDueDate(piInv.invoiceDate, client?.paymentTermsDays).dueDate; if (!dd) return 0; return Math.max(0, Math.floor((new Date() - new Date(dd)) / 86400000)); })()} onAction={(i, tk) => onSendEmail(i, tk)} compact />
                              : dash
                            }
                          </td>
                        )}
                        {/* PI Del */}
                        {piDrawerOpen && canDelete && (
                          <td style={cellStyle} onClick={e => e.stopPropagation()}>
                            {piInv
                              ? <button onClick={() => onDelete(piInv)} title="Delete PI" style={actionBtnStyle}><Trash2 className="w-3 h-3" style={{ color: 'var(--brio-red)' }} /></button>
                              : dash
                            }
                          </td>
                        )}
                      </>
                    );
                  })()}
                  {/* ── TI columns ── */}
                  {(() => {
                    const tiInv = row.tiInvs[0] || null;
                    const dash = <span className="brio-text-grey" style={{ fontSize: '8px' }}>—</span>;
                    const cellStyle = { padding: '4px 2px', textAlign: 'center' };
                    if (parentDisabled) {
                      return (
                        <>
                          <td style={{ ...cellStyle, borderLeft: '1px solid var(--brio-border)' }}>{dash}</td>
                          {tiDrawerOpen && <td style={cellStyle}>{dash}</td>}
                          {tiDrawerOpen && <td style={cellStyle}>{dash}</td>}
                          {tiDrawerOpen && <td style={cellStyle}>{dash}</td>}
                          {tiDrawerOpen && canDelete && <td style={cellStyle}>{dash}</td>}
                        </>
                      );
                    }
                    return (
                      <>
                        {/* TI Status */}
                        <td style={{ ...cellStyle, borderLeft: '1px solid var(--brio-border)' }}>
                          {tiStatus.label
                            ? <span className={`brio-badge ${tiStatus.cls}`} style={{ fontSize: '7px', padding: '1px 4px' }}>{tiStatus.label}</span>
                            : canCreate && row.piInvs.length > 0 && !row.tiInvs.length
                              ? <button onClick={(e) => { e.stopPropagation(); openCreate(stage.key, timelineInvDate, 'tax'); }} title="Create TI" style={{ ...actionBtnStyle, fontSize: '8px', color: 'var(--brio-gold)', textDecoration: 'underline' }}>+ New</button>
                              : dash
                          }
                        </td>
                        {/* TI Edit */}
                        {tiDrawerOpen && (
                          <td style={cellStyle} onClick={e => e.stopPropagation()}>
                            {tiInv && tiInv.status === 'draft' && canCreate
                              ? <button onClick={() => openEdit(tiInv)} title="Edit TI draft" style={actionBtnStyle}><Pencil className="w-3 h-3" style={{ color: 'var(--brio-grey)' }} /></button>
                              : dash
                            }
                          </td>
                        )}
                        {/* TI PDF */}
                        {tiDrawerOpen && (
                          <td style={cellStyle} onClick={e => e.stopPropagation()}>
                            {tiInv && (tiInv.status === 'issued' || tiInv.status === 'paid') && onExportPdf
                              ? <button onClick={() => onExportPdf(tiInv)} title="Export TI PDF" style={actionBtnStyle}><Download className="w-3 h-3" style={{ color: 'var(--brio-gold)' }} /></button>
                              : dash
                            }
                          </td>
                        )}
                        {/* TI Mail */}
                        {tiDrawerOpen && (
                          <td style={cellStyle} onClick={e => e.stopPropagation()}>
                            {tiInv && (tiInv.status === 'issued' || tiInv.status === 'paid')
                              ? <ReminderActionMenu invoice={tiInv} displayStatus={getInvoiceDisplayStatus(tiInv, calculateDueDate(tiInv.invoiceDate, client?.paymentTermsDays).dueDate)} daysOverdue={(() => { const dd = calculateDueDate(tiInv.invoiceDate, client?.paymentTermsDays).dueDate; if (!dd) return 0; return Math.max(0, Math.floor((new Date() - new Date(dd)) / 86400000)); })()} onAction={(i, tk) => onSendEmail(i, tk)} compact />
                              : dash
                            }
                          </td>
                        )}
                        {/* TI Del */}
                        {tiDrawerOpen && canDelete && (
                          <td style={cellStyle} onClick={e => e.stopPropagation()}>
                            {tiInv
                              ? <button onClick={() => onDelete(tiInv)} title="Delete TI" style={actionBtnStyle}><Trash2 className="w-3 h-3" style={{ color: 'var(--brio-red)' }} /></button>
                              : dash
                            }
                          </td>
                        )}
                      </>
                    );
                  })()}
                </tr>

                {/* ── Layer 2: Expanded details panel (invoice list only — planning moved inline) ── */}
                {isExpanded && (
                  <tr>
                    <td colSpan={7 + 1 + (piDrawerOpen ? (canDelete ? 4 : 3) : 0) + 1 + (tiDrawerOpen ? (canDelete ? 4 : 3) : 0)} style={{ padding: 0, borderBottom: '2px solid var(--brio-gold, #9F902A)' }}>
                      <div style={{ padding: '10px 12px 10px 36px', background: 'var(--brio-grey-light)', fontSize: '10px' }}>

                        {/* Section B: Invoice breakdown list */}
                        {stageInvs.length > 0 && (
                          <div style={{ border: '1px solid var(--brio-border)', background: 'var(--brio-white, #fff)' }}>
                            <div style={{ padding: '4px 8px', fontWeight: 600, fontSize: '9px', color: 'var(--brio-grey)', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid var(--brio-border)' }}>
                              Invoices ({stageInvs.length})
                              {canCreate && (
                                <button onClick={(e) => { e.stopPropagation(); openCreate(stage.key, timelineInvDate); }}
                                  style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', fontSize: '9px', color: 'var(--brio-gold)', fontFamily: 'var(--brio-font-family)' }}>
                                  + Add
                                </button>
                              )}
                            </div>

                            {/* Bulk action bar for this stage's invoices (admin only) */}
                            {isAdmin && (() => {
                              const stageSelected = stageInvs.filter(i => selectedInvIds.has(i.id));
                              if (stageSelected.length === 0) return null;
                              return (
                                <div className="brio-flex brio-items-center brio-gap-xs"
                                  style={{ padding: '4px 8px', background: 'var(--brio-gold-light, #fef9e7)', borderBottom: '1px solid var(--brio-border)', fontSize: '9px' }}>
                                  <span className="brio-font-medium">{stageSelected.length} selected</span>
                                  <span className="brio-text-grey">→</span>
                                  <select value={bulkStageKey} onChange={e => setBulkStageKey(e.target.value)}
                                    className="brio-input"
                                    style={{ padding: '1px 4px', fontSize: '9px', border: '1px solid var(--brio-border)', fontFamily: 'var(--brio-font-family)', maxWidth: '180px' }}>
                                    <option value="">Assign stage…</option>
                                    {stageOptions.filter(o => o.value !== 'legacy_import').map(o =>
                                      <option key={o.value} value={o.value}>{o.label}</option>
                                    )}
                                    <option value="legacy_import">— Unassigned / Legacy —</option>
                                  </select>
                                  <button onClick={handleBulkStageApply} disabled={!bulkStageKey || bulkBusy}
                                    className="brio-btn brio-btn-primary"
                                    style={{ padding: '2px 8px', fontSize: '9px', opacity: (!bulkStageKey || bulkBusy) ? 0.5 : 1 }}>
                                    {bulkBusy ? '…' : 'Apply'}
                                  </button>
                                  <button onClick={() => setSelectedInvIds(new Set())}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '8px', color: 'var(--brio-grey)', fontFamily: 'var(--brio-font-family)' }}>
                                    Clear
                                  </button>
                                </div>
                              );
                            })()}

                            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                              <table style={{ width: '100%', fontSize: '9px', borderCollapse: 'collapse' }}>
                                <thead>
                                  <tr style={{ borderBottom: '1px solid var(--brio-border)', background: 'var(--brio-grey-light)' }}>
                                    {isAdmin && (
                                      <th style={{ padding: '3px 3px', textAlign: 'center', width: '24px' }}>
                                        <input type="checkbox"
                                          checked={stageInvs.length > 0 && stageInvs.every(i => selectedInvIds.has(i.id))}
                                          onChange={() => {
                                            const allIn = stageInvs.every(i => selectedInvIds.has(i.id));
                                            setSelectedInvIds(prev => {
                                              const next = new Set(prev);
                                              stageInvs.forEach(i => { if (allIn) next.delete(i.id); else next.add(i.id); });
                                              return next;
                                            });
                                          }}
                                          title="Select all in stage"
                                          style={{ accentColor: 'var(--brio-gold, #9F902A)' }}
                                        />
                                      </th>
                                    )}
                                    <th style={{ padding: '3px 6px', textAlign: 'left', fontWeight: 500 }}>Inv #</th>
                                    <th style={{ padding: '3px 4px', textAlign: 'center', fontWeight: 500 }}>Type</th>
                                    <th style={{ padding: '3px 4px', textAlign: 'center', fontWeight: 500 }}>Date</th>
                                    <th style={{ padding: '3px 4px', textAlign: 'right', fontWeight: 500 }}>Base</th>
                                    <th style={{ padding: '3px 4px', textAlign: 'right', fontWeight: 500 }}>Net</th>
                                    <th style={{ padding: '3px 4px', textAlign: 'center', fontWeight: 500 }}>Status</th>
                                    <th style={{ padding: '3px 3px', textAlign: 'center', fontWeight: 500 }}>Office</th>
                                    {isAdmin && <th style={{ padding: '3px 4px', textAlign: 'center', fontWeight: 500 }}>Stage</th>}
                                    <th style={{ padding: '3px 3px', textAlign: 'center', fontWeight: 500 }} title="Edit / Issue">Edit</th>
                                    <th style={{ padding: '3px 3px', textAlign: 'center', fontWeight: 500 }} title="Export PDF">PDF</th>
                                    <th style={{ padding: '3px 3px', textAlign: 'center', fontWeight: 500 }} title="Send Email">Mail</th>
                                    <th style={{ padding: '3px 3px', textAlign: 'center', fontWeight: 500 }} title="Payment">Pay</th>
                                    {canDelete && <th style={{ padding: '3px 3px', textAlign: 'center', fontWeight: 500 }}>Del</th>}
                                  </tr>
                                </thead>
                                <tbody>
                                  {stageInvs.map(inv => {
                                    const { dueDate: invDueDate } = calculateDueDate(inv.invoiceDate, client?.paymentTermsDays);
                                    const displayStatus = getInvoiceDisplayStatus(inv, invDueDate);
                                    const badge = STATUS_BADGE[displayStatus] || STATUS_BADGE[inv.status] || STATUS_BADGE.draft;
                                    const isTally = inv.source === 'tally_import';
                                    const isUnassigned = !inv.stageKey || inv.stageKey === 'legacy_import';
                                    const isBusy = busyInvIds.has(inv.id);
                                    return (
                                      <tr key={inv.id} style={{ borderBottom: '1px solid var(--brio-border)', opacity: isBusy ? 0.5 : 1 }}
                                        onMouseEnter={e => e.currentTarget.style.background = 'var(--brio-grey-light)'}
                                        onMouseLeave={e => e.currentTarget.style.background = ''}>
                                        {isAdmin && (
                                          <td style={{ padding: '4px 3px', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                                            <input type="checkbox"
                                              checked={selectedInvIds.has(inv.id)}
                                              onChange={() => toggleInvSelect(inv.id)}
                                              style={{ accentColor: 'var(--brio-gold, #9F902A)' }}
                                            />
                                          </td>
                                        )}
                                        <td style={{ padding: '4px 6px' }}>
                                          <span>{inv.invoiceNumber || <span className="brio-text-grey">draft</span>}</span>
                                          {isTally && (
                                            <span style={{ marginLeft: '3px', fontSize: '7px', padding: '0px 3px', background: 'var(--brio-gold-light, #fef9e7)', border: '1px solid var(--brio-gold)', color: 'var(--brio-gold-dark, #7a6f1f)', borderRadius: '2px', fontWeight: 500 }}>
                                              Tally
                                            </span>
                                          )}
                                        </td>
                                        <td style={{ padding: '4px 4px', textAlign: 'center', fontSize: '8px', color: inv.invoiceDocType === 'proforma' ? 'var(--brio-gold)' : 'var(--brio-grey)' }}>
                                          {inv.invoiceDocType === 'proforma' ? 'PI' : 'TI'}
                                        </td>
                                        <td style={{ padding: '4px 4px', textAlign: 'center' }}>{fmtDate(inv.invoiceDate)}</td>
                                        <td style={{ padding: '4px 4px', textAlign: 'right' }}>{fmtCC(Number(inv.baseAmount) || 0)}</td>
                                        <td style={{ padding: '4px 4px', textAlign: 'right', fontWeight: 500 }}>{fmtCC(Number(inv.netAmount) || 0)}</td>
                                        <td style={{ padding: '4px 4px', textAlign: 'center' }}>
                                          <span className={`brio-badge ${badge.className}`} style={{ fontSize: '8px', padding: '1px 5px' }}>{badge.label}</span>
                                          {displayStatus === 'overdue' && invDueDate && (() => {
                                            const today = new Date();
                                            const due = new Date(invDueDate);
                                            const diffDays = Math.floor((today - due) / (1000 * 60 * 60 * 24));
                                            return diffDays > 0 ? (
                                              <div style={{ fontSize: '7px', color: 'var(--brio-red, #dc2626)', marginTop: '1px' }}>
                                                {diffDays}d overdue
                                              </div>
                                            ) : null;
                                          })()}
                                          {(displayStatus === 'sent' || displayStatus === 'due') && invDueDate && (
                                            <div style={{ fontSize: '7px', color: 'var(--brio-muted, #94a3b8)', marginTop: '1px' }}>
                                              Due {fmtDate(invDueDate)}
                                            </div>
                                          )}
                                        </td>
                                        <td style={{ padding: '4px 3px', textAlign: 'center', fontSize: '8px', color: 'var(--brio-grey)' }}>
                                          {inv.officeLocation || '—'}
                                        </td>
                                        {isAdmin && (
                                          <td style={{ padding: '4px 4px', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                                            <select
                                              value={inv.stageKey || 'legacy_import'}
                                              onChange={e => handleInvStageChange(inv.id, e.target.value)}
                                              disabled={isBusy}
                                              className="brio-input"
                                              style={{
                                                width: '100%', maxWidth: '120px', padding: '1px 3px', fontSize: '8px',
                                                border: isUnassigned ? '1px solid var(--brio-gold)' : '1px solid var(--brio-border)',
                                                background: isUnassigned ? 'var(--brio-gold-light, #fef9e7)' : 'transparent',
                                                fontFamily: 'var(--brio-font-family)',
                                              }}
                                            >
                                              <option value="legacy_import">— Unassigned —</option>
                                              {stageOptions.filter(o => o.value !== 'legacy_import').map(o =>
                                                <option key={o.value} value={o.value}>{o.label}</option>
                                              )}
                                            </select>
                                          </td>
                                        )}
                                        {/* ── Edit / Issue ── */}
                                        <td style={{ padding: '4px 3px', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                                          {inv.status === 'draft' && canCreate ? (
                                            <div className="brio-flex brio-gap-xs brio-justify-center">
                                              <button onClick={() => openEdit(inv)} title="Edit draft" style={actionBtnStyle}>
                                                <Pencil className="w-3 h-3" style={{ color: 'var(--brio-grey)' }} />
                                              </button>
                                              <button onClick={async () => {
                                                if (!window.confirm('Issue this invoice? It will be locked.')) return;
                                                const result = await onIssue(inv.id);
                                                if (!result.success) alert(result.error);
                                              }} title="Issue invoice" style={actionBtnStyle}>
                                                <Lock className="w-3 h-3" style={{ color: 'var(--brio-gold)' }} />
                                              </button>
                                            </div>
                                          ) : null}
                                        </td>
                                        {/* ── PDF ── */}
                                        <td style={{ padding: '4px 3px', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                                          {(inv.status === 'issued' || inv.status === 'paid') && onExportPdf ? (
                                            <button onClick={() => onExportPdf(inv)} title="Export PDF" style={actionBtnStyle}>
                                              <Download className="w-3 h-3" style={{ color: 'var(--brio-gold)' }} />
                                            </button>
                                          ) : null}
                                        </td>
                                        {/* ── Mail ── */}
                                        <td style={{ padding: '4px 3px', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                                          {(inv.status === 'issued' || inv.status === 'paid') ? (
                                            <ReminderActionMenu
                                              invoice={inv}
                                              displayStatus={displayStatus}
                                              daysOverdue={(() => {
                                                if (!invDueDate) return 0;
                                                const diff = Math.floor((new Date() - new Date(invDueDate)) / 86400000);
                                                return Math.max(0, diff);
                                              })()}
                                              onAction={(i, templateKey) => onSendEmail(i, templateKey)}
                                              compact
                                            />
                                          ) : null}
                                        </td>
                                        {/* ── Pay ── */}
                                        <td style={{ padding: '4px 3px', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                                          {inv.status === 'issued' && canConfirm ? (
                                            <div className="brio-flex brio-gap-xs brio-justify-center">
                                              <button onClick={() => onConfirmPaid(inv)} title="Confirm payment" style={actionBtnStyle}>
                                                <Check className="w-3 h-3" style={{ color: 'var(--brio-green, #16a34a)' }} />
                                              </button>
                                              {inv.paymentDetected && inv.paymentEvent && (
                                                <button
                                                  onClick={() => onConfirmMatch(inv)}
                                                  title={`Bank match: ${inv.paymentEvent.payerName || '?'} ${fmtCurr(inv.paymentEvent.amount)} (${inv.paymentEvent.suggestedConfidence}%)`}
                                                  style={actionBtnStyle}>
                                                  <Check className="w-3 h-3" style={{ color: 'var(--brio-gold)' }} />
                                                </button>
                                              )}
                                            </div>
                                          ) : inv.paidConfirmed && inv.paymentEventId && canConfirm ? (
                                            <button onClick={() => onUndo(inv)} title="Undo payment" style={actionBtnStyle}>
                                              <Undo2 className="w-3 h-3" style={{ color: 'var(--brio-grey)' }} />
                                            </button>
                                          ) : null}
                                        </td>
                                        {/* ── Delete ── */}
                                        {canDelete && (
                                          <td style={{ padding: '4px 3px', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                                            <button onClick={() => onDelete(inv)} title="Delete invoice" style={actionBtnStyle}>
                                              <Trash2 className="w-3 h-3" style={{ color: 'var(--brio-red)' }} />
                                            </button>
                                          </td>
                                        )}
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}

                        {/* No invoices yet message with create button */}
                        {stageInvs.length === 0 && (
                          <div style={{ padding: '8px', textAlign: 'center', color: 'var(--brio-grey)', fontSize: '10px' }}>
                            No invoices for this stage.
                            {canCreate && (
                              <button onClick={(e) => { e.stopPropagation(); openCreate(stage.key, timelineInvDate); }}
                                style={{ marginLeft: '8px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '10px', color: 'var(--brio-gold)', textDecoration: 'underline', fontFamily: 'var(--brio-font-family)' }}>
                                Create draft
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
        <tfoot>
          <tr style={{ borderTop: '2px solid var(--brio-border)' }}>
            <td />
            <td style={{ padding: '5px 6px', fontSize: '10px', fontWeight: 600 }}>Total</td>
            <td style={{ padding: '5px 4px', textAlign: 'center', fontSize: '10px', fontWeight: 700 }}>
              <span style={{ color: Math.abs(totalAllocatedPercent - 100) > 0.01 ? 'var(--brio-red, #c00)' : 'var(--brio-green, #16a34a)' }}>
                {totalAllocatedPercent.toFixed(1)}%
              </span>
              {Math.abs(totalAllocatedPercent - 100) > 0.01 && (
                <div style={{ fontSize: '7px', color: 'var(--brio-red, #c00)', marginTop: '1px' }}>⚠ ≠ 100%</div>
              )}
            </td>
            <td />
            <td style={{ padding: '5px 4px', textAlign: 'right', fontSize: '10px', fontWeight: 600 }}>{fmtCC(totals.planned)}</td>
            <td style={{ padding: '5px 4px', textAlign: 'right', fontSize: '10px', fontWeight: 600 }}>{fmtCC(totals.baseInv)}</td>
            <td style={{ padding: '5px 4px', textAlign: 'right', fontSize: '10px', fontWeight: 600, color: 'var(--brio-green, #16a34a)' }}>{fmtCC(paidTotals.paid)}</td>
            <td colSpan={2 + (piDrawerOpen ? (canDelete ? 4 : 3) : 0) + (tiDrawerOpen ? (canDelete ? 4 : 3) : 0)} />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// ============================================================================
// SUB-COMPONENT: UnassignedImportsPanel
// ============================================================================
// Shows imported invoices with stage_key='legacy_import' (or null) and source='tally_import'.
// Allows admin per-row and bulk stage assignment.
// ============================================================================
function UnassignedImportsPanel({
  invoices, combinedStages, project, fmtCurr, formatMoney, invoicesHook, isAdmin,
}) {
  const fmtCC = (a) => formatMoney ? formatMoney(a, project?.contractCurrency || 'INR') : fmtCurr(a);
  const [selectedIds, setSelectedIds] = React.useState(new Set());
  const [bulkStageKey, setBulkStageKey] = React.useState('');
  const [busyIds, setBusyIds] = React.useState(new Set());
  const [bulkBusy, setBulkBusy] = React.useState(false);

  // Filter to unassigned imported invoices
  const unassigned = React.useMemo(() =>
    (invoices || []).filter(inv =>
      inv.source === 'tally_import' &&
      (!inv.stageKey || inv.stageKey === 'legacy_import')
    ),
    [invoices]
  );

  // Build stage options from project's stage structure
  const stageOptions = React.useMemo(() => {
    const opts = [];
    const timeline = project?.stageTimeline || {};
    (combinedStages || []).forEach(stage => {
      if (stage.isPartStage && !stage.isCCMonthly) {
        // Part stages are already in combinedStages with composite key
        opts.push({ value: stage.key, label: `↳ ${stage.name}` });
      } else if (stage.isCCMonthly) {
        opts.push({ value: stage.key, label: `  ${stage.name}` });
      } else {
        opts.push({ value: stage.key, label: stage.name });
      }
    });
    // Add legacy_import as an option (keep as-is)
    opts.push({ value: 'legacy_import', label: '— Legacy Import (unassigned)' });
    return opts;
  }, [combinedStages, project]);

  // Selection handlers
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (selectedIds.size === unassigned.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(unassigned.map(i => i.id)));
  };

  // Per-row stage change (optimistic)
  const handleRowStageChange = async (invoiceId, newStageKey) => {
    if (!invoicesHook?.updateInvoiceStageKey) return;
    setBusyIds(prev => new Set(prev).add(invoiceId));
    const result = await invoicesHook.updateInvoiceStageKey(invoiceId, newStageKey);
    setBusyIds(prev => { const n = new Set(prev); n.delete(invoiceId); return n; });
    if (!result.success) alert('Failed to assign stage: ' + (result.error || ''));
  };

  // Bulk assign
  const handleBulkApply = async () => {
    if (!bulkStageKey || selectedIds.size === 0 || !invoicesHook?.bulkUpdateInvoiceStageKey) return;
    setBulkBusy(true);
    const result = await invoicesHook.bulkUpdateInvoiceStageKey([...selectedIds], bulkStageKey);
    setBulkBusy(false);
    if (result.success) {
      setSelectedIds(new Set());
      setBulkStageKey('');
    } else {
      alert('Bulk assign failed: ' + (result.error || ''));
    }
  };

  if (unassigned.length === 0) return null;

  const allSelected = unassigned.length > 0 && selectedIds.size === unassigned.length;

  return (
    <div style={{ border: '1px solid var(--brio-gold)', background: 'var(--brio-gold-light, #fef9e7)', padding: '12px 16px', marginBottom: '12px' }}>
      <div className="brio-flex brio-justify-between brio-items-center brio-mb-xs">
        <h4 className="brio-font-bold" style={{ fontSize: '13px', margin: 0, color: 'var(--brio-gold-dark, #7a6f1f)' }}>
          Unassigned Imports ({unassigned.length})
        </h4>
        <span className="brio-text-xs brio-text-grey">
          Imported invoices need a stage assignment
        </span>
      </div>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="brio-flex brio-items-center brio-gap-xs brio-mb-xs"
          style={{ padding: '6px 10px', background: 'var(--brio-white, #fff)', border: '1px solid var(--brio-border)', fontSize: '10px' }}>
          <span className="brio-font-medium">{selectedIds.size} selected</span>
          <span className="brio-text-grey">→</span>
          <select
            value={bulkStageKey}
            onChange={e => setBulkStageKey(e.target.value)}
            className="brio-input"
            style={{ padding: '2px 6px', fontSize: '10px', border: '1px solid var(--brio-border)', fontFamily: 'var(--brio-font-family)', maxWidth: '220px' }}
          >
            <option value="">Assign stage…</option>
            {stageOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <button
            onClick={handleBulkApply}
            disabled={!bulkStageKey || bulkBusy}
            className="brio-btn brio-btn-primary"
            style={{ padding: '3px 12px', fontSize: '10px', opacity: (!bulkStageKey || bulkBusy) ? 0.5 : 1 }}
          >
            {bulkBusy ? 'Applying…' : 'Apply'}
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '9px', color: 'var(--brio-grey)', fontFamily: 'var(--brio-font-family)' }}
          >
            Clear
          </button>
        </div>
      )}

      {/* Table */}
      <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
        <table style={{ width: '100%', fontSize: '10px', borderCollapse: 'collapse', fontFamily: 'var(--brio-font-family)', tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '28px' }} />
            <col style={{ width: '90px' }} />
            <col style={{ width: '70px' }} />
            <col style={{ width: '80px' }} />
            <col style={{ width: '80px' }} />
            <col style={{ width: '60px' }} />
            <col />
          </colgroup>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--brio-border)', background: 'var(--brio-white, #fff)' }}>
              <th style={{ padding: '4px 3px', textAlign: 'center' }}>
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                  title="Select all"
                  style={{ accentColor: 'var(--brio-gold, #9F902A)' }}
                />
              </th>
              <th style={{ padding: '4px 6px', textAlign: 'left', fontWeight: 600 }}>Inv #</th>
              <th style={{ padding: '4px 4px', textAlign: 'center', fontWeight: 600 }}>Type</th>
              <th style={{ padding: '4px 4px', textAlign: 'center', fontWeight: 600 }}>Date</th>
              <th style={{ padding: '4px 4px', textAlign: 'right', fontWeight: 600 }}>Base</th>
              <th style={{ padding: '4px 4px', textAlign: 'right', fontWeight: 600 }}>Net</th>
              <th style={{ padding: '4px 3px', textAlign: 'center', fontWeight: 600 }}>Status</th>
              <th style={{ padding: '4px 6px', textAlign: 'left', fontWeight: 600 }}>Stage</th>
            </tr>
          </thead>
          <tbody>
            {unassigned.map(inv => {
              const badge = STATUS_BADGE[inv.status] || STATUS_BADGE.draft;
              const isBusy = busyIds.has(inv.id);
              return (
                <tr key={inv.id} style={{ borderBottom: '1px solid var(--brio-border)', background: 'var(--brio-white, #fff)', opacity: isBusy ? 0.6 : 1 }}
                  onMouseEnter={e => { if (!isBusy) e.currentTarget.style.background = 'var(--brio-grey-light)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'var(--brio-white, #fff)'; }}
                >
                  <td style={{ padding: '4px 3px', textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(inv.id)}
                      onChange={() => toggleSelect(inv.id)}
                      style={{ accentColor: 'var(--brio-gold, #9F902A)' }}
                    />
                  </td>
                  <td style={{ padding: '4px 6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <span>{inv.invoiceNumber || <span className="brio-text-grey">—</span>}</span>
                    {inv.source === 'tally_import' && (
                      <span style={{ marginLeft: '3px', fontSize: '7px', padding: '0px 3px', background: 'var(--brio-gold-light, #fef9e7)', border: '1px solid var(--brio-gold)', color: 'var(--brio-gold-dark, #7a6f1f)', borderRadius: '2px', fontWeight: 500 }}>
                        Tally
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '4px 4px', textAlign: 'center', fontSize: '8px', color: inv.invoiceDocType === 'proforma' ? 'var(--brio-gold)' : 'var(--brio-grey)' }}>
                    {inv.invoiceDocType === 'proforma' ? 'PI' : 'TI'}
                  </td>
                  <td style={{ padding: '4px 4px', textAlign: 'center' }}>{fmtDate(inv.invoiceDate)}</td>
                  <td style={{ padding: '4px 4px', textAlign: 'right' }}>{fmtCC(Number(inv.baseAmount) || 0)}</td>
                  <td style={{ padding: '4px 4px', textAlign: 'right', fontWeight: 500 }}>{fmtCC(Number(inv.netAmount) || 0)}</td>
                  <td style={{ padding: '4px 3px', textAlign: 'center' }}>
                    <span className={`brio-badge ${badge.className}`} style={{ fontSize: '8px', padding: '1px 5px' }}>{badge.label}</span>
                  </td>
                  <td style={{ padding: '4px 6px' }}>
                    <select
                      value={inv.stageKey || 'legacy_import'}
                      onChange={e => handleRowStageChange(inv.id, e.target.value)}
                      disabled={isBusy}
                      className="brio-input"
                      style={{ width: '100%', padding: '2px 4px', fontSize: '9px', border: '1px solid var(--brio-border)', fontFamily: 'var(--brio-font-family)', background: 'var(--brio-white, #fff)' }}
                    >
                      <option value="legacy_import">— Unassigned —</option>
                      {stageOptions.filter(o => o.value !== 'legacy_import').map(o =>
                        <option key={o.value} value={o.value}>{o.label}</option>
                      )}
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}



// ------------------------------------------------------------------
// FX DISPLAY HELPERS (pure functions)
// ------------------------------------------------------------------
// For NL invoices, invoice rows store original EUR amounts.
// The reporting view (v_invoices_reporting) exposes INR-converted amounts
// in *_inr_default fields (or may override base_amount directly).
export function getInvoiceBaseForINR(inv) {
  if (!inv) return 0;
  if (inv.currency === 'EUR' || inv.officeLocation === 'NL') {
    if (inv.baseAmountInrDefault != null) return Number(inv.baseAmountInrDefault) || 0;
  }
  return Number(inv.baseAmount) || 0;
}

export function getInvoiceGrossForINR(inv) {
  if (!inv) return 0;
  if (inv.currency === 'EUR' || inv.officeLocation === 'NL') {
    if (inv.grossAmountInrDefault != null) return Number(inv.grossAmountInrDefault) || 0;
  }
  return Number(inv.grossAmount) || 0;
}

export function getInvoiceNetForINR(inv) {
  if (!inv) return 0;
  if (inv.currency === 'EUR' || inv.officeLocation === 'NL') {
    if (inv.netAmountInrDefault != null) return Number(inv.netAmountInrDefault) || 0;
  }
  return Number(inv.netAmount) || 0;
}


// ============================================================================
// SUB-COMPONENT: AccountingVerificationPanel
// ============================================================================
// NOTE: MonthlyArchivePanel has been moved to RadarDashboardPage (company-wide)
// ============================================================================

// Placeholder — the MonthlyArchivePanel component has been relocated.
// See src/pages/radar/RadarDashboardPage.jsx for the current implementation.

// ============================================================================
// Compact panel that checks for common billing/invoicing mismatches.
// Uses already-loaded data — no new queries.
// ============================================================================
function AccountingVerificationPanel({ project, invoices, combinedStages, fmtCurr, formatMoney, feeRevisions }) {
  const fmtCC = (a) => formatMoney ? formatMoney(a, project?.contractCurrency || 'INR') : fmtCurr(a);
  const checks = React.useMemo(() => {
    if (!project) return [];
    const results = [];
    const baseDesignFee = Number(project.budget) || 0;
    const ccMonthly = Number(project.retainerConfig?.monthlyAmount) || 0;
    const ccMonths = Number(project.retainerConfig?.totalMonths) || 0;
    const ccTotal = ccMonthly * ccMonths;
    const miscTotal = (Array.isArray(project.miscFeeScopes) ? project.miscFeeScopes : [])
      .reduce((sum, s) => sum + (Number(s.amount) || 0), 0);
    const totalDesignFee = baseDesignFee + ccTotal + miscTotal;
    const timeline = project.stageTimeline || {};
    const invs = invoices || [];

    // ── Check 1: Design fee allocation ──
    // Sum planned amounts for base stages (non-part, non-CC, non-misc)
    let baseStagePlanned = 0;
    let miscPlanned = 0;
    let ccPlanned = 0;
    combinedStages.forEach(stage => {
      const billing = timeline[stage.key]?.billing || {};
      if (stage.isCCMonthly) {
        const ls = Number(billing.lumpsum) || 0;
        ccPlanned += ls > 0 ? ls : (stage.retainerMonthlyAmount || 0);
      } else if (stage.isMiscScope) {
        const miscHasSubstages = combinedStages.some(s => s.isPartStage && s.parentKey === stage.key);
        if (miscHasSubstages) {
          combinedStages.filter(s => s.isPartStage && s.parentKey === stage.key).forEach(sub => {
            const parentParts = timeline[sub.parentKey]?.partStages || [];
            const part = parentParts.find(p => p.id === sub.partId);
            const subBilling = part?.billing || {};
            const subLs = Number(subBilling.lumpsum) || 0;
            if (subLs > 0) { miscPlanned += subLs; return; }
            const pctOfStage = Number(subBilling.percentOfStage) || 0;
            const parentBilling = timeline[sub.parentKey]?.billing || {};
            const parentLs = Number(parentBilling.lumpsum) || 0;
            const parentPlanned = parentLs > 0 ? parentLs : (stage.miscScopeAmount || 0);
            miscPlanned += parentPlanned * pctOfStage / 100;
          });
        } else {
          const ls = Number(billing.lumpsum) || 0;
          miscPlanned += ls > 0 ? ls : (stage.miscScopeAmount || 0);
        }
      } else if (stage.isPartStage) {
        // Part stages counted via parent — skip to avoid double-counting
      } else if (stage.key === 'cc') {
        // CC parent — skip (monthly rows carry the amounts)
      } else {
        // Check if has substages — if so, sum substage planned instead
        const hasSubstages = combinedStages.some(s => s.isPartStage && s.parentKey === stage.key);
        if (hasSubstages) {
          // Sum substage planned
          combinedStages.filter(s => s.isPartStage && s.parentKey === stage.key).forEach(sub => {
            const parentParts = timeline[sub.parentKey]?.partStages || [];
            const part = parentParts.find(p => p.id === sub.partId);
            const subBilling = part?.billing || {};
            const ls = Number(subBilling.lumpsum) || 0;
            if (ls > 0) { baseStagePlanned += ls; return; }
            const pctOfStage = Number(subBilling.percentOfStage) || 0;
            const parentBilling = timeline[sub.parentKey]?.billing || {};
            const parentLs = Number(parentBilling.lumpsum) || 0;
            const parentPlanned = parentLs > 0 ? parentLs : (Number(parentBilling.percent) || 0) * baseDesignFee / 100;
            baseStagePlanned += parentPlanned * pctOfStage / 100;
          });
        } else {
          const pct = Number(billing.percent) || 0;
          const ls = Number(billing.lumpsum) || 0;
          baseStagePlanned += ls > 0 ? ls : (pct * baseDesignFee / 100);
        }
      }
    });
    const allPlanned = baseStagePlanned + miscPlanned + ccPlanned;
    const allocDelta = allPlanned - totalDesignFee;
    results.push({
      name: 'Design fee allocation',
      ok: Math.abs(allocDelta) < 1,
      value: `Planned ${fmtCC(allPlanned)} vs Total ${fmtCC(totalDesignFee)}`,
      delta: allocDelta,
      suggestion: allocDelta > 0 ? 'Planned exceeds total fee — check stage %' : 'Planned is less than total fee — some fee is unallocated',
    });

    // ── Check 2: Invoiced vs planned ──
    const invoicedByStage = {};
    invs.forEach(inv => {
      if (!inv.stageKey || inv.stageKey === 'legacy_import') return;
      if (!invoicedByStage[inv.stageKey]) invoicedByStage[inv.stageKey] = 0;
      invoicedByStage[inv.stageKey] += Number(inv.baseAmount) || 0;
    });
    const totalInvoiced = invs.reduce((s, i) => s + (Number(i.baseAmount) || 0), 0);
    const invoicedDelta = totalInvoiced - allPlanned;
    results.push({
      name: 'Invoiced vs planned',
      ok: Math.abs(invoicedDelta) < 1,
      value: `Invoiced ${fmtCC(totalInvoiced)} vs Planned ${fmtCC(allPlanned)}`,
      delta: invoicedDelta,
      suggestion: invoicedDelta > 0 ? 'Invoiced exceeds planned — some invoices may be over-billed' : 'Not all planned amounts have been invoiced',
    });

    // ── Check 3: Paid vs invoiced ──
    const totalPaid = invs
      .filter(i => i.status === 'paid')
      .reduce((s, i) => s + (Number(i.netAmount) || 0), 0);
    const totalIssuedNet = invs
      .filter(i => i.status === 'issued' || i.status === 'paid')
      .reduce((s, i) => s + (Number(i.netAmount) || 0), 0);
    const paidExceeds = totalPaid > totalIssuedNet + 0.01;
    results.push({
      name: 'Paid vs invoiced',
      ok: !paidExceeds,
      value: `Paid ${fmtCC(totalPaid)} / Invoiced (net) ${fmtCC(totalIssuedNet)}`,
      delta: paidExceeds ? totalPaid - totalIssuedNet : 0,
      suggestion: 'Paid total exceeds invoiced total — check payment confirmations',
    });

    // ── Check 4: Unallocated invoices ──
    const unallocated = invs.filter(i =>
      !i.stageKey || i.stageKey === 'legacy_import' || !i.projectId || !i.officeLocation
    );
    results.push({
      name: 'Unallocated invoices',
      ok: unallocated.length === 0,
      value: unallocated.length === 0 ? 'All invoices have stage + project + office' : `${unallocated.length} invoice(s) missing data`,
      delta: unallocated.length,
      suggestion: 'Assign stage_key, project_id, or office_location to flagged invoices',
    });

    return results;
  }, [project, invoices, combinedStages, fmtCurr]);

  if (!project || checks.length === 0) return null;

  return (
    <div style={{ border: '1px solid var(--brio-border)', padding: '12px 16px', marginTop: '12px' }}>
      <h4 className="brio-font-bold brio-mb-xs" style={{ fontSize: '12px' }}>Accounting Verification</h4>
      <table style={{ width: '100%', fontSize: '10px', borderCollapse: 'collapse', fontFamily: 'var(--brio-font-family)' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--brio-border)' }}>
            <th style={{ padding: '4px 6px', textAlign: 'left', fontWeight: 600 }}>Check</th>
            <th style={{ padding: '4px 6px', textAlign: 'center', fontWeight: 600, width: '50px' }}>Status</th>
            <th style={{ padding: '4px 6px', textAlign: 'left', fontWeight: 600 }}>Value / Delta</th>
          </tr>
        </thead>
        <tbody>
          {checks.map(c => (
            <tr key={c.name} style={{ borderBottom: '1px solid var(--brio-border)' }}>
              <td style={{ padding: '4px 6px' }}>{c.name}</td>
              <td style={{ padding: '4px 6px', textAlign: 'center' }}>
                <span style={{
                  display: 'inline-block', padding: '1px 6px', fontSize: '9px', fontWeight: 600,
                  borderRadius: '3px',
                  background: c.ok ? 'var(--brio-green-light, #dcfce7)' : '#fef2f2',
                  color: c.ok ? 'var(--brio-green, #16a34a)' : '#dc2626',
                }}>
                  {c.ok ? 'OK' : 'WARN'}
                </span>
              </td>
              <td style={{ padding: '4px 6px' }}>
                <span>{c.value}</span>
                {!c.ok && c.delta !== 0 && (
                  <span style={{ marginLeft: '6px', fontSize: '9px', color: '#dc2626' }}>
                    (delta: {typeof c.delta === 'number' && c.delta > 1000 ? fmtCC(c.delta) : c.delta})
                  </span>
                )}
                {!c.ok && <div style={{ fontSize: '8px', color: 'var(--brio-grey)', marginTop: '1px' }}>{c.suggestion}</div>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* ── Fee Revision History ── */}
      {feeRevisions && feeRevisions.length > 0 && (
        <div style={{ marginTop: '10px', borderTop: '1px solid var(--brio-border)', paddingTop: '8px' }}>
          <h5 style={{ fontSize: '11px', fontWeight: 600, marginBottom: '4px' }}>Fee Revision History</h5>
          <table style={{ width: '100%', fontSize: '10px', borderCollapse: 'collapse', fontFamily: 'var(--brio-font-family)' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--brio-border)' }}>
                <th style={{ padding: '3px 6px', textAlign: 'left', fontWeight: 600 }}>Date</th>
                <th style={{ padding: '3px 6px', textAlign: 'right', fontWeight: 600 }}>Old Fee</th>
                <th style={{ padding: '3px 6px', textAlign: 'right', fontWeight: 600 }}>New Fee</th>
                <th style={{ padding: '3px 6px', textAlign: 'right', fontWeight: 600 }}>Delta</th>
                <th style={{ padding: '3px 6px', textAlign: 'left', fontWeight: 600 }}>Reason</th>
              </tr>
            </thead>
            <tbody>
              {feeRevisions.map(rev => (
                <tr key={rev.id} style={{ borderBottom: '1px solid var(--brio-border)' }}>
                  <td style={{ padding: '3px 6px' }}>{rev.effectiveDate || '—'}</td>
                  <td style={{ padding: '3px 6px', textAlign: 'right' }}>{fmtCC(rev.oldTotalFee)}</td>
                  <td style={{ padding: '3px 6px', textAlign: 'right' }}>{fmtCC(rev.newTotalFee)}</td>
                  <td style={{ padding: '3px 6px', textAlign: 'right', color: rev.deltaFee > 0 ? 'var(--brio-green, #16a34a)' : rev.deltaFee < 0 ? '#dc2626' : 'inherit' }}>
                    {rev.deltaFee > 0 ? '+' : ''}{fmtCC(rev.deltaFee)}
                  </td>
                  <td style={{ padding: '3px 6px', fontSize: '9px', color: 'var(--brio-grey)' }}>{rev.reason || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================
export default function InvoicesPage({
  currentUser, activeClients, activeProjects, activeTasks, projects,
  selectedTimelineProject, getSupervisedProjects, getSortedProjects,
  fmtProject, fmtCurr, useSupabaseDB, supabaseProjects, invoicesHook,
  feeRevisionsHook, settingsHook,
  setSelectedTimelineProject,
  setProjects,
}) {
  const tableProps = getTablePropsFromCode(PRESENTATION_CODE);

  const isAdmin      = currentUser.role === 'admin' || currentUser.role === 'admin-pa';
  const isSupervisor = currentUser.role === 'supervisor';
  const canCreate    = isAdmin || isSupervisor;
  const canDelete    = isAdmin;
  const canConfirm   = isAdmin || isSupervisor;

  // Communication log for email draft tracking
  const { logEmail, fetchLogsForProject } = useCommunicationLog();
  const [showCommLog, setShowCommLog] = useState(false);

  // Fee revision panel state
  const [showFeeRevisionPanel, setShowFeeRevisionPanel] = useState(false);

  const [editorInvoice, setEditorInvoice] = useState(null);
  const [showEditor, setShowEditor]       = useState(false);
  const [paymentInvoice, setPaymentInvoice] = useState(null);
  const [knownAccountNames, setKnownAccountNames] = useState([]);
  // C3: Recalculation preview state
  const [recalcPreviewInvoice, setRecalcPreviewInvoice] = useState(null);
  const [pendingRevision, setPendingRevision] = useState(null);
  // C3: "Create with revision" flow — stores intent while RecalcPreview is open
  const [pendingCreateParams, setPendingCreateParams] = useState(null); // { stageKey, invoiceDate }
  // PI number reassignment modal: { invoiceId, inv, snapshotData, onHoldPIs }
  const [piReassignState, setPiReassignState] = useState(null);
  // Selected donor PI id within the reassign modal (null = "next sequential")
  const [piReassignDonorId, setPiReassignDonorId] = useState(null);
  // PDF Preview modal
  const [pdfPreviewInv, setPdfPreviewInv] = useState(null);
  // Reminder picker modal
  const [reminderInvoice, setReminderInvoice] = useState(null);
  // Email draft modal state: { invoice, templateKey }
  const [emailDraftInvoice, setEmailDraftInvoice] = useState(null);

  const DEFAULT_ID_TO_KEY = {
    1: 'advance', 2: 'cd_ad', 3: 'cd_id', 4: 'sd_ad', 5: 'sd_id',
    6: 'dd_ad', 7: 'dd_id', 8: 'aoc_ad', 9: 'aoc_id', 10: 'td_ad', 11: 'td_id',
    12: 'gfc1', 13: 'gfc2', 14: 'gfc3', 15: 'completion', 16: 'cc',
  };

  const sortedStages = useMemo(() => {
    if (!activeTasks || activeTasks.length === 0) {
      return Object.entries(DEFAULT_ID_TO_KEY).map(([id, key]) => ({ key, name: key.replace(/_/g, ' ').toUpperCase(), id: Number(id) }));
    }
    return [...activeTasks]
      .sort((a, b) => {
        const oa = a.order != null ? a.order : (a.id <= 16 ? a.id : 1000 + a.id);
        const ob = b.order != null ? b.order : (b.id <= 16 ? b.id : 1000 + b.id);
        return oa - ob;
      })
      .map(t => ({ key: DEFAULT_ID_TO_KEY[t.id] || `custom_${t.id}`, name: t.name, id: t.id }));
  }, [activeTasks]);

  const availableProjects = useMemo(() => {
    return currentUser.role === 'supervisor' ? getSupervisedProjects() : getSortedProjects();
  }, [currentUser.role, getSupervisedProjects, getSortedProjects]);

  // Lookup map for the used-invoice-numbers list (id → project name). Spans all
  // projects so the list can label invoices belonging to other projects too.
  const projectNameLookup = useMemo(() => {
    const map = new Map();
    const source = Array.isArray(projects) ? projects : availableProjects;
    for (const p of source) {
      if (p?.id != null) map.set(Number(p.id), p.name || '');
    }
    return map;
  }, [projects, availableProjects]);

  const currentProjectId = selectedTimelineProject || (availableProjects[0]?.id ?? null);
  const project = availableProjects.find(p => Number(p.id) === Number(currentProjectId));
  // Format in the selected project's contract currency (no FX conversion)
  const fmtCC = (a) => settingsHook?.formatMoney ? settingsHook.formatMoney(a, project?.contractCurrency || 'INR') : fmtCurr(a);

  // Recommended actions hook — for triggering workflows on payment confirmation
  const recActions = useRecommendedActions(currentProjectId);

  // ── Helper: determine if a stage is allocated (has fee allocation) ──
  const isStageAllocated = useCallback((stageKey) => {
    if (!project) return false;
    const timeline = project.stageTimeline?.[stageKey] || {};
    const billing = timeline.billing || {};
    const allocPct = Number(project.budgetAllocation?.[stageKey]) || 0;
    const invPct = timeline.invoicePercent;
    return (invPct > 0) || (Number(billing.percent) > 0) || (Number(billing.lumpsum) > 0) || (allocPct > 0);
  }, [project]);

  // ── Helper: find the next allocated stage after the given stage key ──
  const getNextAllocatedStage = useCallback((currentStageKey) => {
    if (!sortedStages || sortedStages.length === 0) return null;
    const currentIdx = sortedStages.findIndex(s => s.key === currentStageKey);
    if (currentIdx < 0) return null;
    for (let i = currentIdx + 1; i < sortedStages.length; i++) {
      if (isStageAllocated(sortedStages[i].key)) {
        return sortedStages[i];
      }
    }
    return null; // No allocated stage found after current
  }, [sortedStages, isStageAllocated]);

  // ── Combined stages: standard stages + part stages from current project ──
  // Used for stage selector dropdown, intent alerts, and stageNameOf lookup.
  // Part stage keys use format: ${parentKey}__part__${partId}
  const combinedStages = useMemo(() => {
    const timeline = project?.stageTimeline || {};
    const result = [];
    sortedStages.forEach(stage => {
      result.push(stage);
      // Add part stages as children (if any)
      const partStages = timeline[stage.key]?.partStages || [];
      [...partStages].sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { numeric: true, sensitivity: 'base' })).forEach(part => {
        result.push({
          key: `${stage.key}__part__${part.id}`,
          name: part.name || `${stage.name} — Part`,
          id: `${stage.id}_part_${part.id}`,
          isPartStage: true,
          parentKey: stage.key,
          partId: part.id,
        });
      });

      // CC monthly rows: generate under the CC stage based on CC lifecycle start date
      if (stage.key === 'cc' && project) {
        const retainer = project.retainerConfig || {};
        const monthlyAmt = Number(retainer.monthlyAmount || retainer.retainerMonthlyAmount || project.retainerMonthlyAmount) || 0;
        const totalMonthsPlanned = Number(retainer.totalMonths || retainer.retainerTotalMonths || project.retainerTotalMonths) || 12;
        // CC lifecycle source of truth: stageTimeline.cc (set via TimelinePage controls)
        const ccTl = project.stageTimeline?.cc || {};
        const ccStartDate = ccTl.startDate || null;
        const ccStopDate = ccTl.stopDate || ccTl.stoppedAt || null;

        if (monthlyAmt > 0 && ccStartDate) {
          // Parse start month (normalize to first-of-month)
          const startD = new Date(ccStartDate + (ccStartDate.length <= 7 ? '-01' : ''));
          const startYear = startD.getFullYear();
          const startMonth = startD.getMonth(); // 0-indexed

          // Planned end month = start + totalMonthsPlanned - 1
          const plannedEndD = new Date(startYear, startMonth + totalMonthsPlanned - 1, 1);

          // Current month (first-of-month)
          const now = new Date();
          const currentD = new Date(now.getFullYear(), now.getMonth(), 1);

          // endMonth = max(plannedEnd, currentMonth)
          let endD = plannedEndD > currentD ? plannedEndD : currentD;

          // If stopped, clamp: endMonth = min(endMonth, stopMonth)
          if (ccStopDate) {
            const stopD = new Date(ccStopDate + (ccStopDate.length <= 7 ? '-01' : ''));
            const stopMonthD = new Date(stopD.getFullYear(), stopD.getMonth(), 1);
            if (stopMonthD < endD) endD = stopMonthD;
          }

          // Generate months from startMonth to endD (inclusive)
          const cursor = new Date(startYear, startMonth, 1);
          let guard = 0;
          while (cursor <= endD && guard < 240) {
            const yyyy = cursor.getFullYear();
            const mm = String(cursor.getMonth() + 1).padStart(2, '0');
            const monthKey = `${yyyy}-${mm}`;
            const monthLabel = cursor.toLocaleString('en', { month: 'short', year: 'numeric' });
            result.push({
              key: `cc__${monthKey}`,
              name: `CC — ${monthLabel}`,
              id: `cc_monthly_${monthKey}`,
              isPartStage: true, // treated as child row for indent/muting
              isCCMonthly: true,
              ccMonth: monthKey,
              parentKey: 'cc',
              partId: monthKey,
              retainerMonthlyAmount: monthlyAmt,
            });
            cursor.setMonth(cursor.getMonth() + 1);
            guard++;
          }
        }
      }
    });

    // ── Misc fee scopes: add as top-level entries after all standard stages ──
    const miscScopes = Array.isArray(project?.miscFeeScopes) ? project.miscFeeScopes : [];
    miscScopes.forEach(scope => {
      if (!scope.id || !scope.name) return;
      result.push({
        key: `misc__${scope.id}`,
        name: scope.name,
        id: `misc_${scope.id}`,
        isMiscScope: true,
        miscScopeId: scope.id,
        miscScopeAmount: Number(scope.amount) || 0,
      });
      // Add part stages (substages) under this misc scope if they exist in stageTimeline
      const miscTl = timeline[`misc__${scope.id}`] || {};
      const miscParts = miscTl.partStages || [];
      [...miscParts].sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { numeric: true, sensitivity: 'base' })).forEach(part => {
        result.push({
          key: `misc__${scope.id}__part__${part.id}`,
          name: part.name || `${scope.name} — Part`,
          id: `misc_${scope.id}_part_${part.id}`,
          isPartStage: true,
          parentKey: `misc__${scope.id}`,
          partId: part.id,
          isMiscScopePart: true,
        });
      });
    });

    return result;
  }, [sortedStages, project]);

  useEffect(() => {
    if (currentProjectId && invoicesHook) invoicesHook.fetchInvoices(currentProjectId);
  }, [currentProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Step 1: Fetch all payment events + all invoices on mount (for payment inbox linking)
  useEffect(() => {
    if (invoicesHook?.fetchPaymentEvents) {
      invoicesHook.fetchPaymentEvents(); // fetch all (no project filter)
    }
    if (invoicesHook?.fetchAllInvoices) {
      invoicesHook.fetchAllInvoices(); // fetch all invoices for link dropdown
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Step 2: State for the detailed confirm dialog
  const [matchConfirmTarget, setMatchConfirmTarget] = useState(null); // { paymentEvent, invoice }

  // Feature 2: Fetch learned account names on project change
  useEffect(() => {
    if (currentProjectId && invoicesHook?.fetchProjectAccountNames) {
      invoicesHook.fetchProjectAccountNames(currentProjectId).then(result => {
        if (result.success) setKnownAccountNames(result.data.map(r => r.accountName));
      });
    }
  }, [currentProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Listen for cross-page invoice create intent from TimelinePage ──
  useEffect(() => {
    const handler = (e) => {
      const { projectId, stageKey, invoiceDate } = e.detail || {};
      if (!projectId || !stageKey) return;
      console.log('[InvoicesPage] received brio:invoiceCreateIntent', { projectId, stageKey, invoiceDate });
      // Switch to the correct project if needed
      if (Number(projectId) !== Number(currentProjectId)) {
        setSelectedTimelineProject(Number(projectId));
      }
      // Delay slightly to allow project switch to propagate
      setTimeout(() => {
        openCreate(stageKey, invoiceDate);
      }, 100);
    };
    window.addEventListener('brio:invoiceCreateIntent', handler);
    return () => window.removeEventListener('brio:invoiceCreateIntent', handler);
  }, [currentProjectId, setSelectedTimelineProject]); // eslint-disable-line react-hooks/exhaustive-deps

  // Feature 3: Fetch fee revisions on project change
  useEffect(() => {
    if (currentProjectId && feeRevisionsHook?.fetchRevisions) {
      console.log('[FeeRevisions] fetching revisions for project', currentProjectId);
      feeRevisionsHook.fetchRevisions(currentProjectId);
    } else {
      console.log('[FeeRevisions] SKIP fetch —', {
        hasProjectId: !!currentProjectId,
        hasHook: !!feeRevisionsHook,
        hasFetchMethod: !!feeRevisionsHook?.fetchRevisions,
      });
    }
  }, [currentProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const intentAlerts = useMemo(() => {
    if (!project?.stageTimeline || !invoicesHook) return [];
    const alerts = [];
    // Check full stages (including CC) and part stages
    combinedStages.forEach(stage => {
      let invoiceDate = null;
      if (stage.isPartStage) {
        // Part stage: read invoiceDate from parent's partStages array
        const parentParts = project.stageTimeline[stage.parentKey]?.partStages || [];
        const part = parentParts.find(p => p.id === stage.partId);
        invoiceDate = part?.invoiceDate;
      } else {
        // Full stage (including CC)
        const tl = project.stageTimeline[stage.key];
        invoiceDate = tl?.invoiceDate;
      }
      if (!invoiceDate) return;
      const hasInvoice = invoicesHook.invoices.some(inv => inv.stageKey === stage.key && inv.status !== 'draft');
      if (!hasInvoice) alerts.push({ stageKey: stage.key, stageName: stage.name, intentDate: invoiceDate });
    });
    return alerts;
  }, [project, invoicesHook?.invoices, combinedStages]);

  const summary = useMemo(() => {
    const invs = invoicesHook?.invoices || [];
    const unassignedCount = invs.filter(i => i.source === 'tally_import' && (!i.stageKey || i.stageKey === 'legacy_import')).length;
    return {
      total: invs.length, draft: invs.filter(i => i.status === 'draft').length,
      issued: invs.filter(i => i.status === 'issued').length, paid: invs.filter(i => i.status === 'paid').length,
      totalNet: invs.reduce((sum, i) => sum + (i.netAmount || 0), 0),
      paidNet: invs.filter(i => i.status === 'paid').reduce((sum, i) => sum + (i.netAmount || 0), 0),
      unassigned: unassignedCount,
    };
  }, [invoicesHook?.invoices]);

  const stageNameOf = useCallback((key) => {
    if (!key) return '—';
    const s = combinedStages.find(st => st.key === key);
    return s ? s.name : key;
  }, [combinedStages]);

  const handleSave = useCallback(async (data, existingId) => {
    if (!invoicesHook) return { success: false, error: 'Invoice system not available.' };
    const result = existingId
      ? await invoicesHook.updateInvoice(existingId, data)
      : await invoicesHook.createInvoice(data);

    // Sync the actual invoice date back to the stage timeline so the planned
    // date reflects reality once an invoice with a date exists.
    if (result.success && data.invoiceDate && data.stageKey && currentProjectId) {
      try {
        const partIdx = data.stageKey.indexOf('__part__');
        let partial;
        if (partIdx === -1) {
          // Regular stage: spread existing entry so other fields (targetDate,
          // billing, paidDate, etc.) are preserved — the RPC only shallow-merges
          // at the top level, so we must carry existing keys ourselves.
          const currentStage = project?.stageTimeline?.[data.stageKey] || {};
          partial = { [data.stageKey]: { ...currentStage, invoiceDate: data.invoiceDate } };
        } else {
          // Part-stage: must read-modify-write the parent key to avoid clobbering siblings.
          const parentKey = data.stageKey.slice(0, partIdx);
          const partId = data.stageKey.slice(partIdx + '__part__'.length);
          const currentParent = project?.stageTimeline?.[parentKey] || {};
          const partStages = (currentParent.partStages || []).map(ps =>
            String(ps.id) === String(partId) ? { ...ps, invoiceDate: data.invoiceDate } : ps
          );
          partial = { [parentKey]: { ...currentParent, partStages } };
        }
        await supabase.rpc('merge_project_stage_timeline', {
          p_project_id: currentProjectId,
          p_partial_timeline: partial,
        });
      } catch (e) {
        console.warn('[InvoicesPage] timeline invoiceDate sync failed (non-blocking):', e);
      }
    }

    return result;
  }, [invoicesHook, currentProjectId, project]);

  const handleIssue = useCallback(async (invoiceId) => {
    if (!invoicesHook) return { success: false, error: 'Invoice system not available.' };

    const inv = invoicesHook.invoices.find(i => i.id === invoiceId);

    // ── If this draft already has fee-revision data baked in (from create flow),
    //    skip the recalc preview — it was already shown at creation time. ──
    const alreadyHasRecalc = inv?.feeRevisionId || inv?.recalcBreakdownJson;

    if (!alreadyHasRecalc && inv && inv.status === 'draft' && inv.stageKey && feeRevisionsHook) {
      const latestRevision = feeRevisionsHook.getLatestRevision?.();
      console.log('[IssueGate]', {
        hasRevision: !!latestRevision,
        revisionDetail: latestRevision ? { id: latestRevision.id, oldFee: latestRevision.oldTotalFee, newFee: latestRevision.newTotalFee } : null,
        status: inv.status,
        stageKey: inv.stageKey,
        allRevisions: feeRevisionsHook.revisions?.length ?? 'N/A',
      });
      if (latestRevision) {
        setRecalcPreviewInvoice(inv);
        setPendingRevision(latestRevision);
        return { success: true }; // Don't issue yet — modal will handle it
      }
    } else if (alreadyHasRecalc) {
      console.log('[IssueGate] SKIP — revision data already baked into draft', {
        feeRevisionId: inv.feeRevisionId,
        hasBreakdown: !!inv.recalcBreakdownJson,
      });
    } else {
      console.log('[IssueGate] SKIP revision check —', {
        hasInv: !!inv,
        status: inv?.status,
        stageKey: inv?.stageKey,
        hasHook: !!feeRevisionsHook,
      });
    }

    // Issue with snapshot — for pre-baked drafts the snapshot fields are
    // already stored on the row, but we still pass basic snapshot for
    // invoices that were created without the recalc flow.
    const snapshotData = {};
    if (inv && project && !alreadyHasRecalc) {
      snapshotData.totalFeeSnapshot = getCurrentTotalFee(project);
      if (inv.stageKey) {
        const invPct = project.stageTimeline?.[inv.stageKey]?.invoicePercent;
        const allocPct = Number(project.budgetAllocation?.[inv.stageKey]) || 0;
        snapshotData.invoicePercentSnapshot = invPct != null ? Number(invPct) : allocPct;
      }
      snapshotData.baseAmountPlanned = inv.baseAmount;
      snapshotData.adjustmentAmount = 0;
      snapshotData.basisMode = inv.isManualOverride ? 'lumpsum' : 'percent';
    }

    // PI number reassignment: if this is a proforma invoice, check for same-month
    // draft PIs (on-hold) whose reserved numbers could be reused.
    if (inv && inv.invoiceDocType === 'proforma' && inv.invoiceDate) {
      const monthPrefix = inv.invoiceDate.slice(0, 7).replace('-', '');
      const onHoldPIs = (invoicesHook.allInvoices || []).filter(i =>
        i.id !== inv.id &&
        i.status === 'draft' &&
        i.invoiceDocType === 'proforma' &&
        String(i.invoiceNumber || '').length === 8 &&
        String(i.invoiceNumber || '').startsWith(monthPrefix) &&
        !i.deletedAt
      );
      if (onHoldPIs.length > 0) {
        setPiReassignDonorId(null);
        setPiReassignState({ invoiceId, inv, snapshotData, onHoldPIs });
        return { success: true };
      }
    }

    return invoicesHook.issueInvoice(invoiceId, snapshotData);
  }, [invoicesHook, feeRevisionsHook, project]);

  // PI reassign: user confirmed — optionally clear donor number then issue
  const handlePiReassignConfirm = useCallback(async (chosenNumber, donorId) => {
    if (!piReassignState || !invoicesHook) return;
    const { invoiceId, snapshotData } = piReassignState;
    setPiReassignState(null);
    if (donorId) {
      await invoicesHook.updateInvoice(donorId, { invoiceNumber: '', isManualOverride: false });
    }
    const resolvedSnapshot = chosenNumber
      ? { ...snapshotData, resolvedInvoiceNumber: chosenNumber }
      : snapshotData;
    return invoicesHook.issueInvoice(invoiceId, resolvedSnapshot);
  }, [piReassignState, invoicesHook]);

  // C3: Handle issue from recalculation preview (roll-in approach)
  // This path only triggers for legacy drafts without pre-baked recalc data
  const handleIssueWithRecalc = useCallback(async (adjustmentAmount, explanationText, revision) => {
    if (!invoicesHook || !recalcPreviewInvoice) return;
    const inv = recalcPreviewInvoice;
    const adj = Number(adjustmentAmount) || 0;

    const newBase = (Number(inv.baseAmount) || 0) + adj;
    // Update draft with adjusted base first
    if (adj !== 0) {
      const updateResult = await invoicesHook.updateInvoice(inv.id, {
        baseAmount: newBase,
        basisMode: 'percent_plus_adjustment',
      });
      if (!updateResult.success) {
        alert('Failed to update invoice with adjustment: ' + (updateResult.error || ''));
        return;
      }
    }

    const totalFee = getCurrentTotalFee(project);
    const issuedInvoices = invoicesHook.getIssuedInvoices?.() || [];
    const breakdown = computeRecalcBreakdown(issuedInvoices, totalFee, project, inv.stageKey);

    const snapshotData = {
      totalFeeSnapshot: totalFee,
      invoicePercentSnapshot: inv.stageKey
        ? (project?.stageTimeline?.[inv.stageKey]?.invoicePercent ?? Number(project?.budgetAllocation?.[inv.stageKey]) ?? 0)
        : 0,
      baseAmountPlanned: Number(inv.baseAmount) || 0,
      adjustmentAmount: adj,
      basisMode: adj !== 0 ? 'percent_plus_adjustment' : (inv.isManualOverride ? 'lumpsum' : 'percent'),
      feeRevisionId: revision?.id || null,
      recalcBreakdownJson: {
        issuedLines: breakdown.issuedLines.map(l => ({
          invoiceNumber: l.invoiceNumber, stageKey: l.stageKey, basisMode: l.basisMode,
          invoicePercentSnapshot: l.invoicePercentSnapshot, issuedBase: l.issuedBase,
          plannedAtNewFee: l.plannedAtNewFee, delta: l.delta, estimated: l.estimated,
        })),
        issuedTotalBase: breakdown.issuedTotalBase,
        issuedTotalAtNewFee: breakdown.issuedTotalAtNewFee,
        adjustmentDue: breakdown.adjustmentDue,
        currentStagePlanned: breakdown.currentStagePlanned,
        currentStagePercent: breakdown.currentStagePercent,
        newFee: totalFee,
        approach: 'roll_in',
      },
      recalcExplanationText: explanationText,
    };

    const result = await invoicesHook.issueInvoice(inv.id, snapshotData);
    if (result.success) {
      setRecalcPreviewInvoice(null);
      setPendingRevision(null);
      setShowEditor(false);
      setEditorInvoice(null);
    } else {
      alert('Failed to issue: ' + (result.error || ''));
    }
  }, [invoicesHook, recalcPreviewInvoice, project]);

  // C3: Handle issue with separate adjustment invoice (legacy path)
  const handleIssueWithSeparateAdjustment = useCallback(async (adjustmentAmount, explanationText, revision) => {
    if (!invoicesHook || !recalcPreviewInvoice) return;
    const inv = recalcPreviewInvoice;
    const adj = Number(adjustmentAmount) || 0;

    const totalFee = getCurrentTotalFee(project);
    const issuedInvoices = invoicesHook.getIssuedInvoices?.() || [];
    const breakdown = computeRecalcBreakdown(issuedInvoices, totalFee, project, inv.stageKey);

    const sharedBreakdown = {
      issuedLines: breakdown.issuedLines.map(l => ({
        invoiceNumber: l.invoiceNumber, stageKey: l.stageKey, basisMode: l.basisMode,
        invoicePercentSnapshot: l.invoicePercentSnapshot, issuedBase: l.issuedBase,
        plannedAtNewFee: l.plannedAtNewFee, delta: l.delta, estimated: l.estimated,
      })),
      issuedTotalBase: breakdown.issuedTotalBase,
      issuedTotalAtNewFee: breakdown.issuedTotalAtNewFee,
      adjustmentDue: breakdown.adjustmentDue,
      currentStagePlanned: breakdown.currentStagePlanned,
      currentStagePercent: breakdown.currentStagePercent,
      newFee: totalFee,
      approach: 'separate',
    };

    // Issue the original invoice as-is (no adjustment)
    const snapshotData = {
      totalFeeSnapshot: totalFee,
      invoicePercentSnapshot: inv.stageKey
        ? (project?.stageTimeline?.[inv.stageKey]?.invoicePercent ?? Number(project?.budgetAllocation?.[inv.stageKey]) ?? 0)
        : 0,
      baseAmountPlanned: Number(inv.baseAmount) || 0,
      adjustmentAmount: 0,
      basisMode: inv.isManualOverride ? 'lumpsum' : 'percent',
      feeRevisionId: revision?.id || null,
      recalcBreakdownJson: sharedBreakdown,
      recalcExplanationText: explanationText,
    };

    const issueResult = await invoicesHook.issueInvoice(inv.id, snapshotData);
    if (!issueResult.success) {
      alert('Failed to issue: ' + (issueResult.error || ''));
      return;
    }

    // Create a separate adjustment invoice (as draft)
    if (adj !== 0) {
      const adjResult = await invoicesHook.createInvoice({
        projectId: inv.projectId,
        stageKey: null,
        invoiceDate: new Date().toISOString().slice(0, 10),
        baseAmount: Math.abs(adj),
        gstPercent: inv.gstPercent,
        tdsPercent: inv.tdsPercent,
        basisMode: 'adjustment',
        feeRevisionId: revision?.id || null,
        totalFeeSnapshot: totalFee,
        recalcBreakdownJson: sharedBreakdown,
        recalcExplanationText: explanationText,
        note: `Fee revision adjustment (${adj >= 0 ? '+' : '−'}₹${Math.abs(adj).toLocaleString()}) — ref: ${inv.invoiceNumber}`,
      });
      if (!adjResult.success) {
        alert('Invoice issued, but failed to create separate adjustment: ' + (adjResult.error || ''));
      }
    }

    setRecalcPreviewInvoice(null);
    setPendingRevision(null);
    setShowEditor(false);
    setEditorInvoice(null);
  }, [invoicesHook, recalcPreviewInvoice, project]);

  // Feature 2: Enhanced confirm — passes paymentEvent and refreshes learned names
  // Also triggers recommended actions when an invoice is marked as paid.
  const handleConfirmPayment = useCallback(async (invoiceId, paidDate, paymentEvent = {}) => {
    if (!invoicesHook) return { success: false, error: 'Invoice system not available.' };
    const result = await invoicesHook.confirmPayment(invoiceId, paidDate, paymentEvent);
    if (result.success && paymentEvent.accountName && currentProjectId && invoicesHook.fetchProjectAccountNames) {
      invoicesHook.fetchProjectAccountNames(currentProjectId).then(r => {
        if (r.success) setKnownAccountNames(r.data.map(x => x.accountName));
      });
    }
    // Trigger recommended actions on successful payment via central engine
    if (result.success && paymentInvoice) {
      const stageKey = paymentInvoice.stageKey;
      const triggerType = resolveTimelineTrigger({
        fieldKey: 'paidDate',
        previousValue: null, // payment confirmation = first paid date entry
        nextValue: paidDate,
        stageKey,
      });
      if (triggerType && stageKey) {
        const currentStageObj = sortedStages.find(s => s.key === stageKey);
        const nextStage = getNextAllocatedStage(stageKey);
        recActions.generateFromTrigger(triggerType, {
          stageKey,
          stageName: currentStageObj?.name || stageKey,
          projectName: project?.name || project?.projectName || '',
          projectCode: project?.projectCode || '',
          ...(nextStage ? { nextStageName: nextStage.name, nextStageKey: nextStage.key } : {}),
        });
      }
    }
    return result;
  }, [invoicesHook, currentProjectId, paymentInvoice, project, recActions, getNextAllocatedStage, sortedStages]);

  // Feature 4 + Step 2: Open detailed confirm dialog (from invoice row OR inbox)
  const handleConfirmPaymentMatch = useCallback((invOrEvt) => {
    // Called from invoice row: invOrEvt is an invoice with .paymentEvent
    if (invOrEvt.paymentEvent && invOrEvt.id) {
      setMatchConfirmTarget({ paymentEvent: invOrEvt.paymentEvent, invoice: invOrEvt });
      return;
    }
    // Called from inbox: invOrEvt is a payment event — find the suggested invoice.
    // Search the global allInvoices list first (suggestion may target an invoice
    // outside the currently selected project), then fall back to project-scoped.
    if (invOrEvt.suggestedInvoiceId) {
      const inv =
        invoicesHook?.allInvoices?.find(i => i.id === invOrEvt.suggestedInvoiceId) ||
        invoicesHook?.invoices?.find(i => i.id === invOrEvt.suggestedInvoiceId);
      if (inv) {
        setMatchConfirmTarget({ paymentEvent: invOrEvt, invoice: inv });
        return;
      }
      // Suggested invoice was deleted/unavailable — try to find a replacement:
      // an issued invoice for the same project with an amount that matches the payment.
      const allAvailable = invoicesHook?.allInvoices || invoicesHook?.invoices || [];
      const replacement = allAvailable.find(i =>
        i.status === 'issued' &&
        (!invOrEvt.suggestedProjectId || String(i.projectId) === String(invOrEvt.suggestedProjectId)) &&
        Math.abs(Number(i.netAmount) - Number(invOrEvt.amount)) < 1
      );
      if (replacement) {
        setMatchConfirmTarget({ paymentEvent: invOrEvt, invoice: replacement });
        return;
      }
    }
    alert('Could not find the linked invoice. It may have been deleted or is still loading — please refresh and try again.');
  }, [invoicesHook?.invoices, invoicesHook?.allInvoices]);

  // Step 2: Actual confirm action (called from PaymentMatchConfirmModal)
  const handleDoConfirmMatch = useCallback(async ({ invoiceId, paymentEvent: pe, invoice: inv }) => {
    if (!invoicesHook?.confirmPaymentMatch) return { success: false, error: 'Not available.' };
    const result = await invoicesHook.confirmPaymentMatch({ invoiceId, paymentEvent: pe, invoice: inv });
    if (result.success) {
      // Refresh the inbox globally — confirmed invoice may belong to a different
      // project than currentProjectId, and the inbox is rendered across all projects.
      invoicesHook.refreshPaymentEvents?.();
      if (currentProjectId) {
        // Archived list and learned account names remain scoped to the current project.
        invoicesHook.fetchArchivedPaymentEvents?.(currentProjectId, 0);
        invoicesHook.fetchProjectAccountNames?.(currentProjectId)?.then(r => {
          if (r?.success) setKnownAccountNames(r.data.map(x => x.accountName));
        });
      }
      // Trigger recommended actions via central engine
      const stageKey = inv?.stageKey;
      const triggerType = resolveTimelineTrigger({
        fieldKey: 'paidDate',
        previousValue: null, // payment match confirmation = first paid date entry
        nextValue: new Date().toISOString().slice(0, 10),
        stageKey,
      });
      if (triggerType && stageKey) {
        const currentStageObj = sortedStages.find(s => s.key === stageKey);
        const nextStage = getNextAllocatedStage(stageKey);
        recActions.generateFromTrigger(triggerType, {
          stageKey,
          stageName: currentStageObj?.name || stageKey,
          projectName: project?.name || project?.projectName || '',
          projectCode: project?.projectCode || '',
          ...(nextStage ? { nextStageName: nextStage.name, nextStageKey: nextStage.key } : {}),
        });
      }
    }
    return result;
  }, [invoicesHook, currentProjectId, project, recActions, getNextAllocatedStage, sortedStages]);

  // Step 1: Ignore handler
  const handleIgnorePayment = useCallback(async (eventId) => {
    if (!invoicesHook?.ignorePaymentEvent) return { success: false };
    const result = await invoicesHook.ignorePaymentEvent(eventId);
    if (result.success) invoicesHook.refreshPaymentEvents?.();
    return result;
  }, [invoicesHook]);

  // Step 1: Link-to-invoice handler
  const handleLinkPayment = useCallback(async ({ eventId, invoiceId }) => {
    if (!invoicesHook?.linkPaymentToInvoice) return { success: false };
    const result = await invoicesHook.linkPaymentToInvoice({ eventId, invoiceId });
    if (result.success) invoicesHook.refreshPaymentEvents?.();
    return result;
  }, [invoicesHook]);

  // Step 1: Mark as expense payment handler
  const handleExpensePayment = useCallback(async (eventId, note) => {
    if (!invoicesHook?.markAsExpensePayment) return { success: false };
    const result = await invoicesHook.markAsExpensePayment(eventId, note);
    if (result.success) invoicesHook.refreshPaymentEvents?.();
    return result;
  }, [invoicesHook]);

  // Step 4: Undo payment confirmation (admin only)
  const handleUndoPayment = useCallback(async (inv) => {
    if (!invoicesHook?.undoPaymentConfirmation) return;
    if (!window.confirm(
      `Undo payment confirmation for ${inv.invoiceNumber || '#' + inv.id}?\n` +
      'The invoice will revert to "Issued" status. Account name will be preserved.'
    )) return;
    const result = await invoicesHook.undoPaymentConfirmation(inv.id);
    if (!result.success) {
      alert('Undo failed: ' + (result.error || ''));
    } else if (currentProjectId) {
      invoicesHook.refreshPaymentEvents?.(currentProjectId);
    }
  }, [invoicesHook, currentProjectId]);

  const handleDelete = useCallback(async (inv) => {
    if (!invoicesHook) return;
    if (!window.confirm(`Delete invoice "${inv.invoiceNumber || inv.id}"? This cannot be undone.`)) return;
    const result = await invoicesHook.deleteInvoice(inv.id);
    if (!result.success) alert('Failed to delete: ' + (result.error || 'Unknown error'));
  }, [invoicesHook]);

  const openCreate = useCallback((prefillStageKey, prefillDate) => {
    if (DEBUG_INVOICES) console.log('[DEBUG_INVOICES] openCreate', { prefillStageKey, prefillDate });
    // ── Fee revision gate: show recalc preview BEFORE creating draft ──
    const latestRevision = feeRevisionsHook?.getLatestRevision?.();
    if (latestRevision && prefillStageKey) {
      console.log('[CreateInvoice] revision gate triggered', {
        stageKey: prefillStageKey,
        revisionId: latestRevision.id,
        oldFee: latestRevision.oldTotalFee,
        newFee: latestRevision.newTotalFee,
      });
      // Build a synthetic "invoice" object so RecalcPreviewModal can compute
      // stage breakdowns — it only needs stageKey, baseAmount, gstPercent, tdsPercent
      const totalFee = getCurrentTotalFee(project);
      // For part stages, look up the parent key for allocation/invoicePercent
      const lookupKey = prefillStageKey.includes('__part__') ? prefillStageKey.slice(0, prefillStageKey.indexOf('__part__')) : prefillStageKey;
      const invPct = project?.stageTimeline?.[lookupKey]?.invoicePercent;
      const allocPct = Number(project?.budgetAllocation?.[lookupKey]) || 0;
      const stagePercent = invPct != null ? Number(invPct) : allocPct;
      const suggestedBase = totalFee * stagePercent / 100;

      setPendingCreateParams({
        stageKey: prefillStageKey,
        invoiceDate: prefillDate || new Date().toISOString().slice(0, 10),
      });
      setRecalcPreviewInvoice({
        // Synthetic — no id means "not yet created"
        stageKey: prefillStageKey,
        baseAmount: suggestedBase,
        gstPercent: DEFAULT_GST_PERCENT,
        tdsPercent: DEFAULT_TDS_PERCENT,
        isManualOverride: false,
      });
      setPendingRevision(latestRevision);
      return;
    }

    // No revision or no stageKey — open editor directly
    setEditorInvoice({ stageKey: prefillStageKey || '', invoiceDate: prefillDate || new Date().toISOString().slice(0, 10) });
    setShowEditor(true);
  }, [feeRevisionsHook, project]);

  const openEdit = useCallback((inv) => { setEditorInvoice(inv); setShowEditor(true); }, []);

  // ── D1) Open Email Draft Modal for invoice / reminder ──
  const handleSendInvoiceEmail = useCallback((inv, templateKey = 'invoice_sent') => {
    if (!inv) return;
    const client = activeClients?.find(c => Number(c.id) === Number(project?.clientId));
    if (!client) { alert('No client linked to this project.'); return; }
    setEmailDraftInvoice({ invoice: inv, templateKey });
  }, [activeClients, project]);

  // ── D) Export invoice ledger CSV ──
  const handleExportLedger = useCallback(() => {
    const invs = invoicesHook?.invoices || [];
    if (invs.length === 0) { alert('No invoices to export.'); return; }
    const headers = [
      'Project ID', 'Project Name', 'Stage Key', 'Stage Name', 'Invoice #',
      'Doc Type', 'Invoice Date', 'Base Amount', 'Tax Regime', 'Tax %', 'TDS Applied',
      'TDS %', 'Net Amount', 'Status', 'Account', 'Office Location',
      'Fee Revision', 'Adjustment Notes',
    ];
    const escCsv = (v) => {
      const s = String(v ?? '');
      return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = invs.map(inv => {
      const sName = combinedStages.find(s => s.key === inv.stageKey)?.name || inv.stageKey || '';
      const taxRegimeLabel = TAX_REGIME_OPTIONS.find(o => o.value === inv.taxRegime)?.label || inv.taxRegime || '';

      // For reporting exports, prefer INR-converted amounts for NL.
      const baseForExport = inv.officeLocation === 'NL' && inv.baseAmountInrDefault != null
        ? inv.baseAmountInrDefault
        : (inv.baseAmount || 0);
      const netForExport = inv.officeLocation === 'NL' && inv.netAmountInrDefault != null
        ? inv.netAmountInrDefault
        : (inv.netAmount || 0);

      const hasFeeRevision = inv.feeRevisionId || inv.basisMode === 'adjustment';
      const adjustmentNotes = inv.recalcExplanationText || (inv.basisMode === 'adjustment' ? 'Fee revision adjustment' : '');

      return [
        currentProjectId, project?.name || '', inv.stageKey || '', sName,
        inv.invoiceNumber || '', inv.invoiceDocType === 'proforma' ? 'Proforma' : 'Tax',
        inv.invoiceDate || '',
        baseForExport, taxRegimeLabel, inv.gstPercent || 0,
        inv.applyTds !== false ? 'true' : 'false', inv.tdsPercent || 0,
        netForExport, inv.status || '', inv.accountName || '',
        inv.officeLocation || '',
        hasFeeRevision ? 'Yes' : '',
        adjustmentNotes,
      ].map(escCsv).join(',');
    });
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `invoice-ledger-${project?.name?.replace(/\s+/g, '-') || currentProjectId}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [invoicesHook?.invoices, combinedStages, currentProjectId, project]);

  // ── D2) Export invoice ledger XLSX (Revised Fee Schedule workbook) ──
  const handleExportLedgerXlsx = useCallback(async () => {
    const invs = invoicesHook?.invoices || [];
    if (invs.length === 0) { alert('No invoices to export.'); return; }
    try {
      const { buildLedgerWorkbook, addFeeRevisionHistorySheet, downloadLedgerWorkbook } = await import('../../utils/invoiceLedgerXlsx');
      const client = activeClients?.find(c => Number(c.id) === Number(project?.clientId));
      const wb = await buildLedgerWorkbook({
        invoices: invs,
        combinedStages,
        project,
        clientName: client?.invoicingName || client?.name || '',
      });
      // Add fee revision history sheet if revisions exist
      const revisions = feeRevisionsHook?.revisions || [];
      if (revisions.length > 0) {
        addFeeRevisionHistorySheet(wb, revisions, project);
      }
      const filename = `fee-schedule-${project?.name?.replace(/\s+/g, '-') || currentProjectId}-${new Date().toISOString().slice(0, 10)}.xlsx`;
      await downloadLedgerWorkbook(wb, filename);
    } catch (err) {
      console.error('[LedgerXlsx] export error:', err);
      alert('Failed to export XLSX ledger. See console for details.');
    }
  }, [invoicesHook?.invoices, combinedStages, currentProjectId, project, activeClients, feeRevisionsHook?.revisions]);

  // ── E) Assemble invoice PDF data for a given office ──
  // Returns all fields needed for PDF rendering.
  // officeKey: 'rotterdam' | 'mumbai' — selects header image + bank details.
  const getInvoicePdfData = useCallback((inv, officeKey = 'rotterdam') => {
    if (!inv) return null;
    const branding = settingsHook?.invoiceBranding || {};
    if (typeof window !== 'undefined' && window.location?.search?.includes('debugPdf')) {
      console.log('[PDF-DATA] settingsHook.invoiceBranding:', JSON.stringify({
        hasOffices: !!branding.offices,
        officeKeys: Object.keys(branding.offices || {}),
        officeKey,
        hasHeaderImage: !!resolveBrandingImageSrc(branding.offices?.[officeKey]?.headerImage),
        headerImageSrcLen: resolveBrandingImageSrc(branding.offices?.[officeKey]?.headerImage).length,
        bankKeys: Object.keys(branding.offices?.[officeKey]?.bankDetails || {}),
        bankNonEmpty: Object.entries(branding.offices?.[officeKey]?.bankDetails || {}).filter(([,v]) => v).length,
        hasSigBlock: !!branding.signatureBlock,
        sigCompany: branding.signatureBlock?.companyLine || '',
        sigName: branding.signatureBlock?.signatoryName || '',
        hasSigImage: !!resolveBrandingImageSrc(branding.signatureBlock?.signatureImage),
        sigImageSrcLen: resolveBrandingImageSrc(branding.signatureBlock?.signatureImage).length,
      }, null, 2));
    }
    const client = activeClients?.find(c => Number(c.id) === Number(project?.clientId));

    // Normalize any image field: string, {url}, {dataUrl}, or null
    const normalizeImage = (x) => {
      const src = resolveBrandingImageSrc(x);
      if (!src) return null;
      const filename = (x && typeof x === 'object' && x.filename) || '';
      // Keep Storage URLs as `url` so the renderer and the browser can cache
      // them; inline base64 stays on `dataUrl`.
      return src.startsWith('data:') ? { dataUrl: src, filename } : { url: src, filename };
    };

    // Office-specific settings
    const officeSettings = branding.offices?.[officeKey] || {};

    // ── Header image: office-specific, normalized ──
    const headerImage = normalizeImage(officeSettings.headerImage);

    // ── Bank details: office-specific, always an object ──
    const rawBank = officeSettings.bankDetails;
    const bankDetails = (rawBank && typeof rawBank === 'object' && !Array.isArray(rawBank)) ? rawBank : {};

    // ── Signature block + image: try office-level first, then global branding ──
    const officeSig = officeSettings.signatureBlock || {};
    const globalSig = branding.signatureBlock || {};
    const signatureBlock = {
      companyLine:    String(officeSig.companyLine    || globalSig.companyLine    || ''),
      signatoryName:  String(officeSig.signatoryName  || globalSig.signatoryName  || ''),
      signatoryTitle: String(officeSig.signatoryTitle || globalSig.signatoryTitle || ''),
    };

    // Signature image: office-level > global signatureBlock > null
    const signatureImage = normalizeImage(officeSig.signatureImage) || normalizeImage(globalSig.signatureImage);

    // Project name: invoice override > internal name
    const projectDisplayName = project?.invoiceProjectName || project?.name || '';

    // Invoice type: invoice-level > client-level > omit
    const invoiceType = inv.invoiceType || client?.invoiceType || '';

    // Stage name + optional part-stage note (custom description overrides)
    const stageDef = combinedStages.find(s => s.key === inv.stageKey);
    let stageLine = inv.customStageName || stageDef?.name || inv.stageKey || '';
    if (!inv.customStageName && inv.stageKey && inv.stageKey.includes('__part__')) {
      const [parentKey, , partId] = inv.stageKey.split('__part__');
      const partStages = project?.stageTimeline?.[parentKey]?.partStages;
      if (Array.isArray(partStages)) {
        const part = partStages.find(ps => String(ps.id) === String(partId));
        const pctOfStage = part?.billing?.percentOfStage;
        if (pctOfStage != null) {
          stageLine += ` (${pctOfStage}% of the stage)`;
        }
      }
    }

    // Debug flag
    const debugPdf = typeof window !== 'undefined' && window.location?.search?.includes('debugPdf');

    return {
      headerImage,
      officeKey,
      officeLabel: officeSettings.label || officeKey,
      clientName: client?.invoicingName || client?.name || '',
      clientOrganisation: client?.organisation || '',
      clientAddress: client?.address || '',
      clientGst: client?.gst || '',
      clientPan: client?.pan || '',
      clientSacCode: inv.sacCode || client?.sacCode || '',
      clientPlaceOfSupply: client?.placeOfSupply || '',
      clientVatNumber: client?.vatBtw || '',
      projectName: projectDisplayName,
      invoiceType,
      invoiceNumber: inv.invoiceNumber || '',
      invoiceDate: inv.invoiceDate || '',
      stageLine,
      baseAmount: inv.baseAmount || 0,
      gstPercent: inv.gstPercent || 0,
      tdsPercent: inv.tdsPercent || 0,
      grossAmount: inv.grossAmount || 0,
      tdsAmount: inv.tdsAmount || 0,
      netAmount: inv.netAmount || 0,
      taxRegime: inv.taxRegime || '',
      applyTds: inv.applyTds !== false,
      note: inv.note || '',
      currency: inv.currency || (officeKey === 'rotterdam' ? 'EUR' : 'INR'),
      bankDetails,
      signatureBlock,
      signatureImage,
      debugPdf,
    };
  }, [settingsHook?.invoiceBranding, activeClients, project, combinedStages]);

  // ── Create draft WITH fee-revision snapshot pre-baked (roll-in approach) ──
  const handleCreateWithRecalc = useCallback(async (adjustmentAmount, explanationText, revision) => {
    if (!invoicesHook || !pendingCreateParams) return;
    const { stageKey, invoiceDate } = pendingCreateParams;

    const totalFee = getCurrentTotalFee(project);
    const issuedInvoices = invoicesHook.getIssuedInvoices?.() || [];
    const breakdown = computeRecalcBreakdown(issuedInvoices, totalFee, project, stageKey);
    const { currentStagePlanned, currentStagePercent, issuedTotalBase, issuedTotalAtNewFee, adjustmentDue } = breakdown;

    const adj = Number(adjustmentAmount) || 0;
    const baseAmount = currentStagePlanned + adj;
    const basisMode = adj !== 0 ? 'percent_plus_adjustment' : 'percent';
    const isEur = project?.contractCurrency === 'EUR';
    const currSym = isEur ? '€' : '₹';

    console.log('[CreateInvoice] creating draft with revision payload', {
      stageKey, currentStagePlanned, adj, baseAmount, basisMode,
      revisionId: revision?.id,
    });

    const result = await invoicesHook.createInvoice({
      projectId: currentProjectId,
      stageKey,
      invoiceDate,
      baseAmount,
      currency: isEur ? 'EUR' : 'INR',
      gstPercent: isEur ? 0 : DEFAULT_GST_PERCENT,
      tdsPercent: isEur ? 0 : DEFAULT_TDS_PERCENT,
      applyTds: !isEur,
      officeLocation: isEur ? 'NL' : 'IN',
      isManualOverride: false,
      basisMode,
      feeRevisionId: revision?.id || null,
      totalFeeSnapshot: totalFee,
      invoicePercentSnapshot: currentStagePercent,
      baseAmountPlanned: currentStagePlanned,
      adjustmentAmount: adj,
      recalcBreakdownJson: {
        issuedLines: breakdown.issuedLines.map(l => ({
          invoiceNumber: l.invoiceNumber,
          stageKey: l.stageKey,
          basisMode: l.basisMode,
          invoicePercentSnapshot: l.invoicePercentSnapshot,
          issuedBase: l.issuedBase,
          plannedAtNewFee: l.plannedAtNewFee,
          delta: l.delta,
          estimated: l.estimated,
        })),
        issuedTotalBase,
        issuedTotalAtNewFee,
        adjustmentDue,
        currentStagePlanned,
        currentStagePercent,
        newFee: totalFee,
        approach: 'roll_in',
      },
      recalcExplanationText: explanationText,
      note: adj !== 0
        ? `Fee revision adjustment (${adj >= 0 ? '+' : '−'}${currSym}${Math.abs(adj).toLocaleString()}) rolled in`
        : '',
    });

    // Clean up modal state
    setRecalcPreviewInvoice(null);
    setPendingRevision(null);
    setPendingCreateParams(null);

    if (result.success) {
      // Open editor with the newly created draft so user can review/adjust
      console.log('[DraftFromRecalc] created invoice', result.data);
      console.log('[DraftFromRecalc] opening editor with base', result.data.baseAmount, 'adjustment', result.data.adjustmentAmount, 'basisMode', result.data.basisMode);
      setEditorInvoice(result.data);
      setShowEditor(true);
    } else {
      alert('Failed to create draft: ' + (result.error || ''));
    }
  }, [invoicesHook, pendingCreateParams, project, currentProjectId]);

  // ── Create draft WITH fee-revision, separate adjustment approach ──
  const handleCreateWithSeparateAdjustment = useCallback(async (adjustmentAmount, explanationText, revision) => {
    if (!invoicesHook || !pendingCreateParams) return;
    const { stageKey, invoiceDate } = pendingCreateParams;

    const totalFee = getCurrentTotalFee(project);
    const issuedInvoices = invoicesHook.getIssuedInvoices?.() || [];
    const breakdown = computeRecalcBreakdown(issuedInvoices, totalFee, project, stageKey);
    const { currentStagePlanned, currentStagePercent, issuedTotalBase, issuedTotalAtNewFee, adjustmentDue } = breakdown;
    const adj = Number(adjustmentAmount) || 0;
    const isEur = project?.contractCurrency === 'EUR';
    const currSym = isEur ? '€' : '₹';

    console.log('[CreateInvoice] creating draft (separate approach)', {
      stageKey, currentStagePlanned, adj,
      revisionId: revision?.id,
    });

    const sharedBreakdown = {
      issuedLines: breakdown.issuedLines.map(l => ({
        invoiceNumber: l.invoiceNumber,
        stageKey: l.stageKey,
        basisMode: l.basisMode,
        invoicePercentSnapshot: l.invoicePercentSnapshot,
        issuedBase: l.issuedBase,
        plannedAtNewFee: l.plannedAtNewFee,
        delta: l.delta,
        estimated: l.estimated,
      })),
      issuedTotalBase,
      issuedTotalAtNewFee,
      adjustmentDue,
      currentStagePlanned,
      currentStagePercent,
      newFee: totalFee,
      approach: 'separate',
    };

    // Main invoice at planned base (no adjustment rolled in)
    const mainResult = await invoicesHook.createInvoice({
      projectId: currentProjectId,
      stageKey,
      invoiceDate,
      baseAmount: currentStagePlanned,
      currency: isEur ? 'EUR' : 'INR',
      gstPercent: isEur ? 0 : DEFAULT_GST_PERCENT,
      tdsPercent: isEur ? 0 : DEFAULT_TDS_PERCENT,
      applyTds: !isEur,
      officeLocation: isEur ? 'NL' : 'IN',
      isManualOverride: false,
      basisMode: 'percent',
      feeRevisionId: revision?.id || null,
      totalFeeSnapshot: totalFee,
      invoicePercentSnapshot: currentStagePercent,
      baseAmountPlanned: currentStagePlanned,
      adjustmentAmount: 0,
      recalcBreakdownJson: sharedBreakdown,
      recalcExplanationText: explanationText,
    });

    // Create separate adjustment draft if delta non-zero
    if (mainResult.success && adj !== 0) {
      const adjResult = await invoicesHook.createInvoice({
        projectId: currentProjectId,
        stageKey: null,
        invoiceDate,
        baseAmount: Math.abs(adj),
        currency: isEur ? 'EUR' : 'INR',
        gstPercent: isEur ? 0 : DEFAULT_GST_PERCENT,
        tdsPercent: isEur ? 0 : DEFAULT_TDS_PERCENT,
        applyTds: !isEur,
        officeLocation: isEur ? 'NL' : 'IN',
        isManualOverride: true,
        basisMode: 'adjustment',
        feeRevisionId: revision?.id || null,
        totalFeeSnapshot: totalFee,
        recalcBreakdownJson: sharedBreakdown,
        recalcExplanationText: explanationText,
        note: `Fee revision adjustment (${adj >= 0 ? '+' : '−'}${currSym}${Math.abs(adj).toLocaleString()}) — separate invoice`,
      });
      if (!adjResult.success) {
        alert('Main invoice created, but adjustment draft failed: ' + (adjResult.error || ''));
      }
    }

    // Clean up modal state
    setRecalcPreviewInvoice(null);
    setPendingRevision(null);
    setPendingCreateParams(null);

    if (mainResult.success) {
      console.log('[DraftFromRecalc] created invoice (separate)', mainResult.data);
      console.log('[DraftFromRecalc] opening editor with base', mainResult.data.baseAmount, 'basisMode', mainResult.data.basisMode);
      setEditorInvoice(mainResult.data);
      setShowEditor(true);
    } else {
      alert('Failed to create draft: ' + (mainResult.error || ''));
    }
  }, [invoicesHook, pendingCreateParams, project, currentProjectId]);

  // ============================================================================
  // RENDER
  // ============================================================================
  return (
    <div className="brio-flex brio-flex-col brio-analytics-font">
      <div className="brio-pageband brio-pageband--header">
        <BrioFrame>
          <div className="brio-page-header__content">
            <div className="brio-page-header__left">

              <p className="brio-analytics-muted brio-mt-xs">Create, issue, and track invoice payments by project</p>
            </div>
          </div>
        </BrioFrame>
      </div>

      {/* Payment Inbox - above project selector so new payments are immediately visible */}
      {canConfirm && (
        <BrioFrame>
          <PaymentInboxPanel
            suggestedEvents={invoicesHook?.suggestedPaymentEvents || []}
            unmatchedEvents={invoicesHook?.unmatchedPaymentEvents || []}
            loading={invoicesHook?.paymentEventsLoading || false}
            invoices={invoicesHook?.allInvoices || []}
            projects={availableProjects || []}
            activeClients={activeClients}
            fmtProject={fmtProject}
            onConfirmMatch={handleConfirmPaymentMatch}
            onIgnore={handleIgnorePayment}
            onLinkToInvoice={handleLinkPayment}
            onExpensePayment={handleExpensePayment}
            fmtCurr={fmtCurr}
            canConfirm={canConfirm}
            archivedEvents={invoicesHook?.archivedPaymentEvents || []}
            archivedLoading={invoicesHook?.archivedPaymentEventsLoading || false}
            archivedPage={invoicesHook?.archivedPaymentPage || 0}
            archivedHasNext={invoicesHook?.archivedPaymentHasNext || false}
            onFetchArchived={(page) => currentProjectId && invoicesHook?.fetchArchivedPaymentEvents?.(currentProjectId, page)}
          />
        </BrioFrame>
      )}

      <BrioFrame>
        <div className="brio-gap-s" style={{ paddingTop: 'var(--brio-space-s)' }}>
          <BrioAnalyticsSurface padding="m">
            {(!availableProjects || availableProjects.length === 0) ? (
              <div className="brio-text-center brio-analytics-muted" style={{ padding: '48px 0' }}>
                <p className="brio-mb-s">No projects available</p>
                {currentUser.role === 'supervisor' && <p className="brio-text-xs">You are not assigned to supervise any projects.</p>}
              </div>
            ) : (
              <>
                {/* Project Selector */}
                <div className="brio-mb-s">
                  <div className="brio-flex brio-justify-between brio-items-center">
                    <label className="brio-modal-label">Select Project</label>
                    <span className="brio-text-xs brio-text-grey">{availableProjects.length} {availableProjects.length === 1 ? 'project' : 'projects'}</span>
                  </div>
                  <select
                    value={currentProjectId || ''}
                    onChange={(e) => setSelectedTimelineProject(Number(e.target.value))}
                    className="brio-modal-select"
                    style={{ maxWidth: '384px' }}
                  >
                    {availableProjects.map(p => {
                      const c = activeClients.find(x => x.id === p.clientId);
                      return <option key={p.id} value={p.id}>{fmtProject(p)} {c ? `– ${c?.organisation || c?.name}` : ''}</option>;
                    })}
                  </select>
                </div>

                {project && (
                  <>
                    {/* Gold header band */}
                    <div className="brio-mb-s" style={{ padding: '16px', background: 'var(--brio-gold-light)', border: '1px solid var(--brio-gold)' }}>
                      <div className="brio-flex brio-justify-between brio-items-center">
                        <div>
                          <h3 className="brio-font-bold">{project.name}</h3>
                          <div className="brio-text-xs brio-text-grey brio-mt-xs">
                            Client: {activeClients.find(c => c.id === project.clientId)?.name || '—'}{' | '}Total Fee: {fmtCC(getCurrentTotalFee(project))}{getCurrentTotalFee(project) !== (Number(project.budget) || 0) ? ` (Base: ${fmtCC(project.budget)})` : ''}
                          </div>
                        </div>
                        {canCreate && (
                          <div className="brio-flex brio-gap-xs">
                            <button onClick={handleExportLedger} className="brio-btn brio-btn-secondary"
                              style={{ padding: '6px 14px', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <Download className="w-3 h-3" /> Export Ledger
                            </button>
                            <button onClick={handleExportLedgerXlsx} className="brio-btn brio-btn-secondary"
                              style={{ padding: '6px 14px', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <FileText className="w-3 h-3" /> Export Ledger (.xlsx)
                            </button>
                            <button onClick={() => setShowFeeRevisionPanel(true)} className="brio-btn brio-btn-secondary"
                              style={{ padding: '6px 14px', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <RefreshCw className="w-3 h-3" /> Fee Revisions
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Intent Alerts */}
                    {intentAlerts.length > 0 && (
                      <div className="brio-mb-s" style={{ padding: '12px 16px', background: 'var(--brio-gold-light)', border: '1px solid var(--brio-gold)' }}>
                        <div className="brio-flex brio-items-center brio-gap-xs brio-mb-xs">
                          <AlertTriangle className="w-4 h-4" style={{ color: 'var(--brio-gold)' }} />
                          <span className="brio-text-s brio-font-bold">Invoice Intents</span>
                        </div>
                        {intentAlerts.map(a => (
                          <div key={a.stageKey} className="brio-flex brio-justify-between brio-items-center brio-text-s" style={{ padding: '4px 0' }}>
                            <span><strong>{a.stageName}</strong> — invoice due on {fmtDate(a.intentDate)}, no invoice created yet.</span>
                            {canCreate && (
                              <button onClick={() => openCreate(a.stageKey, a.intentDate)} className="brio-btn brio-btn-secondary"
                                style={{ padding: '3px 10px', fontSize: '10px', whiteSpace: 'nowrap' }}>Create Invoice</button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Fee Revision Panel (collapsible) */}
                    {showFeeRevisionPanel && (
                      <div className="brio-mb-s" style={{ padding: '12px 16px', background: 'var(--brio-grey-light)', border: '1px solid var(--brio-border)' }}>
                        <div className="brio-flex brio-justify-between brio-items-center brio-mb-xs">
                          <div className="brio-flex brio-items-center brio-gap-xs">
                            <RefreshCw className="w-4 h-4" style={{ color: 'var(--brio-gold)' }} />
                            <span className="brio-text-s brio-font-bold">Fee Revision History</span>
                          </div>
                          <button onClick={() => setShowFeeRevisionPanel(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                            <X className="w-4 h-4" style={{ color: 'var(--brio-grey)' }} />
                          </button>
                        </div>
                        <div className="brio-text-xs brio-text-grey brio-mb-xs">
                          Fee revisions are created automatically when a project's total fee changes. Each revision captures the old and new fee, enabling invoice recalculation.
                        </div>
                        {(feeRevisionsHook?.revisions || []).length === 0 ? (
                          <div className="brio-text-s brio-text-grey" style={{ padding: '8px 0' }}>No fee revisions for this project.</div>
                        ) : (
                          <table style={{ width: '100%', fontSize: '10px', borderCollapse: 'collapse', fontFamily: 'var(--brio-font-family)' }}>
                            <thead>
                              <tr style={{ borderBottom: '2px solid var(--brio-border)' }}>
                                <th style={{ padding: '4px 6px', textAlign: 'left', fontWeight: 600 }}>Date</th>
                                <th style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 600 }}>Old Fee</th>
                                <th style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 600 }}>New Fee</th>
                                <th style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 600 }}>Delta</th>
                                <th style={{ padding: '4px 6px', textAlign: 'left', fontWeight: 600 }}>Reason</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(feeRevisionsHook?.revisions || []).map(rev => (
                                <tr key={rev.id} style={{ borderBottom: '1px solid var(--brio-border)' }}>
                                  <td style={{ padding: '4px 6px' }}>{fmtDate(rev.effectiveDate)}</td>
                                  <td style={{ padding: '4px 6px', textAlign: 'right' }}>{fmtCC(rev.oldTotalFee)}</td>
                                  <td style={{ padding: '4px 6px', textAlign: 'right' }}>{fmtCC(rev.newTotalFee)}</td>
                                  <td style={{ padding: '4px 6px', textAlign: 'right', color: rev.deltaFee > 0 ? 'var(--brio-green, #16a34a)' : rev.deltaFee < 0 ? '#dc2626' : 'inherit' }}>
                                    {rev.deltaFee > 0 ? '+' : ''}{fmtCC(rev.deltaFee)}
                                  </td>
                                  <td style={{ padding: '4px 6px', fontSize: '9px', color: 'var(--brio-grey)' }}>{rev.reason || '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                        <div className="brio-text-xs brio-text-grey" style={{ marginTop: '6px' }}>
                          Tip: Change the project budget in Manage Projects to trigger a new fee revision automatically.
                        </div>
                      </div>
                    )}

                    {/* Summary Bar */}
                    <div className="brio-mb-s brio-flex brio-gap-s brio-flex-wrap" style={{ padding: '12px 16px', background: 'var(--brio-grey-light)', border: '1px solid var(--brio-border)' }}>
                      <div className="brio-text-s"><span className="brio-text-grey">Total:</span> <strong>{summary.total}</strong></div>
                      <div className="brio-text-s"><span className="brio-text-grey">Draft:</span> <strong>{summary.draft}</strong></div>
                      <div className="brio-text-s"><span className="brio-text-grey">Issued:</span> <strong>{summary.issued}</strong></div>
                      <div className="brio-text-s"><span className="brio-text-grey">Paid:</span> <strong>{summary.paid}</strong></div>
                      {isAdmin && summary.unassigned > 0 && (
                        <div className="brio-text-s"><span style={{ color: 'var(--brio-gold-dark, #7a6f1f)' }}>Unassigned:</span> <strong style={{ color: 'var(--brio-gold-dark, #7a6f1f)' }}>{summary.unassigned}</strong></div>
                      )}
                      <div style={{ flex: 1 }} />
                      <div className="brio-text-s"><span className="brio-text-grey">Invoiced:</span> <strong>{fmtCC(summary.totalNet)}</strong></div>
                      <div className="brio-text-s"><span className="brio-text-grey">Received:</span> <strong style={{ color: 'var(--brio-green)' }}>{fmtCC(summary.paidNet)}</strong></div>
                    </div>

                    {/* Unassigned Imports Panel (admin only) */}
                    {isAdmin && (
                      <UnassignedImportsPanel
                        invoices={invoicesHook?.invoices || []}
                        combinedStages={combinedStages}
                        project={project}
                        fmtCurr={fmtCurr}
                        formatMoney={settingsHook?.formatMoney}
                        invoicesHook={invoicesHook}
                        isAdmin={isAdmin}
                      />
                    )}

                    {/* Stage Billing & Invoices (single unified table) */}
                    {invoicesHook?.loading ? (
                      <div className="brio-text-center brio-text-grey" style={{ padding: '32px 0' }}>Loading invoices…</div>
                    ) : (
                      <>
                      {/* Payment terms warning */}
                      {(() => {
                        const cl = activeClients?.find(c => Number(c.id) === Number(project?.clientId));
                        if (cl && cl.paymentTermsDays == null) {
                          return (
                            <div style={{
                              padding: '6px 12px', marginBottom: '8px',
                              background: '#fffbeb', border: '1px solid #fbbf24', borderRadius: '4px',
                              fontSize: '10px', color: '#92400e',
                              display: 'flex', alignItems: 'center', gap: '6px',
                            }}>
                              <AlertTriangle className="w-3 h-3" style={{ flexShrink: 0 }} />
                              No payment terms set for this client — using 14-day fallback for due date calculations.
                            </div>
                          );
                        }
                        if (!cl) {
                          return (
                            <div style={{
                              padding: '6px 12px', marginBottom: '8px',
                              background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '4px',
                              fontSize: '10px', color: '#991b1b',
                              display: 'flex', alignItems: 'center', gap: '6px',
                            }}>
                              <AlertTriangle className="w-3 h-3" style={{ flexShrink: 0 }} />
                              No client linked to this project. Invoice emails and reminders require a linked client.
                            </div>
                          );
                        }
                        return null;
                      })()}
                      <div className="brio-mb-s" style={{ border: '1px solid var(--brio-border)', padding: '12px 16px' }}>
                        <StageBillingTable
                          combinedStages={combinedStages}
                          project={project}
                          invoices={invoicesHook?.invoices || []}
                          fmtCurr={fmtCurr}
                          formatMoney={settingsHook?.formatMoney}
                          canCreate={canCreate}
                          canConfirm={canConfirm}
                          canDelete={canDelete}
                          openCreate={openCreate}
                          openEdit={openEdit}
                          onIssue={handleIssue}
                          onConfirmPaid={(inv) => setPaymentInvoice(inv)}
                          onConfirmMatch={handleConfirmPaymentMatch}
                          onUndo={handleUndoPayment}
                          onDelete={handleDelete}
                          onExportPdf={(inv) => setPdfPreviewInv(inv)}
                          onSendEmail={handleSendInvoiceEmail}
                          onSendReminder={(inv) => setReminderInvoice(inv)}
                          client={activeClients?.find(c => Number(c.id) === Number(project?.clientId))}
                          useSupabaseDB={useSupabaseDB}
                          supabaseProjects={supabaseProjects}
                          setProjects={setProjects}
                          isAdmin={isAdmin}
                          invoicesHook={invoicesHook}
                        />
                      </div>
                      </>
                    )}

                    {/* Accounting Verification */}
                    <AccountingVerificationPanel
                      project={project}
                      invoices={invoicesHook?.invoices || []}
                      combinedStages={combinedStages}
                      fmtCurr={fmtCurr}
                      formatMoney={settingsHook?.formatMoney}
                      feeRevisions={feeRevisionsHook?.revisions || []}
                    />

                    {/* Monthly Archive moved to Radar dashboard (company-wide workflow) */}

                    {/* Legend */}
                    <div className="brio-mt-s" style={{ padding: '16px', background: 'var(--brio-grey-light)' }}>
                      <h4 className="brio-font-bold brio-mb-s">Status Legend</h4>
                      <div className="brio-flex brio-gap-s brio-flex-wrap brio-text-s">
                        <div className="brio-flex brio-items-center brio-gap-xs"><span className="brio-badge">Draft</span><span className="brio-text-grey">Editable, not yet issued</span></div>
                        <div className="brio-flex brio-items-center brio-gap-xs"><span className="brio-badge brio-badge--warning">Issued</span><span className="brio-text-grey">Locked, awaiting payment</span></div>
                        <div className="brio-flex brio-items-center brio-gap-xs"><span className="brio-badge brio-badge--warning">Due</span><span className="brio-text-grey">Payment due today</span></div>
                        <div className="brio-flex brio-items-center brio-gap-xs"><span className="brio-badge brio-badge--error">Overdue</span><span className="brio-text-grey">Past due date</span></div>
                        <div className="brio-flex brio-items-center brio-gap-xs"><span className="brio-badge brio-badge--approved">Paid</span><span className="brio-text-grey">Payment confirmed</span></div>
                      </div>
                      <div className="brio-mt-s brio-text-xs brio-text-grey" style={{ borderTop: '1px solid var(--brio-border)', paddingTop: '12px' }}>
                        <p>• <strong>Net Receivable</strong> = (Base + Tax) − TDS on base (when TDS applied)</p>
                        <p>• <strong>Invoice Intent</strong> dates set in Timeline are shown as alerts until an invoice is created</p>
                        <p>• <strong>Payment confirmation</strong> in this page is reflected read-only in Timeline</p>
                        <p>• <strong>Invoice numbers</strong> are auto-assigned (YYYYMMSS format) — override toggle for edge cases</p>
                        <p>• <strong>Account Name</strong> is a confidence signal for payment matching, not a primary match key</p>
                        <p>• <strong>Fee Revisions</strong> trigger a recalculation preview before issuing — already-issued invoices are never changed</p>
                        <p>• <strong>Invoice %</strong> uses Timeline's Inv.% column (falls back to allocation % if not set)</p>
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </BrioAnalyticsSurface>
        </div>
      {/* ── Communication History (collapsible) ── */}
      {project && (isAdmin || isSupervisor) && (
        <div style={{ margin: '12px auto 0', maxWidth: 875, padding: '0 16px' }}>
          <button
            onClick={() => setShowCommLog(!showCommLog)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: '6px',
              fontSize: '11px', fontWeight: 500, color: 'var(--brio-muted, #94a3b8)',
              padding: '6px 0',
            }}
          >
            {showCommLog ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            <Mail className="w-3 h-3" />
            Communication History
          </button>
          {showCommLog && (
            <div style={{
              padding: '10px 14px', marginTop: '4px',
              background: 'var(--brio-white, #fff)',
              border: '1px solid var(--brio-border, #d1d5db)',
              borderRadius: '6px',
            }}>
              <CommunicationLogList
                fetchLogs={() => fetchLogsForProject(project.id)}
                emptyText="No email drafts have been opened for this project yet."
                limit={8}
              />
            </div>
          )}
        </div>
      )}

      </BrioFrame>

      {/* Modals */}
      {showEditor && (
        <InvoiceEditorModal
          key={editorInvoice?.id || 'new'}
          invoice={editorInvoice}
          projectId={currentProjectId}
          project={project}
          client={activeClients?.find(c => Number(c.id) === Number(project?.clientId))}
          stages={combinedStages}
          onSave={handleSave}
          onIssue={handleIssue}
          onClose={() => { setShowEditor(false); setEditorInvoice(null); }}
          fmtCurr={fmtCurr}
          formatMoney={settingsHook?.formatMoney}
          allocateInvoiceNumber={invoicesHook?.allocateInvoiceNumber}
          allInvoices={invoicesHook?.allInvoices}
          projectsById={projectNameLookup}
          sacCodes={settingsHook?.invoiceBranding?.sacCodes || []}
        />
      )}
      {paymentInvoice && (
        <PaymentConfirmModal
          invoice={paymentInvoice}
          onConfirm={handleConfirmPayment}
          onClose={() => setPaymentInvoice(null)}
          fmtCurr={fmtCurr}
          formatMoney={settingsHook?.formatMoney}
          knownAccountNames={knownAccountNames}
        />
      )}
      {/* Step 2: Detailed payment match confirmation dialog */}
      {matchConfirmTarget && (
        <PaymentMatchConfirmModal
          paymentEvent={matchConfirmTarget.paymentEvent}
          invoice={matchConfirmTarget.invoice}
          onConfirm={handleDoConfirmMatch}
          onClose={() => setMatchConfirmTarget(null)}
          fmtCurr={fmtCurr}
          formatMoney={settingsHook?.formatMoney}
        />
      )}
      {recalcPreviewInvoice && pendingRevision && (
        <RecalculationPreviewModal
          revision={pendingRevision}
          project={project}
          stages={combinedStages}
          stageTimeline={project?.stageTimeline || {}}
          budgetAllocation={project?.budgetAllocation || {}}
          invoicesHook={invoicesHook}
          currentInvoice={recalcPreviewInvoice}
          fmtCurr={fmtCurr}
          formatMoney={settingsHook?.formatMoney}
          mode={pendingCreateParams ? 'create' : 'issue'}
          onIssue={pendingCreateParams ? handleCreateWithRecalc : handleIssueWithRecalc}
          onIssueWithSeparateAdjustment={pendingCreateParams ? handleCreateWithSeparateAdjustment : handleIssueWithSeparateAdjustment}
          onClose={() => { setRecalcPreviewInvoice(null); setPendingRevision(null); setPendingCreateParams(null); }}
        />
      )}
      {pdfPreviewInv && (
        <PdfPreviewModal
          inv={pdfPreviewInv}
          getInvoicePdfData={getInvoicePdfData}
          fmtCurr={fmtCurr}
          invoicesHook={invoicesHook}
          onClose={() => setPdfPreviewInv(null)}
        />
      )}
      {/* ── Email Draft Modal (replaces direct mailto) ── */}
      {emailDraftInvoice && (() => {
        const inv = emailDraftInvoice.invoice;
        const templateKey = emailDraftInvoice.templateKey;
        const client = activeClients?.find(c => Number(c.id) === Number(project?.clientId));
        const stageDef = combinedStages.find(s => s.key === inv.stageKey);
        const stageName = stageDef?.name || inv.stageKey || 'Project';
        const paymentTerms = client?.paymentTermsDays;
        const { dueDate } = calculateDueDate(inv.invoiceDate, paymentTerms);
        return (
          <EmailDraftModal
            client={client}
            project={project}
            stageName={stageName}
            eventType={templateKey}
            dateValue={inv.invoiceDate}
            invoice={inv}
            stages={combinedStages}
            hasPdfAttachment={false}
            extraVars={{
              invoiceNumber: inv.invoiceNumber || `#${inv.id}`,
              invoiceDate: formatDateForEmail(inv.invoiceDate),
              dueDate: formatDateForEmail(dueDate),
              paymentTermsDays: paymentTerms != null ? String(paymentTerms) : '14',
              amount: fmtCurr(getInvoiceNetForINR(inv)),
            }}
            onClose={() => setEmailDraftInvoice(null)}
            onLogEmail={(emailData) => {
              logEmail({
                ...emailData,
                projectId: project?.id,
                clientId: client?.id,
                invoiceId: inv.id,
              });
            }}
            onSaveOnly={() => setEmailDraftInvoice(null)}
            onSaveAndMail={() => setEmailDraftInvoice(null)}
          />
        );
      })()}

      {/* ── Reminder Picker Modal ── */}
      {reminderInvoice && (() => {
        const remClient = activeClients?.find(c => Number(c.id) === Number(project?.clientId));
        const { dueDate: remDueDate } = calculateDueDate(reminderInvoice.invoiceDate, remClient?.paymentTermsDays);
        const remDisplayStatus = getInvoiceDisplayStatus(reminderInvoice, remDueDate);
        const daysOverdue = remDueDate ? Math.max(0, Math.floor((new Date() - new Date(remDueDate)) / (1000 * 60 * 60 * 24))) : 0;
        return (
          <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onClick={() => setReminderInvoice(null)}>
            <div onClick={e => e.stopPropagation()} style={{ background: 'var(--brio-white, #fff)', border: '1px solid var(--brio-border)', borderRadius: '6px', width: '100%', maxWidth: '360px' }}>
              <div className="brio-flex brio-justify-between brio-items-center" style={{ padding: '12px 16px', borderBottom: '1px solid var(--brio-border)' }}>
                <span className="brio-font-bold" style={{ fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Bell className="w-4 h-4" style={{ color: 'var(--brio-gold, #9F902A)' }} />
                  Draft Reminder
                </span>
                <button onClick={() => setReminderInvoice(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px' }}><X className="w-4 h-4" /></button>
              </div>
              <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {/* Invoice context */}
                <div style={{
                  padding: '8px 10px', background: 'var(--brio-gold-light, #fef9e7)',
                  border: '1px solid var(--brio-gold, #c5a572)', borderRadius: '4px',
                  fontSize: '10px', color: 'var(--brio-text, #475569)',
                }}>
                  <div><strong>Invoice:</strong> {reminderInvoice.invoiceNumber || `#${reminderInvoice.id}`}</div>
                  {remDueDate && <div><strong>Due:</strong> {fmtDate(remDueDate)}{daysOverdue > 0 && <span style={{ color: 'var(--brio-red, #dc2626)', marginLeft: '6px' }}>{daysOverdue} days overdue</span>}</div>}
                  <div><strong>Status:</strong> <span className={`brio-badge ${(STATUS_BADGE[remDisplayStatus] || {}).className || ''}`} style={{ fontSize: '8px', padding: '1px 5px' }}>{(STATUS_BADGE[remDisplayStatus] || {}).label || remDisplayStatus}</span></div>
                </div>

                {/* Reminder type buttons */}
                <div style={{ fontSize: '10px', fontWeight: 600, color: 'var(--brio-muted, #94a3b8)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Choose reminder type
                </div>
                {getReminderTemplateTypes().map(({ key, label }) => (
                  <button key={key}
                    onClick={() => { handleSendInvoiceEmail(reminderInvoice, key); setReminderInvoice(null); }}
                    className="brio-btn brio-btn-secondary brio-flex brio-items-center brio-gap-xs"
                    style={{ padding: '8px 14px', fontSize: '11px', justifyContent: 'flex-start' }}>
                    <Bell className="w-3 h-3" style={{ color: key === 'reminder_final' ? '#dc2626' : key === 'reminder_overdue' ? '#ef4444' : '#f59e0b' }} /> {label}
                  </button>
                ))}

                {/* Invoice email option */}
                <button
                  onClick={() => { handleSendInvoiceEmail(reminderInvoice, 'invoice_sent'); setReminderInvoice(null); }}
                  className="brio-btn brio-btn-secondary brio-flex brio-items-center brio-gap-xs"
                  style={{ padding: '8px 14px', fontSize: '11px', justifyContent: 'flex-start', borderTop: '1px solid var(--brio-border, #d1d5db)', borderRadius: 0, marginTop: '2px', paddingTop: '10px' }}>
                  <Mail className="w-3 h-3" style={{ color: 'var(--brio-gold, #9F902A)' }} /> Draft Invoice Email
                </button>

                <div className="brio-text-xs brio-text-grey" style={{ marginTop: '2px', fontStyle: 'italic', fontSize: '9px' }}>
                  Opens a draft in your mail app. Attach invoice PDF manually if needed.
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── PI Number Reassignment Modal ── */}
      {piReassignState && (() => {
        const { inv, onHoldPIs } = piReassignState;
        const selectedDonorId = piReassignDonorId;
        const setSelectedDonorId = setPiReassignDonorId;
        const donorInv = onHoldPIs.find(i => i.id === selectedDonorId);
        return (
          <div style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onClick={() => setPiReassignState(null)}>
            <div onClick={e => e.stopPropagation()} style={{ background: 'var(--brio-white, #fff)', border: '1px solid var(--brio-border)', width: '100%', maxWidth: '420px', maxHeight: '80vh', overflowY: 'auto' }}>
              <div className="brio-flex brio-justify-between brio-items-center" style={{ padding: '12px 16px', borderBottom: '1px solid var(--brio-border)' }}>
                <span className="brio-font-bold" style={{ fontSize: '13px' }}>Issue Proforma Invoice</span>
                <button onClick={() => setPiReassignState(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px' }}><X className="w-4 h-4" /></button>
              </div>
              <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ padding: '8px 10px', background: '#fefce8', border: '1px solid #fde047', fontSize: '11px' }}>
                  There {onHoldPIs.length === 1 ? 'is' : 'are'} <strong>{onHoldPIs.length} on-hold proforma {onHoldPIs.length === 1 ? 'invoice' : 'invoices'}</strong> with reserved numbers in <strong>{inv.invoiceDate?.slice(0, 7)}</strong>. You can reuse one of those numbers for this invoice, or proceed with the next sequential number.
                </div>
                <div>
                  <div style={{ fontSize: '11px', fontWeight: 600, marginBottom: '6px' }}>On-hold PI numbers available to reuse:</div>
                  {onHoldPIs.map(donor => {
                    const projName = projectNameLookup.get(Number(donor.projectId)) || `Project #${donor.projectId}`;
                    return (
                      <label key={donor.id} className="brio-flex brio-items-center brio-gap-xs" style={{ fontSize: '11px', padding: '5px 0', cursor: 'pointer', borderBottom: '1px solid var(--brio-border)' }}>
                        <input type="radio" name="piDonor" value={donor.id}
                          checked={selectedDonorId === donor.id}
                          onChange={() => setSelectedDonorId(donor.id)} />
                        <span><strong>{donor.invoiceNumber}</strong> — {projName}{donor.stageKey ? ` (${donor.stageKey})` : ''}</span>
                      </label>
                    );
                  })}
                  <label className="brio-flex brio-items-center brio-gap-xs" style={{ fontSize: '11px', padding: '5px 0', cursor: 'pointer' }}>
                    <input type="radio" name="piDonor" value=""
                      checked={selectedDonorId === null}
                      onChange={() => setSelectedDonorId(null)} />
                    <span>Proceed with next sequential number</span>
                  </label>
                </div>
                {selectedDonorId !== null && (
                  <div style={{ fontSize: '10px', color: '#6b7280', padding: '6px 8px', background: '#f9fafb', border: '1px solid var(--brio-border)' }}>
                    The on-hold PI <strong>{donorInv?.invoiceNumber}</strong> will have its number cleared and will receive a new number when it is issued.
                  </div>
                )}
                <div className="brio-flex brio-gap-s brio-justify-end" style={{ borderTop: '1px solid var(--brio-border)', paddingTop: '12px' }}>
                  <button onClick={() => setPiReassignState(null)} className="brio-btn brio-btn-secondary" style={{ padding: '6px 14px', fontSize: '11px' }}>Cancel</button>
                  <button
                    onClick={async () => {
                      const result = await handlePiReassignConfirm(donorInv?.invoiceNumber || null, selectedDonorId || null);
                      if (result && !result.success) alert(result.error || 'Failed to issue invoice.');
                    }}
                    className="brio-btn brio-btn-primary" style={{ padding: '6px 14px', fontSize: '11px' }}>
                    {selectedDonorId ? `Issue with number ${donorInv?.invoiceNumber}` : 'Issue with next number'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
